import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createTransportAdapter } from "@repo/band-sync";
import type { PlayerSnapshot, StemRole } from "@repo/audio-engine";
import { toDataURL as toQrDataUrl } from "qrcode";
import type {
  LivekitTransport,
  LivekitTransportEvent,
  TransportCommandEnvelope,
} from "@repo/band-sync";
import { TokenService, type TokenBootstrapResponse } from "./services/tokenService";
import { CognitoAuthManager } from "./services/cognitoAuth";
import { resolveCognitoRedirectUri } from "./services/cognitoConfig";
import {
  DEFAULT_COUNTRY_CODES,
  countryCodeToFlagEmoji,
  eventColor,
  executeEventType,
  hasMixPayloadKeys,
  issueEventType,
  parseBenchPayload,
  receiveEventType,
  summarizeJoinMs,
} from "./lib/oscilloscopeUtils";
import type { BenchPayload, DemoCommandOp, VisualEventType } from "./lib/oscilloscopeUtils";
import {
  parseSessionJoinBootstrap,
  type SessionSongDescriptor,
  type SessionTransportSnapshot,
} from "./lib/joinBootstrap";
import { meterLevel01FromRmsLinear, meterPercentFromLevel01 } from "./lib/meterMath";
import {
  applyLwwArbitrationRows,
  descriptorHash,
  type CommandArbitrationRow,
} from "./lib/syncDiagnostics";
import {
  LATE_JOIN_CORRECTION_DRIFT_THRESHOLD_MS,
  LATE_JOIN_SECOND_PASS_DELAY_MS,
  planLateJoinCorrection,
  projectAuthoritativePositionSec,
} from "./lib/lateJoinCorrection";
import { LabPlaybackController } from "./audio/labPlaybackController";
import { DeterministicCommandScheduler } from "./audio/deterministicCommandScheduler";
import { CORE_ROLES, type StemLoadProgress } from "./audio/backendSongLoader";
import {
  deleteMixPreset,
  loadMixPresets,
  upsertMixPreset,
  type MixPreset,
  type StemMixSetting,
} from "./lib/mixPresets";
import {
  loadOrCreateTabParticipantId,
  loadParticipantProfile,
  regenerateTabParticipantId,
  saveTabParticipantId,
  updateParticipantNickname,
  type ParticipantProfile,
} from "./lib/participantProfile";
import { MemberProfileService } from "./services/memberProfileService";
import { SessionSongService } from "./services/sessionSongService";
import { SessionTransportService } from "./services/sessionTransportService";

type TriggerMode = "operator" | "random-client";

interface VisualEvent {
  readonly atMs: number;
  readonly lane: number;
  readonly type: VisualEventType;
  readonly marker?: string;
  readonly barPercent?: number;
}

interface ClientRow {
  readonly id: string;
  readonly lane: number;
  readonly countryCode: string;
  readonly deviceType?: string;
  readonly latencyCompMs?: number;
  readonly connected: boolean;
  readonly status: "idle" | "connecting" | "connected" | "disconnected" | "failed";
  readonly joinElapsedMs?: number | undefined;
  readonly errorMessage?: string | undefined;
  readonly lastSeenAtEpochMs?: number | undefined;
  readonly isLocal?: boolean;
}

interface RuntimeClient {
  readonly id: string;
  readonly lane: number;
  readonly countryCode: string;
  readonly transport: LivekitTransport | null;
  readonly unsubscribe: (() => void) | null;
}

interface StemMixerRow {
  readonly role: StemRole;
  readonly volume: number;
  readonly muted: boolean;
  readonly solo: boolean;
  readonly level: number;
}

interface StemDownloadRow {
  readonly role: StemRole;
  readonly phase: "queued" | "downloading" | "decoding" | "ready" | "error";
  readonly loadedBytes: number;
  readonly totalBytes?: number;
  readonly message?: string;
}

type RoomLoadPhase = "queued" | "downloading" | "decoding" | "ready" | "error" | "timeout";

interface RoomSongLoadStatus {
  readonly participantId: string;
  readonly songYoutubeId: string;
  readonly phase: RoomLoadPhase;
  readonly percentage: number;
  readonly updatedAtMs: number;
  readonly errorMessage?: string;
}

interface CommandBanner {
  readonly message: string;
  readonly tone: "info" | "warning";
}

interface SyncTelemetry {
  readonly deliveryP95Ms: number;
  readonly deliveryP99Ms: number;
  readonly applyOffsetP95Ms: number;
  readonly applyOffsetP99Ms: number;
  readonly applyOffsetMaxMs: number;
  readonly deliverySampleCount: number;
  readonly applySampleCount: number;
}

interface ParticipantTelemetry {
  readonly receives: number;
  readonly applies: number;
  readonly lastDeliveryLatencyMs: number;
  readonly lastApplyOffsetMs: number;
}

interface CommandToast {
  readonly id: number;
  readonly message: string;
  readonly tone: "info" | "warning";
  readonly createdAtEpochMs: number;
}

interface SkewReading {
  readonly skewMs: number;
  readonly sampledAtMs: number;
  readonly authorityParticipantId: string;
}

interface SongLoadRetryRequest {
  readonly kind: "random" | "descriptor";
  readonly song?: SessionSongDescriptor;
  readonly source?: "join" | "command";
}

const OSC_WINDOW_MS = 20000;
const MAX_VISUAL_EVENTS = 2500;
const MAX_TELEMETRY_SAMPLES = 2000;
const MAX_ACK_ENTRIES = 512;
const MAX_APPLIED_COMMAND_IDS = 4096;
const LATE_COMMAND_THRESHOLD_MS = 80;
const LATE_RESCHEDULE_DELAY_MS = 12;
const PLAYBACK_GATE_OPS: readonly DemoCommandOp[] = [
  "transport.play",
  "transport.pause",
  "transport.seek",
  "transport.setRate",
  "transport.setPitch",
];
const ROOM_PROGRESS_TIMEOUT_MS = 20000;
const ROOM_PROGRESS_BROADCAST_INTERVAL_MS = 250;
const SONG_LOAD_SCOPE_MIN_INTERVAL_MS = 150;
const SKEW_REPORT_INTERVAL_MS = 500;
const SKEW_PLOT_WINDOW_MS = 20000;
const SKEW_PLOT_MAX_POINTS_PER_PARTICIPANT = 180;
const SKEW_PLOT_MIN_ABS_MS = 10;
const SYNC_PULSE_INTERVAL_MS = 500;
const SYNC_NUDGE_DEADBAND_MS = 18;
const SYNC_NUDGE_COOLDOWN_MS = 250;
const SYNC_NUDGE_ALPHA = 0.45;
const SYNC_NUDGE_MIN_STEP_MS = 8;
const SYNC_NUDGE_MAX_STEP_MS = 80;
const DEPRECATED_BAND_SYNC_V2_API_BASE_URL =
  "https://pj724ne7xj.execute-api.ap-southeast-2.amazonaws.com/dev";
const DEFAULT_BAND_SYNC_V2_API_BASE_URL =
  "https://i4qiggeyz0.execute-api.ap-southeast-2.amazonaws.com/dev";
const DEFAULT_LAN_SHARE_HOST = "192.168.0.136";
const SESSION_ROOM_STORAGE_KEY = "band-sync-lab:last-room:v1";
const AUTO_RECONNECT_STORAGE_KEY = "band-sync-lab:auto-reconnect:v1";
const BAND_SYNC_STORAGE_PREFIX = "band-sync-lab:";
const STEM_ROLE_LABELS: Record<StemRole, string> = {
  vocals: "Vocals",
  drums: "Drums",
  bass: "Bass",
  other: "Other",
  piano: "Piano",
  guitar: "Guitar",
};

function randomIdSegment(length: number): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID().replace(/-/g, "").slice(0, length);
  }
  return Math.random()
    .toString(36)
    .slice(2, 2 + length);
}

const GUEST_NICKNAME_ADJECTIVES = [
  "Swift",
  "Bright",
  "Groovy",
  "Chill",
  "Neon",
  "Sonic",
  "Golden",
  "Silver",
  "Fresh",
  "Lucky",
];

const GUEST_NICKNAME_NOUNS = [
  "Drummer",
  "Singer",
  "Player",
  "Bassline",
  "Chord",
  "Rhythm",
  "Melody",
  "Groove",
  "Pulse",
  "Wave",
];

function randomGuestNickname(): string {
  const adjective =
    GUEST_NICKNAME_ADJECTIVES[Math.floor(Math.random() * GUEST_NICKNAME_ADJECTIVES.length)] ??
    "Guest";
  const noun =
    GUEST_NICKNAME_NOUNS[Math.floor(Math.random() * GUEST_NICKNAME_NOUNS.length)] ?? "User";
  return `${adjective}${noun}${randomIdSegment(2).toUpperCase()}`;
}

function createEphemeralParticipantProfile(): ParticipantProfile {
  return {
    participantId: `participant-${randomIdSegment(12)}`,
    nickname: randomGuestNickname(),
  };
}

function createEphemeralTabParticipantId(baseParticipantId: string): string {
  return `${baseParticipantId}-tab-${randomIdSegment(8)}`;
}

function detectDeviceType(): string {
  if (typeof navigator === "undefined") {
    return "Unknown device";
  }
  const ua = navigator.userAgent;
  const maxTouchPoints =
    typeof navigator.maxTouchPoints === "number" ? navigator.maxTouchPoints : 0;

  // iPadOS on Safari can identify as Macintosh; touch-points disambiguates.
  if (/Macintosh/i.test(ua) && maxTouchPoints > 1) {
    return "Apple tablet";
  }
  if (/iPhone|iPod/i.test(ua)) {
    return "Apple mobile";
  }
  if (/iPad/i.test(ua)) {
    return "Apple tablet";
  }
  if (/Android/i.test(ua)) {
    return /Mobile/i.test(ua) ? "Android mobile" : "Android tablet";
  }
  if (/Macintosh|Mac OS X/i.test(ua)) {
    return "Apple desktop";
  }
  if (/Windows/i.test(ua)) {
    return "Windows desktop";
  }
  if (/Linux/i.test(ua)) {
    return "Linux desktop";
  }
  return "Unknown device";
}

function readLocalLatencyCompMs(playback: LabPlaybackController | null): number | undefined {
  if (!playback) {
    return undefined;
  }
  const value = playback.estimateAudibleOutputLatencyMs();
  if (!Number.isFinite(value)) {
    return undefined;
  }
  return Math.max(0, Math.round(value * 10) / 10);
}

function skewColorForParticipant(participantId: string): string {
  const palette = [
    "#67e8f9",
    "#a3e635",
    "#fbbf24",
    "#f472b6",
    "#c084fc",
    "#60a5fa",
    "#34d399",
    "#f87171",
  ];
  let hash = 0;
  for (let i = 0; i < participantId.length; i += 1) {
    hash = (hash * 31 + participantId.charCodeAt(i)) >>> 0;
  }
  return palette[hash % palette.length] ?? "#67e8f9";
}

function clearBandSyncBrowserStorage(): void {
  if (typeof window === "undefined") {
    return;
  }
  for (let index = window.localStorage.length - 1; index >= 0; index -= 1) {
    const key = window.localStorage.key(index);
    if (key?.startsWith(BAND_SYNC_STORAGE_PREFIX)) {
      window.localStorage.removeItem(key);
    }
  }
  for (let index = window.sessionStorage.length - 1; index >= 0; index -= 1) {
    const key = window.sessionStorage.key(index);
    if (key?.startsWith(BAND_SYNC_STORAGE_PREFIX)) {
      window.sessionStorage.removeItem(key);
    }
  }
}

function normalizeStemUrlsByRole(
  value: Readonly<Record<string, string>> | undefined,
): Readonly<Record<string, string>> | undefined {
  if (value === undefined) {
    return undefined;
  }
  const normalized: Record<string, string> = {};
  for (const [role, maybeUrl] of Object.entries(value)) {
    const trimmedRole = role.trim();
    const trimmedUrl = maybeUrl.trim();
    if (!trimmedRole || !trimmedUrl) {
      continue;
    }
    normalized[trimmedRole] = trimmedUrl;
  }
  return Object.keys(normalized).length > 0 ? normalized : undefined;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function formatClock(seconds: number | undefined): string {
  if (seconds === undefined || !Number.isFinite(seconds)) {
    return "0:00";
  }
  const safe = Math.max(0, seconds);
  const totalSeconds = Math.floor(safe);
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  return `${mins}:${String(secs).padStart(2, "0")}`;
}

function clampPercent(value: number): number {
  if (!Number.isFinite(value)) {
    return 0;
  }
  return Math.max(0, Math.min(100, value));
}

function stemRowPercent(row: StemDownloadRow): number {
  if (row.phase === "ready") {
    return 100;
  }
  if (row.phase === "error") {
    return 0;
  }
  if (row.totalBytes !== undefined && row.totalBytes > 0) {
    return clampPercent(Math.max(1, Math.min(99, (row.loadedBytes / row.totalBytes) * 100)));
  }
  if (row.phase === "decoding") {
    return 95;
  }
  return clampPercent(Math.min(90, Math.max(4, row.loadedBytes / 200000)));
}

function aggregateStemRows(rows: readonly StemDownloadRow[]): {
  readonly phase: RoomLoadPhase;
  readonly percentage: number;
} {
  if (rows.length === 0) {
    return { phase: "queued", percentage: 0 };
  }
  if (rows.some((row) => row.phase === "error")) {
    return { phase: "error", percentage: 0 };
  }
  if (rows.every((row) => row.phase === "ready")) {
    return { phase: "ready", percentage: 100 };
  }
  if (rows.some((row) => row.phase === "decoding")) {
    return {
      phase: "decoding",
      percentage: clampPercent(
        rows.reduce((sum, row) => sum + stemRowPercent(row), 0) / rows.length,
      ),
    };
  }
  if (rows.some((row) => row.phase === "downloading")) {
    return {
      phase: "downloading",
      percentage: clampPercent(
        rows.reduce((sum, row) => sum + stemRowPercent(row), 0) / rows.length,
      ),
    };
  }
  return {
    phase: "queued",
    percentage: clampPercent(rows.reduce((sum, row) => sum + stemRowPercent(row), 0) / rows.length),
  };
}

function percentile(values: number[], p: number): number {
  if (!values.length) {
    return 0;
  }
  const sorted = [...values].sort((a, b) => a - b);
  const idx = (sorted.length - 1) * p;
  const lower = Math.floor(idx);
  const upper = Math.ceil(idx);
  if (lower === upper) {
    return sorted[lower] ?? 0;
  }
  const weight = idx - lower;
  const lowerValue = sorted[lower] ?? 0;
  const upperValue = sorted[upper] ?? lowerValue;
  return lowerValue * (1 - weight) + upperValue * weight;
}

function normalizeApiBaseUrl(input: string): string {
  const trimmed = input.trim();
  if (!trimmed || trimmed === "/" || trimmed === DEPRECATED_BAND_SYNC_V2_API_BASE_URL) {
    return DEFAULT_BAND_SYNC_V2_API_BASE_URL;
  }
  return trimmed;
}

function resolveInitialApiBaseUrl(rawApiBaseUrl: string | undefined): string {
  const isLocalNetworkHostname = (hostname: string): boolean => {
    const normalized = hostname.trim().toLowerCase();
    if (
      normalized === "localhost" ||
      normalized === "127.0.0.1" ||
      normalized === "::1" ||
      normalized.startsWith("10.") ||
      normalized.startsWith("192.168.")
    ) {
      return true;
    }
    const match = /^172\.(\d{1,3})\./.exec(normalized);
    if (!match) {
      return false;
    }
    const octet = Number.parseInt(match[1] ?? "", 10);
    return Number.isFinite(octet) && octet >= 16 && octet <= 31;
  };

  const shouldRejectHostedLocalApiBase = (apiBase: string): boolean => {
    if (typeof window === "undefined") {
      return false;
    }
    try {
      const currentHost = new URL(window.location.origin).hostname;
      const currentIsLocal = isLocalNetworkHostname(currentHost);
      if (currentIsLocal) {
        return false;
      }
      const parsedApiBase = new URL(apiBase);
      return isLocalNetworkHostname(parsedApiBase.hostname);
    } catch {
      return false;
    }
  };

  if (typeof window !== "undefined") {
    const queryApiBase = new URLSearchParams(window.location.search).get("apiBase")?.trim();
    if (queryApiBase) {
      const normalized = normalizeApiBaseUrl(queryApiBase);
      if (!shouldRejectHostedLocalApiBase(normalized)) {
        return normalized;
      }
    }
  }
  const trimmed = rawApiBaseUrl?.trim();
  if (!trimmed) {
    return DEFAULT_BAND_SYNC_V2_API_BASE_URL;
  }
  return normalizeApiBaseUrl(trimmed);
}

function resolveInitialRoomName(initialFreshGuestMode: boolean): string {
  if (typeof window === "undefined") {
    return "sync-visual-demo";
  }
  const queryRoom = new URLSearchParams(window.location.search).get("room")?.trim();
  if (queryRoom) {
    return queryRoom;
  }
  if (initialFreshGuestMode) {
    return "sync-visual-demo";
  }
  const saved = window.localStorage.getItem(SESSION_ROOM_STORAGE_KEY)?.trim();
  return saved && saved.length > 0 ? saved : "sync-visual-demo";
}

function resolveShareOrigin(): string {
  if (typeof window === "undefined") {
    return "";
  }
  const current = new URL(window.location.origin);
  const isLocalhost = current.hostname === "localhost" || current.hostname === "127.0.0.1";
  if (!isLocalhost) {
    return current.origin;
  }
  const configuredLanHost = (import.meta.env.VITE_LAN_SHARE_HOST as string | undefined)?.trim();
  const lanHost =
    configuredLanHost && configuredLanHost.length > 0 ? configuredLanHost : DEFAULT_LAN_SHARE_HOST;
  const portSegment = current.port ? `:${current.port}` : "";
  return `${current.protocol}//${lanHost}${portSegment}`;
}

function classifyLoadError(message: string): string {
  const normalized = message.toLowerCase();
  if (normalized.includes("401") || normalized.includes("403") || normalized.includes("unauthor")) {
    return "Auth error while loading song. Sign in and retry.";
  }
  if (normalized.includes("failed to fetch") || normalized.includes("networkerror")) {
    return "Network/CORS/DNS error while loading song. Check API base URL and endpoint health.";
  }
  if (normalized.includes("404")) {
    return "Song or stem endpoint not found. Verify backend song catalog/proxy.";
  }
  return message;
}

function isAbortLikeError(error: unknown): boolean {
  if (error instanceof DOMException) {
    return error.name === "AbortError";
  }
  if (error instanceof Error) {
    return error.name === "AbortError" || error.message === "SONG_LOAD_CANCELLED";
  }
  return false;
}

function isTransportOutOfOrderError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return (
    message.includes("TRANSPORT_COMMAND_OUT_OF_ORDER") ||
    message.includes("seq must increase") ||
    message.includes("executeAtServerMono must not move backward")
  );
}

function resolveCommandId(payload: BenchPayload): string {
  return payload.commandId.trim().length > 0
    ? payload.commandId
    : `${payload.issuerId}:${payload.seq}`;
}

function resolveAuthoritativeExecuteAtMs(payload: BenchPayload): number {
  return payload.authoritativeExecuteAtMs ?? payload.executeAtMs;
}

function resolveExecuteAtPerfMs(
  payload: BenchPayload,
  localMinusServerOffsetMs = 0,
  audibleLatencyCompensationMs = 0,
): number {
  const nowPerfMs = performance.now();
  const nowWallClockMs = Date.now();
  const targetWallClockMs =
    resolveAuthoritativeExecuteAtMs(payload) +
    localMinusServerOffsetMs -
    Math.max(0, audibleLatencyCompensationMs);
  return nowPerfMs + Math.max(0, targetWallClockMs - nowWallClockMs);
}

function requiresPlaybackGate(op: DemoCommandOp): boolean {
  return PLAYBACK_GATE_OPS.includes(op);
}

function clampScheduledLeadMs(value: number): number {
  if (!Number.isFinite(value)) {
    return 0;
  }
  return Math.max(0, Math.round(value));
}

function formatSignedOffsetMs(offsetMs: number): string {
  return `${offsetMs >= 0 ? "+" : ""}${offsetMs.toFixed(1)}ms`;
}

const TEMPO_SLIDER_MIN = -100;
const TEMPO_SLIDER_MAX = 100;
const TEMPO_SLIDER_STEP = 1;
const TEMPO_MIN_RATE = 0.5;
const TEMPO_MAX_RATE = 2;

function clampTempoRate(rate: number): number {
  if (!Number.isFinite(rate)) {
    return 1;
  }
  return Math.min(TEMPO_MAX_RATE, Math.max(TEMPO_MIN_RATE, rate));
}

function tempoRateToSliderValue(rate: number): number {
  const clamped = clampTempoRate(rate);
  return Math.round((Math.log2(clamped) / Math.log2(TEMPO_MAX_RATE)) * TEMPO_SLIDER_MAX);
}

function tempoSliderValueToRate(value: number): number {
  const clamped = Math.min(TEMPO_SLIDER_MAX, Math.max(TEMPO_SLIDER_MIN, value));
  const normalized = clamped / TEMPO_SLIDER_MAX;
  const rate = 2 ** normalized;
  return clampTempoRate(rate);
}

function getScopeShiftMarker(
  op: DemoCommandOp,
  payload: { rate?: number; pitchSemitones?: number },
): string | undefined {
  if (
    op === "transport.setRate" &&
    typeof payload.rate === "number" &&
    Number.isFinite(payload.rate)
  ) {
    return payload.rate >= 1 ? "T↑" : "T↓";
  }
  if (
    op === "transport.setPitch" &&
    typeof payload.pitchSemitones === "number" &&
    Number.isFinite(payload.pitchSemitones)
  ) {
    return payload.pitchSemitones >= 0 ? "P↑" : "P↓";
  }
  return undefined;
}

function getAdjustmentMarker(positionDeltaSec: number): string | undefined {
  if (!Number.isFinite(positionDeltaSec)) {
    return undefined;
  }
  if (positionDeltaSec > 0.001) {
    return "A→";
  }
  if (positionDeltaSec < -0.001) {
    return "A←";
  }
  return undefined;
}

export function App() {
  const initialFreshGuestMode =
    typeof window !== "undefined"
      ? (() => {
          const params = new URLSearchParams(window.location.search);
          if (params.get("persistBrowserState") === "1") {
            return false;
          }
          return params.get("freshGuestMode") === "1";
        })()
      : false;
  const initialProfile = initialFreshGuestMode
    ? createEphemeralParticipantProfile()
    : loadParticipantProfile();
  const [apiBaseUrl, setApiBaseUrl] = useState(() =>
    resolveInitialApiBaseUrl(import.meta.env.VITE_API_BASE_URL as string | undefined),
  );
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [freshGuestMode] = useState(initialFreshGuestMode);
  const [participantProfile, setParticipantProfile] = useState<ParticipantProfile>(
    () => initialProfile,
  );
  const [sessionParticipantId, setSessionParticipantId] = useState<string>(() =>
    initialFreshGuestMode
      ? createEphemeralTabParticipantId(initialProfile.participantId)
      : loadOrCreateTabParticipantId(initialProfile.participantId),
  );
  const [roomName, setRoomName] = useState(() => resolveInitialRoomName(initialFreshGuestMode));
  const [isProvisioningRoom, setIsProvisioningRoom] = useState(false);
  const [roomProvisioned, setRoomProvisioned] = useState(false);
  const roomDiscoveryMessage = "Link-only join mode. Room discovery is disabled.";
  const [autoReconnectEnabled, setAutoReconnectEnabled] = useState(() => {
    if (initialFreshGuestMode) {
      return false;
    }
    if (typeof window === "undefined") {
      return true;
    }
    const saved = window.localStorage.getItem(AUTO_RECONNECT_STORAGE_KEY);
    return saved === null ? true : saved === "1";
  });
  const [participantCountryCode, setParticipantCountryCode] = useState("AU");
  const [triggerMode, setTriggerMode] = useState<TriggerMode>("operator");
  const [debugSyntheticModeEnabled] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }
    return new URLSearchParams(window.location.search).get("debugSynthetic") === "1";
  });
  const [leadMs, setLeadMs] = useState(120);
  const [latencyCompEnabled, setLatencyCompEnabled] = useState(true);
  const [localMinusServerClockOffsetMs, setLocalMinusServerClockOffsetMs] = useState(0);
  const [cycleMs, setCycleMs] = useState(200);
  const [seekTargetSec, setSeekTargetSec] = useState(6);
  const [tempoRate, setTempoRate] = useState(1);
  const [keySemitones, setKeySemitones] = useState(0);
  const [clients, setClients] = useState<ClientRow[]>([]);
  const [isConnecting, setIsConnecting] = useState(false);
  const [randomCycleOn, setRandomCycleOn] = useState(false);
  const [playbackReady, setPlaybackReady] = useState(false);
  const [playbackError, setPlaybackError] = useState<string | null>(null);
  const [backendSongLabel, setBackendSongLabel] = useState<string>("not loaded");
  const [currentSongYoutubeId, setCurrentSongYoutubeId] = useState<string | null>(null);
  const [currentSongDescriptor, setCurrentSongDescriptor] = useState<SessionSongDescriptor | null>(
    null,
  );
  const [isLoadingBackendSong, setIsLoadingBackendSong] = useState(false);
  const [hasRetryableSongLoad, setHasRetryableSongLoad] = useState(false);
  const [backendLoadMessage, setBackendLoadMessage] = useState<string>(
    "Waiting to initialize backend stem loader.",
  );
  const [stemDownloadRows, setStemDownloadRows] = useState<StemDownloadRow[]>([]);
  const [roomSongLoadStatusByParticipant, setRoomSongLoadStatusByParticipant] = useState<
    Record<string, RoomSongLoadStatus>
  >({});
  const [roomReadinessGateEnabled, setRoomReadinessGateEnabled] = useState(true);
  const [stemMixerRows, setStemMixerRows] = useState<StemMixerRow[]>([]);
  const [localSnapshot, setLocalSnapshot] = useState<PlayerSnapshot | null>(null);
  const [scrubPositionSec, setScrubPositionSec] = useState(0);
  const [isScrubbing, setIsScrubbing] = useState(false);
  const [isAdjustingTempo, setIsAdjustingTempo] = useState(false);
  const [isAdjustingKey, setIsAdjustingKey] = useState(false);
  const [playbackTraceLines, setPlaybackTraceLines] = useState<string[]>([]);
  const [mixPresets, setMixPresets] = useState<MixPreset[]>(() =>
    initialFreshGuestMode ? [] : loadMixPresets(),
  );
  const [selectedPresetName, setSelectedPresetName] = useState("");
  const [newPresetName, setNewPresetName] = useState("");
  const [commandBanner, setCommandBanner] = useState<CommandBanner | null>(null);
  const [lastInviteCopyUrl, setLastInviteCopyUrl] = useState<string>("");
  const [lastInviteCopyStatus, setLastInviteCopyStatus] = useState<string>("");
  const [syncTelemetry, setSyncTelemetry] = useState<SyncTelemetry>({
    deliveryP95Ms: 0,
    deliveryP99Ms: 0,
    applyOffsetP95Ms: 0,
    applyOffsetP99Ms: 0,
    applyOffsetMaxMs: 0,
    deliverySampleCount: 0,
    applySampleCount: 0,
  });
  const [logLines, setLogLines] = useState<string[]>([]);
  const [participantTelemetry, setParticipantTelemetry] = useState<
    Record<string, ParticipantTelemetry>
  >({});
  const [commandToasts, setCommandToasts] = useState<readonly CommandToast[]>([]);
  const [commandArbitrationRows, setCommandArbitrationRows] = useState<
    readonly CommandArbitrationRow[]
  >([]);
  const [latestSkewByParticipant, setLatestSkewByParticipant] = useState<
    Record<string, SkewReading>
  >({});
  const [lastConnectError, setLastConnectError] = useState<string | null>(null);
  const [localSessionRole, setLocalSessionRole] = useState<"host" | "follower" | null>(null);
  const [lastCatchUpSummary, setLastCatchUpSummary] = useState<string | null>(null);
  const [hasAutoReconnectAttempted, setHasAutoReconnectAttempted] = useState(false);
  const [smokeChecklist, setSmokeChecklist] = useState({
    tabsJoined: false,
    sameRoom: false,
    sharedSong: false,
    syncSeek: false,
    distinctMix: false,
  });
  const [lastJoinSummary, setLastJoinSummary] = useState<{
    totalMs: number;
    connectedCount: number;
    failedCount: number;
    p95Ms: number;
  } | null>(null);

  const runtimeClientsRef = useRef<RuntimeClient[]>([]);
  const eventsRef = useRef<VisualEvent[]>([]);
  const ackMapRef = useRef<Map<string, Set<string>>>(new Map());
  const lastSeqByIssuerIdRef = useRef<Map<string, number>>(new Map());
  const toastCounterRef = useRef(1);
  const seqRef = useRef(0);
  const nextRandomOpRef = useRef<DemoCommandOp>("transport.play");
  const randomTimerRef = useRef<number | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const skewCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawFrameRef = useRef<number | null>(null);
  const skewDrawFrameRef = useRef<number | null>(null);
  const executionPulseTimersRef = useRef<Set<number>>(new Set());
  const connectAttemptIdRef = useRef(0);
  const connectAbortControllerRef = useRef<AbortController | null>(null);
  const localMinusServerClockOffsetMsRef = useRef(0);
  const connectBatchStartedAtRef = useRef<number | null>(null);
  const joinElapsedByClientRef = useRef<Map<string, number>>(new Map());
  const localPlaybackClientIdRef = useRef<string | null>(null);
  const playbackControllerRef = useRef<LabPlaybackController | null>(null);
  const playbackInitGenerationRef = useRef(0);
  const playbackSchedulerRef = useRef<DeterministicCommandScheduler | null>(null);
  const lateJoinCorrectionTimerRef = useRef<number | null>(null);
  const apiBaseUrlRef = useRef(apiBaseUrl);
  const participantLaneByIdRef = useRef<Map<string, number>>(new Map());
  const connectedParticipantCountRef = useRef(0);
  const deliveryLatencySamplesRef = useRef<number[]>([]);
  const applyOffsetSamplesRef = useRef<number[]>([]);
  const transportAuthorityParticipantIdRef = useRef<string | null>(null);
  const lastTransportPulseAtRef = useRef(0);
  const lastTransportPulsePositionRef = useRef(-1);
  const lastLeadResyncConnectedOthersRef = useRef(0);
  const lastLatencyCompResyncConnectedOthersRef = useRef(0);
  const activeSongLoadYoutubeIdRef = useRef<string | null>(null);
  const activeSongLoadAbortControllerRef = useRef<AbortController | null>(null);
  const songLoadRetryRequestRef = useRef<SongLoadRetryRequest | null>(null);
  const lastSongLoadScopePulseByParticipantRef = useRef<Map<string, number>>(new Map());
  const skewHistoryByParticipantRef = useRef<Map<string, { atMs: number; skewMs: number }[]>>(
    new Map(),
  );
  const latestLocalSkewMsRef = useRef(0);
  const lastSyncNudgeAtRef = useRef(0);
  const lastSyncNudgeLogAtRef = useRef(0);
  const lastProgressPublishAtRef = useRef(0);

  const syncSeqFromAuthoritative = useCallback((value: unknown) => {
    if (typeof value !== "number" || !Number.isFinite(value)) {
      return;
    }
    const nextSeq = Math.trunc(value);
    if (nextSeq < 0) {
      return;
    }
    seqRef.current = Math.max(seqRef.current, nextSeq);
  }, []);

  const pushSongLoadScopePulse = useCallback(
    (participantId: string, phase: RoomLoadPhase, percentage: number) => {
      if (phase === "queued") {
        return;
      }
      const now = Date.now();
      const force = phase === "ready" || phase === "error" || phase === "timeout";
      const lastAt = lastSongLoadScopePulseByParticipantRef.current.get(participantId) ?? 0;
      if (!force && now - lastAt < SONG_LOAD_SCOPE_MIN_INTERVAL_MS) {
        return;
      }
      lastSongLoadScopePulseByParticipantRef.current.set(participantId, now);
      const lane =
        participantLaneByIdRef.current.get(participantId) ??
        (participantId === sessionParticipantId ? 0 : undefined);
      if (lane === undefined) {
        return;
      }
      const events = eventsRef.current;
      events.push({
        atMs: now,
        lane,
        type: "download-progress",
        marker: "D",
        barPercent: clampPercent(percentage),
      });
      if (events.length > MAX_VISUAL_EVENTS) {
        events.splice(0, events.length - MAX_VISUAL_EVENTS);
      }
    },
    [sessionParticipantId],
  );

  const recordSkewSample = useCallback(
    (
      participantId: string,
      skewMs: number,
      sampledAtMs: number,
      authorityParticipantId: string,
    ) => {
      const clampedSkewMs = Number.isFinite(skewMs) ? Math.max(-2000, Math.min(2000, skewMs)) : 0;
      const safeSampledAtMs = Number.isFinite(sampledAtMs) ? sampledAtMs : Date.now();
      const history = skewHistoryByParticipantRef.current.get(participantId) ?? [];
      history.push({ atMs: safeSampledAtMs, skewMs: clampedSkewMs });
      const cutoffMs = Date.now() - SKEW_PLOT_WINDOW_MS;
      while (history.length > 0) {
        const first = history[0];
        if (
          !first ||
          (history.length <= SKEW_PLOT_MAX_POINTS_PER_PARTICIPANT && first.atMs >= cutoffMs)
        ) {
          break;
        }
        history.shift();
      }
      skewHistoryByParticipantRef.current.set(participantId, history);
      setLatestSkewByParticipant((prev) => ({
        ...prev,
        [participantId]: {
          skewMs: clampedSkewMs,
          sampledAtMs: safeSampledAtMs,
          authorityParticipantId,
        },
      }));
    },
    [],
  );

  const handleSyncPulse = useCallback(
    (
      payloadRaw: unknown,
      sourceParticipantId: string,
      sourceLane: number,
      commandIdRaw: unknown,
    ) => {
      if (sourceParticipantId === sessionParticipantId) {
        return;
      }
      if (payloadRaw === null || payloadRaw === undefined || typeof payloadRaw !== "object") {
        return;
      }
      const payload = payloadRaw as Record<string, unknown>;
      const authorityParticipantId =
        typeof payload.participantId === "string" ? payload.participantId.trim() : "";
      if (authorityParticipantId.length === 0) {
        return;
      }
      const currentAuthority = transportAuthorityParticipantIdRef.current;
      if (
        currentAuthority !== null &&
        currentAuthority.length > 0 &&
        authorityParticipantId !== currentAuthority
      ) {
        return;
      }
      transportAuthorityParticipantIdRef.current = authorityParticipantId;
      const stateRaw = typeof payload.state === "string" ? payload.state.trim() : "";
      const state: "playing" | "paused" | "stopped" =
        stateRaw === "playing" || stateRaw === "paused" || stateRaw === "stopped"
          ? stateRaw
          : "paused";
      const positionSec = typeof payload.positionSec === "number" ? payload.positionSec : NaN;
      const playbackRate = typeof payload.playbackRate === "number" ? payload.playbackRate : NaN;
      const sampledAtEpochMs =
        typeof payload.sampledAtEpochMs === "number" ? payload.sampledAtEpochMs : NaN;
      if (
        !Number.isFinite(positionSec) ||
        !Number.isFinite(playbackRate) ||
        !Number.isFinite(sampledAtEpochMs)
      ) {
        return;
      }
      const playback = playbackControllerRef.current;
      if (!playback) {
        return;
      }
      let localSnapshot: PlayerSnapshot;
      try {
        localSnapshot = playback.snapshot();
      } catch {
        return;
      }
      const nowMs = Date.now();
      const projectedAuthorityPositionSec =
        state === "playing" && playbackRate > 0
          ? Math.max(0, positionSec + ((nowMs - sampledAtEpochMs) / 1000) * playbackRate)
          : Math.max(0, positionSec);
      const driftMs = (projectedAuthorityPositionSec - localSnapshot.positionSec) * 1000;
      latestLocalSkewMsRef.current = driftMs;
      const absDriftMs = Math.abs(driftMs);
      if (absDriftMs <= SYNC_NUDGE_DEADBAND_MS) {
        return;
      }
      if (nowMs - lastSyncNudgeAtRef.current < SYNC_NUDGE_COOLDOWN_MS) {
        return;
      }
      const stepMs =
        Math.sign(driftMs) *
        Math.max(
          SYNC_NUDGE_MIN_STEP_MS,
          Math.min(SYNC_NUDGE_MAX_STEP_MS, absDriftMs * SYNC_NUDGE_ALPHA),
        );
      const targetPositionSec = Math.max(0, localSnapshot.positionSec + stepMs / 1000);
      const nudgePayload: BenchPayload = {
        commandId:
          typeof commandIdRaw === "string" && commandIdRaw.trim().length > 0
            ? `sync-nudge:${commandIdRaw}`
            : `sync-nudge:${nowMs}`,
        seq: 0,
        op: "transport.seek",
        issuedAtMs: nowMs,
        executeAtMs: nowMs,
        issuerId: authorityParticipantId,
        toPositionSec: targetPositionSec,
      };
      playback.applyCommand(nudgePayload);
      lastSyncNudgeAtRef.current = nowMs;
      const adjustmentMarker = getAdjustmentMarker(stepMs / 1000);
      const events = eventsRef.current;
      events.push({
        atMs: nowMs,
        lane: sourceLane,
        type: "execute-seek",
        ...(adjustmentMarker ? { marker: adjustmentMarker } : {}),
      });
      if (events.length > MAX_VISUAL_EVENTS) {
        events.splice(0, events.length - MAX_VISUAL_EVENTS);
      }
      if (nowMs - lastSyncNudgeLogAtRef.current >= 1000) {
        lastSyncNudgeLogAtRef.current = nowMs;
        const logLine =
          `sync pulse nudge: authority=${authorityParticipantId} drift=${formatSignedOffsetMs(driftMs)} ` +
          `step=${formatSignedOffsetMs(stepMs)} target=${targetPositionSec.toFixed(3)}s`;
        setLogLines((prev) => [`${new Date().toISOString()} ${logLine}`, ...prev].slice(0, 40));
      }
    },
    [sessionParticipantId],
  );

  const cognitoAuth = useMemo(() => {
    const domain = (import.meta.env.VITE_COGNITO_DOMAIN as string | undefined)?.trim();
    const clientId = (import.meta.env.VITE_COGNITO_CLIENT_ID as string | undefined)?.trim();
    const redirectUri = resolveCognitoRedirectUri(
      import.meta.env.VITE_COGNITO_REDIRECT_URI as string | undefined,
      {
        origin: window.location.origin,
        pathname: window.location.pathname,
      },
    );
    const scope = (
      (import.meta.env.VITE_COGNITO_SCOPE as string | undefined) ?? "openid email profile"
    ).trim();
    if (!domain || !clientId || !redirectUri) {
      return null;
    }
    return new CognitoAuthManager({
      domain,
      clientId,
      redirectUri,
      scope,
    });
  }, []);

  const tokenService = useMemo(
    () =>
      new TokenService({
        apiBaseUrl,
        mintPath: "/v2/band-sync/livekit/mint-token-open",
        ...(cognitoAuth
          ? {
              getAuthToken: async () => cognitoAuth.getIdToken(),
              onUnauthorized: async () => cognitoAuth.refreshIdToken(),
            }
          : {}),
      }),
    [apiBaseUrl, cognitoAuth],
  );

  const memberProfileService = useMemo(
    () =>
      new MemberProfileService({
        apiBaseUrl,
        ...(cognitoAuth
          ? {
              getAuthToken: async () => cognitoAuth.getIdToken(),
              onUnauthorized: async () => cognitoAuth.refreshIdToken(),
            }
          : {}),
      }),
    [apiBaseUrl, cognitoAuth],
  );

  const sessionSongService = useMemo(
    () =>
      new SessionSongService({
        apiBaseUrl,
        upsertPath: "/v2/band-sync/livekit/session-song/upsert-open",
        ...(cognitoAuth
          ? {
              getAuthToken: async () => cognitoAuth.getIdToken(),
              onUnauthorized: async () => cognitoAuth.refreshIdToken(),
            }
          : {}),
      }),
    [apiBaseUrl, cognitoAuth],
  );

  const sessionTransportService = useMemo(
    () =>
      new SessionTransportService({
        apiBaseUrl,
        upsertPath: "/v2/band-sync/livekit/session-transport/upsert-open",
        ...(cognitoAuth
          ? {
              getAuthToken: async () => cognitoAuth.getIdToken(),
              onUnauthorized: async () => cognitoAuth.refreshIdToken(),
            }
          : {}),
      }),
    [apiBaseUrl, cognitoAuth],
  );

  const connectedCount = clients.filter((client) => client.connected).length;
  const localClientConnected = clients.some(
    (client) => client.id === sessionParticipantId && client.connected,
  );
  const playbackIssueBlockReason = !localClientConnected ? "participant is not connected" : null;
  const canIssuePlaybackCommands = localClientConnected;
  const failedClients = clients.filter(
    (client) => client.status === "failed" && client.errorMessage !== undefined,
  );
  const skewSloPass =
    syncTelemetry.applyOffsetP95Ms <= 5 &&
    syncTelemetry.applyOffsetP99Ms <= 10 &&
    syncTelemetry.applyOffsetMaxMs <= 30;
  const backendLoadState = isLoadingBackendSong
    ? "loading"
    : playbackReady
      ? "ready"
      : playbackError
        ? "error"
        : "idle";
  const currentDescriptorHash = descriptorHash(currentSongDescriptor);
  const localRequiredStemRoles = useMemo(() => {
    const stemMap = currentSongDescriptor?.stemUrlsByRole;
    if (!stemMap) {
      return CORE_ROLES;
    }
    const present = CORE_ROLES.filter((role) => {
      const candidate = stemMap[role];
      return typeof candidate === "string" && candidate.trim().length > 0;
    });
    return present.length > 0 ? present : CORE_ROLES;
  }, [currentSongDescriptor]);
  const localReadyForPlayback = useMemo(() => {
    if (!playbackReady || !currentSongYoutubeId) {
      return false;
    }
    if (localRequiredStemRoles.length === 0) {
      return false;
    }
    return localRequiredStemRoles.every((role) => {
      const row = stemDownloadRows.find((candidate) => candidate.role === role);
      return row?.phase === "ready";
    });
  }, [currentSongYoutubeId, localRequiredStemRoles, playbackReady, stemDownloadRows]);
  const roomLoadStatuses = useMemo(
    () =>
      clients
        .filter(
          (client) =>
            client.connected &&
            (!currentSongYoutubeId ||
              roomSongLoadStatusByParticipant[client.id]?.songYoutubeId === currentSongYoutubeId),
        )
        .map((client) => {
          const fallback: RoomSongLoadStatus = {
            participantId: client.id,
            songYoutubeId: currentSongYoutubeId ?? "unknown",
            phase: "queued",
            percentage: 0,
            updatedAtMs: client.lastSeenAtEpochMs ?? Date.now(),
          };
          return {
            client,
            status: roomSongLoadStatusByParticipant[client.id] ?? fallback,
          };
        }),
    [clients, currentSongYoutubeId, roomSongLoadStatusByParticipant],
  );
  const waitingParticipants = roomLoadStatuses.filter(({ status }) => status.phase !== "ready");
  const roomReadyForPlayback = waitingParticipants.length === 0 && roomLoadStatuses.length > 0;
  const playBlockReason = !localReadyForPlayback
    ? "Local stems are still loading."
    : roomReadinessGateEnabled && !roomReadyForPlayback
      ? `Waiting for ${waitingParticipants.length} participant(s) to finish loading.`
      : null;
  const [inviteQrUrl, setInviteQrUrl] = useState("");
  const inviteUrl = useMemo(() => {
    if (typeof window === "undefined") {
      return "";
    }
    const shareOrigin = resolveShareOrigin();
    const next = new URL(`${shareOrigin}${window.location.pathname}`);
    const normalizedRoom = roomName.trim() || "sync-visual-demo";
    next.searchParams.set("room", normalizedRoom);
    const normalizedApiBase = normalizeApiBaseUrl(apiBaseUrl);
    if (normalizedApiBase !== DEFAULT_BAND_SYNC_V2_API_BASE_URL) {
      next.searchParams.set("apiBase", normalizedApiBase);
    }
    return next.toString();
  }, [apiBaseUrl, roomName]);
  useEffect(() => {
    let cancelled = false;
    if (!inviteUrl) {
      setInviteQrUrl("");
      return () => {
        cancelled = true;
      };
    }
    void toQrDataUrl(inviteUrl, {
      errorCorrectionLevel: "M",
      margin: 1,
      width: 180,
    })
      .then((dataUrl) => {
        if (!cancelled) {
          setInviteQrUrl(dataUrl);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setInviteQrUrl("");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [inviteUrl]);

  useEffect(() => {
    apiBaseUrlRef.current = apiBaseUrl;
  }, [apiBaseUrl]);

  useEffect(() => {
    localMinusServerClockOffsetMsRef.current = localMinusServerClockOffsetMs;
  }, [localMinusServerClockOffsetMs]);

  useEffect(() => {
    if (!freshGuestMode) {
      return;
    }
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      if (params.has("code") && params.has("state")) {
        return;
      }
    }
    clearBandSyncBrowserStorage();
  }, [freshGuestMode]);

  useEffect(() => {
    if (!freshGuestMode && typeof window !== "undefined") {
      window.localStorage.setItem(SESSION_ROOM_STORAGE_KEY, roomName);
    }
  }, [freshGuestMode, roomName]);

  useEffect(() => {
    if (!freshGuestMode && typeof window !== "undefined") {
      window.localStorage.setItem(AUTO_RECONNECT_STORAGE_KEY, autoReconnectEnabled ? "1" : "0");
    }
  }, [autoReconnectEnabled, freshGuestMode]);

  useEffect(() => {
    if (debugSyntheticModeEnabled) {
      return;
    }
    setTriggerMode("operator");
  }, [debugSyntheticModeEnabled]);

  useEffect(() => {
    connectedParticipantCountRef.current = clients.filter(
      (participant) => participant.connected,
    ).length;
  }, [clients]);

  useEffect(() => {
    const canvas = skewCanvasRef.current;
    if (!canvas) {
      return;
    }
    const draw = () => {
      const now = Date.now();
      const width = canvas.clientWidth || 900;
      const height = canvas.clientHeight || 240;
      const pixelRatio = window.devicePixelRatio || 1;
      if (
        canvas.width !== Math.floor(width * pixelRatio) ||
        canvas.height !== Math.floor(height * pixelRatio)
      ) {
        canvas.width = Math.floor(width * pixelRatio);
        canvas.height = Math.floor(height * pixelRatio);
      }
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        skewDrawFrameRef.current = window.requestAnimationFrame(draw);
        return;
      }
      ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
      ctx.clearRect(0, 0, width, height);
      ctx.fillStyle = "#030706";
      ctx.fillRect(0, 0, width, height);

      const left = 64;
      const right = width - 8;
      const top = 14;
      const bottom = height - 22;
      const centerY = (top + bottom) / 2;
      const plotWidth = Math.max(1, right - left);
      const plotHeight = Math.max(1, bottom - top);

      ctx.fillStyle = "#7fc595";
      ctx.font = "11px monospace";
      ctx.fillText("Live device skew (ms, vs authority)", 10, 12);

      const participantIds = clients.map((client) => client.id);
      const cutoffMs = now - SKEW_PLOT_WINDOW_MS;
      const maxAbsFromWindow = participantIds.reduce((max, participantId) => {
        const history = skewHistoryByParticipantRef.current.get(participantId) ?? [];
        for (const point of history) {
          if (point.atMs < cutoffMs) {
            continue;
          }
          max = Math.max(max, Math.abs(point.skewMs));
        }
        const latest = latestSkewByParticipant[participantId];
        if (latest) {
          max = Math.max(max, Math.abs(latest.skewMs));
        }
        return max;
      }, 0);
      // Zoom in by default to ±10ms, but autoscale out in 5ms steps when needed.
      const yMax = Math.max(
        SKEW_PLOT_MIN_ABS_MS,
        Math.min(600, Math.ceil(maxAbsFromWindow / 5) * 5 || SKEW_PLOT_MIN_ABS_MS),
      );

      ctx.strokeStyle = "#174333";
      ctx.lineWidth = 1;
      const yTicks = [-yMax, -yMax / 2, 0, yMax / 2, yMax];
      for (const tick of yTicks) {
        const y = centerY - (tick / yMax) * (plotHeight / 2);
        ctx.beginPath();
        ctx.moveTo(left, y);
        ctx.lineTo(right, y);
        ctx.stroke();
        ctx.fillStyle = tick === 0 ? "#9fe89f" : "#5f8a78";
        ctx.fillText(`${Math.round(tick)}ms`, 8, y + 3);
      }

      for (const participantId of participantIds) {
        const color = skewColorForParticipant(participantId);
        const history = skewHistoryByParticipantRef.current.get(participantId) ?? [];
        while (history.length > 0) {
          const first = history[0];
          if (!first || first.atMs >= cutoffMs) {
            break;
          }
          history.shift();
        }
        skewHistoryByParticipantRef.current.set(participantId, history);
        if (history.length < 2) {
          continue;
        }
        ctx.strokeStyle = color;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        for (let i = 0; i < history.length; i += 1) {
          const point = history[i];
          if (!point) {
            continue;
          }
          const x = left + ((point.atMs - cutoffMs) / SKEW_PLOT_WINDOW_MS) * plotWidth;
          const y = centerY - (point.skewMs / yMax) * (plotHeight / 2);
          if (i === 0) {
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }
        }
        ctx.stroke();
      }

      for (let i = 0; i < participantIds.length; i += 1) {
        const participantId = participantIds[i];
        if (!participantId) {
          continue;
        }
        const reading = latestSkewByParticipant[participantId];
        const labelY = bottom + 12 + (i % 2) * 10;
        const labelX = left + (i % 4) * (plotWidth / 4);
        const color = skewColorForParticipant(participantId);
        ctx.fillStyle = color;
        const shortId = participantId.slice(0, 18);
        const value = reading
          ? `${reading.skewMs >= 0 ? "+" : ""}${reading.skewMs.toFixed(1)}ms`
          : "n/a";
        ctx.fillText(`${shortId}: ${value}`, labelX, labelY);
      }

      skewDrawFrameRef.current = window.requestAnimationFrame(draw);
    };
    skewDrawFrameRef.current = window.requestAnimationFrame(draw);
    return () => {
      if (skewDrawFrameRef.current !== null) {
        window.cancelAnimationFrame(skewDrawFrameRef.current);
      }
    };
  }, [clients, latestSkewByParticipant]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      const now = Date.now();
      setRoomSongLoadStatusByParticipant((prev) => {
        let changed = false;
        const next: Record<string, RoomSongLoadStatus> = { ...prev };
        for (const [participantId, status] of Object.entries(prev)) {
          if (
            status.phase === "ready" ||
            status.phase === "error" ||
            status.phase === "timeout" ||
            now - status.updatedAtMs < ROOM_PROGRESS_TIMEOUT_MS
          ) {
            continue;
          }
          changed = true;
          next[participantId] = {
            ...status,
            phase: "timeout",
            errorMessage: "No progress update received in time.",
            updatedAtMs: now,
          };
        }
        return changed ? next : prev;
      });
    }, 1000);
    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      const delivery = deliveryLatencySamplesRef.current;
      const apply = applyOffsetSamplesRef.current;
      setSyncTelemetry({
        deliveryP95Ms: percentile(delivery, 0.95),
        deliveryP99Ms: percentile(delivery, 0.99),
        applyOffsetP95Ms: percentile(apply, 0.95),
        applyOffsetP99Ms: percentile(apply, 0.99),
        applyOffsetMaxMs: apply.length ? Math.max(...apply) : 0,
        deliverySampleCount: delivery.length,
        applySampleCount: apply.length,
      });
    }, 500);
    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    if (!commandBanner) {
      return;
    }
    const timeoutId = window.setTimeout(() => {
      setCommandBanner(null);
    }, 3000);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [commandBanner]);

  useEffect(() => {
    if (commandToasts.length === 0) {
      return;
    }
    const timeoutId = window.setTimeout(() => {
      setCommandToasts((prev) =>
        prev.filter((toast) => Date.now() - toast.createdAtEpochMs <= 7000).slice(-8),
      );
    }, 1200);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [commandToasts]);

  const appendLog = useCallback((line: string) => {
    setLogLines((prev) => [`${new Date().toISOString()} ${line}`, ...prev].slice(0, 40));
  }, []);

  const appendPlaybackTrace = useCallback((line: string) => {
    setPlaybackTraceLines((prev) => [`${new Date().toISOString()} ${line}`, ...prev].slice(0, 80));
  }, []);

  const publishRoomLoadProgress = useCallback(
    (status: RoomSongLoadStatus, force: boolean) => {
      const now = Date.now();
      if (!force && now - lastProgressPublishAtRef.current < ROOM_PROGRESS_BROADCAST_INTERVAL_MS) {
        return;
      }
      const localRuntime = runtimeClientsRef.current.find(
        (client) => client.id === sessionParticipantId,
      );
      if (!localRuntime?.transport) {
        return;
      }
      lastProgressPublishAtRef.current = now;
      localRuntime.transport.publish({
        op: "transport.songLoadProgress",
        commandId: `${sessionParticipantId}:song-load:${now}`,
        payload: {
          participantId: sessionParticipantId,
          songYoutubeId: status.songYoutubeId,
          phase: status.phase,
          percentage: clampPercent(status.percentage),
          updatedAtMs: status.updatedAtMs,
          ...(status.errorMessage ? { errorMessage: status.errorMessage } : {}),
        },
      });
    },
    [sessionParticipantId],
  );

  const upsertRoomSongLoadStatus = useCallback(
    (
      status: RoomSongLoadStatus,
      options?: { readonly broadcast?: boolean; readonly force?: boolean },
    ) => {
      setRoomSongLoadStatusByParticipant((prev) => ({
        ...prev,
        [status.participantId]: status,
      }));
      if (options?.broadcast) {
        publishRoomLoadProgress(status, options.force ?? false);
      }
    },
    [publishRoomLoadProgress],
  );

  const pushCommandToast = useCallback((message: string, tone: "info" | "warning") => {
    const now = Date.now();
    const nextId = toastCounterRef.current;
    toastCounterRef.current += 1;
    setCommandToasts((prev) =>
      [...prev, { id: nextId, message, tone, createdAtEpochMs: now }]
        .filter((toast) => now - toast.createdAtEpochMs <= 7000)
        .slice(-8),
    );
  }, []);

  const syncMemberProfileToBackend = useCallback(
    async (nextNickname: string, nextPresets: MixPreset[]) => {
      if (freshGuestMode) {
        return;
      }
      if (!runtimeClientsRef.current.length) {
        return;
      }
      try {
        await memberProfileService.upsertProfile({
          sessionId: roomName,
          participantId: sessionParticipantId,
          nickname: nextNickname,
          mixPresets: nextPresets,
        });
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        appendLog(`profile sync failed: ${message}`);
      }
    },
    [appendLog, freshGuestMode, memberProfileService, roomName, sessionParticipantId],
  );

  const loadMemberProfileFromBackend = useCallback(async () => {
    try {
      const profile = await memberProfileService.getProfile({
        sessionId: roomName,
        participantId: sessionParticipantId,
      });
      setMixPresets(freshGuestMode ? [] : profile.mixPresets);
      if (profile.nickname && profile.nickname !== participantProfile.nickname) {
        if (freshGuestMode) {
          setParticipantProfile((prev) => ({
            ...prev,
            nickname: profile.nickname.trim() || "Guest",
          }));
        } else {
          setParticipantProfile((prev) => updateParticipantNickname(prev, profile.nickname));
        }
      }
      appendLog(`loaded profile (${profile.mixPresets.length} presets)`);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      appendLog(`profile load failed: ${message}`);
    }
  }, [
    appendLog,
    freshGuestMode,
    memberProfileService,
    participantProfile.nickname,
    roomName,
    sessionParticipantId,
  ]);

  const refreshStemMixerRows = useCallback((includeLevels: boolean) => {
    const playback = playbackControllerRef.current;
    if (!playback) {
      setStemMixerRows([]);
      return;
    }
    try {
      const roles = playback.getStemRoles();
      if (roles.length === 0) {
        setStemMixerRows([]);
        return;
      }
      setStemMixerRows((prev) =>
        roles.map((role) => {
          const settings = playback.getStemSettings(role) ?? {
            volume: 1,
            muted: false,
            solo: false,
          };
          const previous = prev.find((row) => row.role === role);
          const previousLevel = previous?.level ?? 0;
          const rawLevel = includeLevels ? playback.getStemLevel(role) : previousLevel;
          return {
            role,
            volume: settings.volume,
            muted: settings.muted,
            solo: settings.solo,
            level: includeLevels ? meterLevel01FromRmsLinear(rawLevel) : previousLevel,
          };
        }),
      );
    } catch {
      // Controller may exist while player is still initializing.
      setStemMixerRows([]);
    }
  }, []);

  const loadSongByDescriptor = useCallback(
    async (
      song: SessionSongDescriptor,
      source: "join" | "command",
      requestedSignal?: AbortSignal,
    ) => {
      const playback = playbackControllerRef.current;
      if (!playback) {
        return;
      }
      const youtubeId = song.youtubeId.trim();
      if (!youtubeId) {
        return;
      }
      if (activeSongLoadYoutubeIdRef.current === youtubeId) {
        return;
      }
      songLoadRetryRequestRef.current = { kind: "descriptor", song, source };
      activeSongLoadYoutubeIdRef.current = youtubeId;
      setHasRetryableSongLoad(false);
      const localAbortController = requestedSignal ? null : new AbortController();
      const loadSignal = requestedSignal ?? localAbortController?.signal;
      if (localAbortController) {
        activeSongLoadAbortControllerRef.current = localAbortController;
      }
      let stallIntervalId: number | null = null;
      try {
        setIsLoadingBackendSong(true);
        setPlaybackReady(false);
        setPlaybackError(null);
        setBackendLoadMessage(`Loading session song ${youtubeId}...`);
        const queuedRows: StemDownloadRow[] = CORE_ROLES.map((role) => ({
          role,
          phase: "queued",
          loadedBytes: 0,
        }));
        setStemDownloadRows(queuedRows);
        upsertRoomSongLoadStatus(
          {
            participantId: sessionParticipantId,
            songYoutubeId: youtubeId,
            phase: "queued",
            percentage: 0,
            updatedAtMs: Date.now(),
          },
          { broadcast: true, force: true },
        );
        await playback.resumeContext();
        setBackendLoadMessage(
          `${source === "command" ? "Session changed song. " : ""}Downloading and decoding six stems...`,
        );
        const lastProgressAtByRole = new Map<string, number>();
        for (const role of CORE_ROLES) {
          lastProgressAtByRole.set(role, Date.now());
        }
        stallIntervalId = window.setInterval(() => {
          const now = Date.now();
          setStemDownloadRows((prev) =>
            prev.map((row) => {
              if (row.phase !== "downloading" && row.phase !== "decoding") {
                return row;
              }
              const lastProgressAt = lastProgressAtByRole.get(row.role) ?? now;
              if (now - lastProgressAt < 8000) {
                return row;
              }
              if (row.message === "stalled (>8s without bytes). Retry available.") {
                return row;
              }
              return {
                ...row,
                message: "stalled (>8s without bytes). Retry available.",
              };
            }),
          );
        }, 1200);
        const loaded = await playback.loadSongFromBackendReference(
          apiBaseUrlRef.current,
          {
            youtubeId,
            ...(song.title ? { title: song.title } : {}),
            ...(song.smamsUrl ? { smamsUrl: song.smamsUrl } : {}),
            ...(song.stemUrlsByRole ? { stemUrlsByRole: song.stemUrlsByRole } : {}),
          },
          (progress: StemLoadProgress) => {
            lastProgressAtByRole.set(progress.role, Date.now());
            let aggregatePhase: RoomLoadPhase = "downloading";
            let aggregatePercentage = 0;
            setStemDownloadRows((prev) => {
              const existing = prev.find((row) => row.role === progress.role);
              const nextRow: StemDownloadRow = {
                role: progress.role,
                phase: progress.phase,
                loadedBytes: progress.loadedBytes,
                ...(progress.totalBytes !== undefined ? { totalBytes: progress.totalBytes } : {}),
              };
              const nextRows =
                existing === undefined
                  ? [...prev, nextRow]
                  : prev.map((row) => (row.role === progress.role ? nextRow : row));
              const aggregate = aggregateStemRows(nextRows);
              aggregatePhase = aggregate.phase;
              aggregatePercentage = aggregate.percentage;
              return nextRows;
            });
            upsertRoomSongLoadStatus(
              {
                participantId: sessionParticipantId,
                songYoutubeId: youtubeId,
                phase: aggregatePhase,
                percentage: aggregatePercentage,
                updatedAtMs: Date.now(),
              },
              { broadcast: true },
            );
            pushSongLoadScopePulse(sessionParticipantId, aggregatePhase, aggregatePercentage);
          },
          loadSignal,
        );
        window.clearInterval(stallIntervalId);
        stallIntervalId = null;
        const normalizedStemUrls = normalizeStemUrlsByRole(loaded.stemUrlsByRole);
        const nextDescriptor: SessionSongDescriptor = {
          youtubeId: loaded.youtubeId,
          title: loaded.title,
          ...(loaded.smamsUrl ? { smamsUrl: loaded.smamsUrl } : {}),
          ...(normalizedStemUrls ? { stemUrlsByRole: normalizedStemUrls } : {}),
        };
        setCurrentSongYoutubeId(loaded.youtubeId);
        setCurrentSongDescriptor(nextDescriptor);
        setBackendSongLabel(`${loaded.title} (${loaded.youtubeId})`);
        setPlaybackReady(true);
        setPlaybackError(null);
        setBackendLoadMessage(`Ready: ${loaded.title} (${loaded.stemCount} stems loaded).`);
        upsertRoomSongLoadStatus(
          {
            participantId: sessionParticipantId,
            songYoutubeId: loaded.youtubeId,
            phase: "ready",
            percentage: 100,
            updatedAtMs: Date.now(),
          },
          { broadcast: true, force: true },
        );
        pushSongLoadScopePulse(sessionParticipantId, "ready", 100);
        refreshStemMixerRows(false);
        appendLog(
          `${source === "join" ? "loaded session song on join" : "loaded session song from command"}: "${loaded.title}" (${loaded.youtubeId})`,
        );
        setHasRetryableSongLoad(false);
      } catch (error) {
        if (isAbortLikeError(error)) {
          setPlaybackReady(false);
          setPlaybackError(null);
          setBackendLoadMessage("Song load cancelled. Press Retry to try again.");
          setStemDownloadRows((prev) =>
            prev.map((row) =>
              row.phase === "ready"
                ? row
                : {
                    ...row,
                    phase: "error",
                    message: "cancelled by user",
                  },
            ),
          );
          upsertRoomSongLoadStatus(
            {
              participantId: sessionParticipantId,
              songYoutubeId: youtubeId,
              phase: "error",
              percentage: 0,
              updatedAtMs: Date.now(),
              errorMessage: "cancelled by user",
            },
            { broadcast: true, force: true },
          );
          appendLog(`session song load cancelled for ${youtubeId}`);
          setHasRetryableSongLoad(true);
          return;
        }
        const message = error instanceof Error ? error.message : "song load by id failed";
        const friendly = classifyLoadError(message);
        setPlaybackReady(false);
        setPlaybackError(friendly);
        setBackendLoadMessage(`Session song load failed: ${friendly}`);
        setStemDownloadRows((prev) =>
          prev.map((row) =>
            row.phase === "ready"
              ? row
              : {
                  ...row,
                  phase: "error",
                  message: friendly,
                },
          ),
        );
        upsertRoomSongLoadStatus(
          {
            participantId: sessionParticipantId,
            songYoutubeId: youtubeId,
            phase: "error",
            percentage: 0,
            updatedAtMs: Date.now(),
            errorMessage: friendly,
          },
          { broadcast: true, force: true },
        );
        appendLog(`session song load failed for ${youtubeId}: ${friendly}`);
        setHasRetryableSongLoad(true);
      } finally {
        if (stallIntervalId !== null) {
          window.clearInterval(stallIntervalId);
        }
        if (
          localAbortController &&
          activeSongLoadAbortControllerRef.current === localAbortController
        ) {
          activeSongLoadAbortControllerRef.current = null;
        }
        if (activeSongLoadYoutubeIdRef.current === youtubeId) {
          activeSongLoadYoutubeIdRef.current = null;
        }
        setIsLoadingBackendSong(false);
      }
    },
    [
      appendLog,
      pushSongLoadScopePulse,
      refreshStemMixerRows,
      sessionParticipantId,
      upsertRoomSongLoadStatus,
    ],
  );

  const applyCatchUpTransportSnapshot = useCallback(
    async (snapshot: SessionTransportSnapshot) => {
      const playback = playbackControllerRef.current;
      if (!playback) {
        return null;
      }
      await playback.resumeContext();
      const sampledAtEpochMs = snapshot.sampledAtEpochMs ?? Date.now();
      const applyNowEpochMs = Date.now();
      const projectedPositionSec = projectAuthoritativePositionSec(snapshot, applyNowEpochMs);
      const stateOp: DemoCommandOp =
        snapshot.state === "playing" ? "transport.play" : "transport.pause";
      const applyOrder: BenchPayload[] = [
        {
          commandId: "join-bootstrap:rate",
          seq: 0,
          op: "transport.setRate",
          issuedAtMs: Date.now(),
          executeAtMs: Date.now(),
          issuerId: "join-bootstrap",
          rate: snapshot.playbackRate,
        },
        {
          commandId: "join-bootstrap:pitch",
          seq: 0,
          op: "transport.setPitch",
          issuedAtMs: Date.now(),
          executeAtMs: Date.now(),
          issuerId: "join-bootstrap",
          pitchSemitones: snapshot.pitchSemitones,
        },
        {
          commandId: "join-bootstrap:seek",
          seq: 0,
          op: "transport.seek",
          issuedAtMs: Date.now(),
          executeAtMs: Date.now(),
          issuerId: "join-bootstrap",
          toPositionSec: projectedPositionSec,
        },
        {
          commandId: "join-bootstrap:state",
          seq: 0,
          op: stateOp,
          issuedAtMs: Date.now(),
          executeAtMs: Date.now(),
          issuerId: "join-bootstrap",
        },
      ];
      for (const payload of applyOrder) {
        playback.applyCommand(payload);
      }
      if (snapshot.issuerId) {
        transportAuthorityParticipantIdRef.current = snapshot.issuerId;
      }
      setSeekTargetSec(projectedPositionSec);
      setScrubPositionSec(projectedPositionSec);
      setTempoRate(snapshot.playbackRate);
      setKeySemitones(snapshot.pitchSemitones);
      const summary = `state=${snapshot.state} pos=${projectedPositionSec.toFixed(3)}s rate=${snapshot.playbackRate.toFixed(2)}x pitch=${snapshot.pitchSemitones.toFixed(2)}st`;
      setLastCatchUpSummary(summary);
      pushCommandToast(`catch-up applied (${summary})`, "info");
      appendLog(
        `applied catch-up snapshot: state=${snapshot.state} pos=${projectedPositionSec.toFixed(3)} rate=${snapshot.playbackRate.toFixed(2)} pitch=${snapshot.pitchSemitones.toFixed(2)} sampleAgeMs=${Math.max(0, applyNowEpochMs - sampledAtEpochMs).toFixed(0)}`,
      );
      return {
        projectedPositionSec,
        applyNowEpochMs,
      };
    },
    [appendLog, pushCommandToast],
  );

  const applyJoinerSecondPassCorrection = useCallback(
    async (snapshot: SessionTransportSnapshot) => {
      const playback = playbackControllerRef.current;
      if (!playback) {
        return;
      }
      await playback.resumeContext();
      let localBefore: PlayerSnapshot;
      try {
        localBefore = playback.snapshot();
      } catch {
        return;
      }
      const correctionAtEpochMs = Date.now();
      const plan = planLateJoinCorrection({
        snapshot,
        localPositionSec: localBefore.positionSec,
        correctionAtEpochMs,
      });
      if (!plan.shouldCorrect) {
        appendLog(
          `late-join correction skipped: reason=${plan.reason} beforeOffset=${formatSignedOffsetMs(plan.beforeOffsetMs)} authPos=${plan.authoritativePositionSec.toFixed(3)}s localPos=${plan.localPositionSec.toFixed(3)}s`,
        );
        return;
      }
      const correctionPayload: BenchPayload = {
        commandId: "join-bootstrap:phase-b-seek",
        seq: 0,
        op: "transport.seek",
        issuedAtMs: correctionAtEpochMs,
        executeAtMs: correctionAtEpochMs,
        issuerId: "join-bootstrap",
        toPositionSec: plan.authoritativePositionSec,
      };
      playback.applyCommand(correctionPayload);
      let localAfterPositionSec = plan.authoritativePositionSec;
      try {
        localAfterPositionSec = playback.snapshot().positionSec;
      } catch {
        // Keep projected position when immediate snapshot is unavailable.
      }
      const afterOffsetMs = (plan.authoritativePositionSec - localAfterPositionSec) * 1000;
      setSeekTargetSec(plan.authoritativePositionSec);
      setScrubPositionSec(plan.authoritativePositionSec);
      const adjustmentMarker = getAdjustmentMarker(
        plan.authoritativePositionSec - plan.localPositionSec,
      );
      const localLane = participantLaneByIdRef.current.get(sessionParticipantId) ?? 0;
      const events = eventsRef.current;
      events.push({
        atMs: Date.now(),
        lane: localLane,
        type: "execute-seek",
        ...(adjustmentMarker ? { marker: adjustmentMarker } : {}),
      });
      if (events.length > MAX_VISUAL_EVENTS) {
        events.splice(0, events.length - MAX_VISUAL_EVENTS);
      }
      appendLog(
        `late-join correction applied: reason=${plan.reason} beforeOffset=${formatSignedOffsetMs(plan.beforeOffsetMs)} afterOffset=${formatSignedOffsetMs(afterOffsetMs)} target=${plan.authoritativePositionSec.toFixed(3)}s localBefore=${plan.localPositionSec.toFixed(3)}s localAfter=${localAfterPositionSec.toFixed(3)}s`,
      );
      pushCommandToast(
        `late-join correction seek (${Math.abs(plan.beforeOffsetMs).toFixed(0)}ms drift)`,
        "warning",
      );
    },
    [appendLog, pushCommandToast, sessionParticipantId],
  );

  const capturePlaybackSnapshot = useCallback(
    (stage: "receive" | "apply", payload: BenchPayload, receiverId: string) => {
      const playback = playbackControllerRef.current;
      if (!playback) {
        return;
      }
      try {
        const audibleLatencyCompensationMs = playback.estimateAudibleOutputLatencyMs();
        const output = playback.captureOutputTimeModel(
          resolveExecuteAtPerfMs(
            payload,
            localMinusServerClockOffsetMsRef.current,
            audibleLatencyCompensationMs,
          ),
        );
        const snapshot = playback.snapshot();
        const commandId = resolveCommandId(payload);
        appendPlaybackTrace(
          `[${commandId} seq ${payload.seq}] ${stage} ${payload.op} via ${receiverId} ` +
            `wall=${Math.round(output.nowWallClockMs)}ms outputCtx=${output.outputContextTimeSec.toFixed(3)}s ` +
            `targetCtx=${output.projectedContextAtExecuteSec.toFixed(3)}s position=${snapshot.positionSec.toFixed(3)}s ` +
            `state=${snapshot.state} rate=${snapshot.playbackRate.toFixed(2)} key=${snapshot.pitchSemitones.toFixed(2)}`,
        );
      } catch {
        // Ignore transient snapshots before init; next command captures once ready.
      }
    },
    [appendPlaybackTrace],
  );

  const scheduleAudioApply = useCallback(
    (payload: BenchPayload, receiverId: string) => {
      if (receiverId !== localPlaybackClientIdRef.current) {
        return;
      }
      const playback = playbackControllerRef.current;
      const scheduler = playbackSchedulerRef.current;
      if (!playback || !scheduler) {
        return;
      }

      const commandId = resolveCommandId(payload);
      const shouldCompensateAudibleLatency =
        latencyCompEnabled &&
        (payload.op === "transport.play" ||
          payload.op === "transport.pause" ||
          payload.op === "transport.seek" ||
          payload.op === "transport.setRate" ||
          payload.op === "transport.setPitch");
      const audibleLatencyCompensationMs = shouldCompensateAudibleLatency
        ? playback.estimateAudibleOutputLatencyMs()
        : 0;
      const executeAtPerfMs = resolveExecuteAtPerfMs(
        payload,
        localMinusServerClockOffsetMsRef.current,
        audibleLatencyCompensationMs,
      );
      const decision = scheduler.schedule(commandId, executeAtPerfMs, () => {
        if (payload.op === "transport.setLead") {
          const nextLeadMs = clampScheduledLeadMs(payload.leadMs ?? 0);
          setLeadMs(nextLeadMs);
          pushCommandToast(
            `applied transport.setLead ${nextLeadMs}ms from ${payload.issuerId} (seq ${payload.seq})`,
            payload.issuerId === sessionParticipantId ? "info" : "warning",
          );
          return;
        }
        if (payload.op === "transport.setLatencyComp") {
          const nextEnabled = payload.latencyCompEnabled !== false;
          setLatencyCompEnabled(nextEnabled);
          pushCommandToast(
            `applied transport.setLatencyComp ${nextEnabled ? "ON" : "OFF"} from ${payload.issuerId} (seq ${payload.seq})`,
            payload.issuerId === sessionParticipantId ? "info" : "warning",
          );
          return;
        }
        void playback
          .resumeContext()
          .then(() => {
            if (payload.op === "transport.setSong" && payload.songYoutubeId) {
              const currentStemMap = normalizeStemUrlsByRole(currentSongDescriptor?.stemUrlsByRole);
              const incomingStemMap = normalizeStemUrlsByRole(payload.songStemUrlsByRole);
              const currentStemJson = JSON.stringify(currentStemMap ?? {});
              const incomingStemJson = JSON.stringify(incomingStemMap ?? {});
              const currentSmamsUrl = currentSongDescriptor?.smamsUrl;
              const incomingSmamsUrl = payload.songSmamsUrl;
              if (
                payload.songYoutubeId === currentSongYoutubeId &&
                playbackReady &&
                (currentSmamsUrl ?? "") === (incomingSmamsUrl ?? "") &&
                currentStemJson === incomingStemJson
              ) {
                return;
              }
              void loadSongByDescriptor(
                {
                  youtubeId: payload.songYoutubeId,
                  title: payload.songTitle ?? "Session Song",
                  ...(payload.songSmamsUrl ? { smamsUrl: payload.songSmamsUrl } : {}),
                  ...(payload.songStemUrlsByRole
                    ? { stemUrlsByRole: payload.songStemUrlsByRole }
                    : {}),
                },
                "command",
              );
              return;
            }
            playback.applyCommand(payload);
            const applyOffsetMs = Math.abs(Date.now() - resolveAuthoritativeExecuteAtMs(payload));
            const applySamples = applyOffsetSamplesRef.current;
            applySamples.push(applyOffsetMs);
            if (applySamples.length > MAX_TELEMETRY_SAMPLES) {
              applySamples.splice(0, applySamples.length - MAX_TELEMETRY_SAMPLES);
            }
            setParticipantTelemetry((prev) => {
              const existing = prev[receiverId] ?? {
                receives: 0,
                applies: 0,
                lastDeliveryLatencyMs: 0,
                lastApplyOffsetMs: 0,
              };
              return {
                ...prev,
                [receiverId]: {
                  ...existing,
                  applies: existing.applies + 1,
                  lastApplyOffsetMs: applyOffsetMs,
                },
              };
            });
            capturePlaybackSnapshot("apply", payload, receiverId);
            pushCommandToast(
              `applied ${payload.op} from ${payload.issuerId} (seq ${payload.seq})`,
              payload.issuerId === sessionParticipantId ? "info" : "warning",
            );
          })
          .catch((error: unknown) => {
            const message = error instanceof Error ? error.message : "audio apply failed";
            setPlaybackError(message);
            appendLog(`audio apply failed for ${payload.op}: ${message}`);
          });
      });

      if (decision.kind === "dropped") {
        appendLog(
          `dropped late ${payload.op} commandId=${commandId} by ${decision.latenessMs.toFixed(1)}ms`,
        );
      }
    },
    [
      appendLog,
      capturePlaybackSnapshot,
      currentSongDescriptor,
      currentSongYoutubeId,
      latencyCompEnabled,
      loadSongByDescriptor,
      playbackReady,
      pushCommandToast,
      sessionParticipantId,
    ],
  );

  const loadRandomBackendSong = useCallback(async () => {
    const playback = playbackControllerRef.current;
    if (!playback) {
      return;
    }
    const localRuntime = runtimeClientsRef.current.find(
      (client) => client.id === sessionParticipantId,
    );
    const descriptorAbortController = new AbortController();
    activeSongLoadAbortControllerRef.current = descriptorAbortController;
    songLoadRetryRequestRef.current = { kind: "random" };
    setHasRetryableSongLoad(false);
    setIsLoadingBackendSong(true);
    setPlaybackReady(false);
    setPlaybackError(null);
    setBackendLoadMessage("Requesting random song descriptor from backend...");
    setStemDownloadRows(
      CORE_ROLES.map((role) => ({
        role,
        phase: "queued",
        loadedBytes: 0,
      })),
    );
    try {
      await playback.resumeContext();
      if (localRuntime) {
        const pauseSeq = ++seqRef.current;
        const pauseIssuedAtMs = Date.now();
        const pauseExecuteAtMs = pauseIssuedAtMs;
        const pauseCommandId = `${localRuntime.id}:${pauseSeq}`;
        lastSeqByIssuerIdRef.current.set(localRuntime.id, pauseSeq);
        const pausePayload = {
          commandId: pauseCommandId,
          seq: pauseSeq,
          op: "transport.pause" as const,
          issuedAtMs: pauseIssuedAtMs,
          executeAtMs: pauseExecuteAtMs,
          issuerId: localRuntime.id,
          issuerCountry: localRuntime.countryCode,
        };
        playback.applyCommand(pausePayload);
        localRuntime.transport?.publish({
          op: "transport.pause",
          commandId: pauseCommandId,
          payload: pausePayload,
        });
        appendLog(
          `[${localRuntime.id} ${countryCodeToFlagEmoji(localRuntime.countryCode)}] pre-load broadcast transport.pause seq=${pauseSeq} commandId=${pauseCommandId}`,
        );
      }
      const randomDescriptor = await playback.loadRandomSongDescriptorFromBackend(
        apiBaseUrlRef.current,
        descriptorAbortController.signal,
      );
      setBackendSongLabel(`${randomDescriptor.title} (${randomDescriptor.youtubeId})`);
      setCurrentSongYoutubeId(randomDescriptor.youtubeId);
      const normalizedStemUrls = normalizeStemUrlsByRole(randomDescriptor.stemUrlsByRole);
      const descriptor: SessionSongDescriptor = {
        youtubeId: randomDescriptor.youtubeId,
        title: randomDescriptor.title,
        ...(randomDescriptor.smamsUrl ? { smamsUrl: randomDescriptor.smamsUrl } : {}),
        ...(normalizedStemUrls ? { stemUrlsByRole: normalizedStemUrls } : {}),
      };
      setCurrentSongDescriptor(descriptor);
      setBackendLoadMessage(
        `Broadcasting "${randomDescriptor.title}" to room. All participants now load stems in parallel...`,
      );
      setRoomSongLoadStatusByParticipant((prev) => {
        const now = Date.now();
        const next: Record<string, RoomSongLoadStatus> = { ...prev };
        for (const client of clients) {
          if (!client.connected) {
            continue;
          }
          next[client.id] = {
            participantId: client.id,
            songYoutubeId: randomDescriptor.youtubeId,
            phase: "queued",
            percentage: 0,
            updatedAtMs: now,
          };
        }
        return next;
      });
      upsertRoomSongLoadStatus(
        {
          participantId: sessionParticipantId,
          songYoutubeId: randomDescriptor.youtubeId,
          phase: "queued",
          percentage: 0,
          updatedAtMs: Date.now(),
        },
        { broadcast: true, force: true },
      );
      if (localRuntime) {
        const seq = ++seqRef.current;
        const issuedAtMs = Date.now();
        let persistedSongUpdatedAtEpochMs: number | null = null;
        let authoritativeSongCommandId: string | null = null;
        let authoritativeSongSeq: number | null = null;
        try {
          const persistedSong = await sessionSongService.upsertSessionSong({
            sessionId: roomName,
            participantId: sessionParticipantId,
            currentSongYoutubeId: randomDescriptor.youtubeId,
            currentSongTitle: randomDescriptor.title,
            ...(randomDescriptor.smamsUrl
              ? { currentSongSmamsUrl: randomDescriptor.smamsUrl }
              : {}),
            ...(normalizedStemUrls ? { currentSongStemUrlsByRole: normalizedStemUrls } : {}),
          });
          persistedSongUpdatedAtEpochMs = persistedSong.updatedAtEpochMs;
          authoritativeSongCommandId = persistedSong.commandId ?? null;
          authoritativeSongSeq = typeof persistedSong.seq === "number" ? persistedSong.seq : null;
        } catch (error) {
          const message = error instanceof Error ? error.message : String(error);
          appendLog(`session song persistence failed: ${message}`);
        }
        const executeAtMs = issuedAtMs + leadMs;
        const authoritativeExecuteAtMs =
          persistedSongUpdatedAtEpochMs !== null
            ? persistedSongUpdatedAtEpochMs + leadMs
            : executeAtMs;
        const commandId = authoritativeSongCommandId ?? `${localRuntime.id}:${seq}`;
        const resolvedSeq = authoritativeSongSeq ?? seq;
        seqRef.current = Math.max(seqRef.current, resolvedSeq);
        const payload = {
          commandId,
          seq: resolvedSeq,
          op: "transport.setSong" as const,
          issuedAtMs,
          executeAtMs,
          ...(authoritativeExecuteAtMs !== executeAtMs ? { authoritativeExecuteAtMs } : {}),
          issuerId: localRuntime.id,
          issuerCountry: localRuntime.countryCode,
          songYoutubeId: randomDescriptor.youtubeId,
          songTitle: randomDescriptor.title,
          ...(randomDescriptor.smamsUrl ? { songSmamsUrl: randomDescriptor.smamsUrl } : {}),
          ...(normalizedStemUrls ? { songStemUrlsByRole: normalizedStemUrls } : {}),
        };
        localRuntime.transport?.publish({
          op: "transport.setSong",
          commandId,
          ...(authoritativeExecuteAtMs !== executeAtMs
            ? { authoritativeExecuteAtMs, executeAtServerMonoMs: authoritativeExecuteAtMs }
            : {}),
          payload,
        });
        pushCommandToast(
          `issued transport.setSong by ${localRuntime.id} (seq ${resolvedSeq})`,
          "info",
        );
        appendLog(
          `broadcast session song descriptor: "${randomDescriptor.title}" (${randomDescriptor.youtubeId}) executeAt=${Math.round(authoritativeExecuteAtMs)}`,
        );
      }
      await loadSongByDescriptor(descriptor, "command", descriptorAbortController.signal);
      setHasRetryableSongLoad(false);
    } catch (error) {
      if (isAbortLikeError(error)) {
        setPlaybackReady(false);
        setPlaybackError(null);
        setBackendLoadMessage("Song load cancelled. Press Retry to try again.");
        appendLog("backend random song load cancelled");
        setHasRetryableSongLoad(true);
        return;
      }
      const message = error instanceof Error ? error.message : "backend random song load failed";
      const friendly = classifyLoadError(message);
      setPlaybackReady(false);
      setBackendSongLabel("not loaded");
      setCurrentSongYoutubeId(null);
      setCurrentSongDescriptor(null);
      setPlaybackError(friendly);
      setBackendLoadMessage(`Backend load failed: ${friendly}`);
      setStemDownloadRows((prev) =>
        prev.map((row) =>
          row.phase === "ready"
            ? row
            : {
                ...row,
                phase: "error",
                message: friendly,
              },
        ),
      );
      setStemMixerRows([]);
      appendLog(`backend random song load failed: ${friendly}`);
      setHasRetryableSongLoad(true);
    } finally {
      if (activeSongLoadAbortControllerRef.current === descriptorAbortController) {
        activeSongLoadAbortControllerRef.current = null;
      }
      setIsLoadingBackendSong(false);
    }
  }, [
    appendLog,
    clients,
    leadMs,
    loadSongByDescriptor,
    pushCommandToast,
    roomName,
    sessionParticipantId,
    sessionSongService,
    upsertRoomSongLoadStatus,
  ]);

  const cancelSongLoad = useCallback(() => {
    const controller = activeSongLoadAbortControllerRef.current;
    if (!controller) {
      return;
    }
    controller.abort();
    setBackendLoadMessage("Cancelling song load...");
    appendLog("cancel requested for backend song load");
    setHasRetryableSongLoad(true);
  }, [appendLog]);

  const retrySongLoad = useCallback(() => {
    if (isLoadingBackendSong) {
      return;
    }
    const request = songLoadRetryRequestRef.current;
    if (!request) {
      return;
    }
    if (request.kind === "random") {
      void loadRandomBackendSong();
      return;
    }
    if (request.song && request.source) {
      void loadSongByDescriptor(request.song, request.source);
    }
  }, [isLoadingBackendSong, loadRandomBackendSong, loadSongByDescriptor]);

  useEffect(() => {
    if (!cognitoAuth) {
      setIsAuthReady(true);
      setIsAuthenticated(false);
      setAuthError(null);
      return;
    }
    void (async () => {
      try {
        const authed = await cognitoAuth.initialize();
        setIsAuthenticated(authed);
        setAuthError(null);
        appendLog(authed ? "cognito session restored" : "cognito session not found");
      } catch (error) {
        const message = error instanceof Error ? error.message : "Cognito auth failed";
        setIsAuthenticated(false);
        setAuthError(message);
        appendLog(`auth init failed: ${message}`);
      } finally {
        setIsAuthReady(true);
      }
    })();
  }, [appendLog, cognitoAuth]);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.AudioContext === "undefined") {
      setPlaybackError("AudioContext is unavailable in this browser");
      return;
    }
    const generation = playbackInitGenerationRef.current + 1;
    playbackInitGenerationRef.current = generation;
    let disposed = false;
    playbackSchedulerRef.current = new DeterministicCommandScheduler({
      clock: {
        nowMs: () => performance.now(),
        setTimeout: (callback, delayMs) => window.setTimeout(callback, delayMs),
        clearTimeout: (timerId) => window.clearTimeout(timerId),
      },
      lateConfig: {
        policy: "apply-immediate",
        thresholdMs: LATE_COMMAND_THRESHOLD_MS,
        rescheduleDelayMs: LATE_RESCHEDULE_DELAY_MS,
      },
      maxAppliedEntries: MAX_APPLIED_COMMAND_IDS,
    });
    const ctx = new AudioContext({ latencyHint: "interactive" });
    const playback = new LabPlaybackController(ctx);
    setBackendLoadMessage("Initializing audio graph...");
    void playback
      .initialize()
      .then(() => {
        if (
          disposed ||
          generation !== playbackInitGenerationRef.current ||
          ctx.state === "closed"
        ) {
          return;
        }
        playbackControllerRef.current = playback;
        setPlaybackReady(false);
        setPlaybackError(null);
        setBackendLoadMessage("Audio ready. Join a room and load/select the session song.");
        appendLog("local playback initialized (Signalsmith active: tempo and key are decoupled)");
      })
      .catch((error: unknown) => {
        if (disposed || generation !== playbackInitGenerationRef.current) {
          return;
        }
        const message = error instanceof Error ? error.message : "local playback init failed";
        setPlaybackReady(false);
        setPlaybackError(message);
        setBackendLoadMessage(`Audio init failed: ${message}`);
        appendLog(`local playback init failed: ${message}`);
      });
    return () => {
      disposed = true;
      playbackSchedulerRef.current?.cancelAll();
      playbackSchedulerRef.current = null;
      playback.dispose();
      if (playbackControllerRef.current === playback) {
        playbackControllerRef.current = null;
      }
      localPlaybackClientIdRef.current = null;
      setPlaybackReady(false);
      setStemMixerRows([]);
      setStemDownloadRows([]);
    };
  }, [appendLog]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      refreshStemMixerRows(true);
    }, 120);
    return () => {
      window.clearInterval(intervalId);
    };
  }, [refreshStemMixerRows]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      const playback = playbackControllerRef.current;
      if (!playback) {
        return;
      }
      try {
        const next = playback.snapshot();
        setLocalSnapshot(next);
        if (!isScrubbing) {
          setScrubPositionSec(next.positionSec);
        }
        if (!isAdjustingTempo) {
          setTempoRate(next.playbackRate);
        }
        if (!isAdjustingKey) {
          setKeySemitones(next.pitchSemitones);
        }
      } catch {
        // Ignore snapshots before player init.
      }
    }, 120);
    return () => {
      window.clearInterval(intervalId);
    };
  }, [isAdjustingKey, isAdjustingTempo, isScrubbing]);

  const setStemVolume = useCallback(
    (role: StemRole, nextVolume: number) => {
      const playback = playbackControllerRef.current;
      if (!playback) {
        return;
      }
      const clamped = Math.max(0, Math.min(1, nextVolume));
      playback.setStemSettings(role, { volume: clamped });
      refreshStemMixerRows(false);
    },
    [refreshStemMixerRows],
  );

  const toggleStemMuted = useCallback(
    (role: StemRole) => {
      const playback = playbackControllerRef.current;
      if (!playback) {
        return;
      }
      const current = playback.getStemSettings(role);
      playback.setStemSettings(role, { muted: !current?.muted });
      refreshStemMixerRows(false);
    },
    [refreshStemMixerRows],
  );

  const toggleStemSolo = useCallback(
    (role: StemRole) => {
      const playback = playbackControllerRef.current;
      if (!playback) {
        return;
      }
      const current = playback.getStemSettings(role);
      playback.setStemSettings(role, { solo: !current?.solo });
      refreshStemMixerRows(false);
    },
    [refreshStemMixerRows],
  );

  const signIn = useCallback(async () => {
    if (!cognitoAuth) {
      appendLog("Cognito is not configured in env vars");
      return;
    }
    try {
      appendLog("redirecting to Cognito sign-in...");
      await cognitoAuth.startLogin();
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      appendLog(`failed to start Cognito sign-in: ${message}`);
    }
  }, [appendLog, cognitoAuth]);

  const signOut = useCallback(() => {
    if (!cognitoAuth) {
      return;
    }
    cognitoAuth.signOut();
    setIsAuthenticated(false);
    setAuthError(null);
    appendLog("cognito session cleared and Hosted UI logout started");
  }, [appendLog, cognitoAuth]);

  const generateNewRoom = useCallback(() => {
    const nextRoom = `room-${randomIdSegment(8)}`.toLowerCase();
    setRoomName(nextRoom);
    setRoomProvisioned(false);
    setHasAutoReconnectAttempted(false);
    appendLog(`generated new room name: ${nextRoom}`);
  }, [appendLog]);

  const createRoomLink = useCallback(async () => {
    if (!cognitoAuth) {
      appendLog("sign-in configuration missing; cannot create a room link");
      return false;
    }
    const authIdToken = await cognitoAuth.getIdToken();
    if (!authIdToken) {
      appendLog("sign in is required to create a room link");
      try {
        await cognitoAuth.startLogin();
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        appendLog(`failed to start Cognito sign-in: ${message}`);
      }
      return false;
    }
    const apiBase = apiBaseUrl.trim().replace(/\/$/, "");
    setIsProvisioningRoom(true);
    try {
      const createLinkResponse = await fetch(
        `${apiBase}/v2/band-sync/livekit/session/create-link`,
        {
          method: "POST",
          headers: {
            "content-type": "application/json",
            authorization: `Bearer ${authIdToken}`,
          },
          body: JSON.stringify({
            sessionId: roomName.trim(),
            participantId: sessionParticipantId,
            maxParticipants: 50,
          }),
        },
      );
      let response = createLinkResponse;
      if (createLinkResponse.status === 404) {
        appendLog(
          "create-link endpoint missing; falling back to legacy /session endpoint for compatibility",
        );
        response = await fetch(`${apiBase}/v2/band-sync/livekit/session`, {
          method: "POST",
          headers: {
            "content-type": "application/json",
            authorization: `Bearer ${authIdToken}`,
          },
          body: JSON.stringify({
            sessionId: roomName.trim(),
            participantId: sessionParticipantId,
            role: "host",
            maxParticipants: 50,
          }),
        });
      }
      if (!response.ok) {
        const body = await response.text().catch(() => "");
        throw new Error(
          `create room link failed status=${response.status}${body ? ` body=${body}` : ""}`,
        );
      }
      const createdRaw: unknown = await response.json().catch((): unknown => ({}));
      const created =
        typeof createdRaw === "object" && createdRaw !== null
          ? (createdRaw as Record<string, unknown>)
          : {};
      const createdSessionId =
        typeof created.sessionId === "string" && created.sessionId.trim().length > 0
          ? created.sessionId.trim()
          : roomName.trim();
      const joinProbe = await fetch(`${apiBase}/v2/band-sync/livekit/session/join-link`, {
        method: "POST",
        headers: {
          "content-type": "application/json",
        },
        body: JSON.stringify({
          sessionId: createdSessionId,
          participantId: sessionParticipantId,
        }),
      });
      if (!joinProbe.ok) {
        const body = await joinProbe.text().catch(() => "");
        throw new Error(
          `room link created but join failed status=${joinProbe.status}${body ? ` body=${body}` : ""}`,
        );
      }
      setRoomName(createdSessionId);
      setRoomProvisioned(true);
      setHasAutoReconnectAttempted(false);
      appendLog(`created room link for ${createdSessionId}`);
      return true;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      appendLog(`failed to create room link: ${message}`);
      return false;
    } finally {
      setIsProvisioningRoom(false);
    }
  }, [apiBaseUrl, appendLog, cognitoAuth, roomName, sessionParticipantId]);

  const copyInviteLink = useCallback(async () => {
    try {
      if (typeof window === "undefined") {
        appendLog("unable to copy invite link from this browser");
        return;
      }
      if (!roomProvisioned && isAuthenticated) {
        const created = await createRoomLink();
        if (!created) {
          return;
        }
      }
      const next = new URL(resolveShareOrigin() + window.location.pathname);
      const normalizedRoom = roomName.trim() || "sync-visual-demo";
      next.searchParams.set("room", normalizedRoom);
      const normalizedApiBase = normalizeApiBaseUrl(apiBaseUrl);
      if (normalizedApiBase !== DEFAULT_BAND_SYNC_V2_API_BASE_URL) {
        next.searchParams.set("apiBase", normalizedApiBase);
      }
      const inviteToCopy = next.toString();
      setLastInviteCopyUrl(inviteToCopy);
      setLastInviteCopyStatus("Preparing copy...");
      if (!roomProvisioned && !isAuthenticated) {
        setLastInviteCopyStatus(
          "Copied current URL. Sign in + Create room link to mint a new persistent invite.",
        );
        appendLog(
          `copied current room URL without provisioning: ${inviteToCopy} (sign in to create a new room link)`,
        );
      }
      if ("clipboard" in navigator) {
        const { clipboard } = navigator;
        await clipboard.writeText(inviteToCopy);
        if (roomProvisioned || isAuthenticated) {
          setLastInviteCopyStatus("Copied to clipboard.");
        }
        appendLog(`copied room invite link to clipboard: ${inviteToCopy}`);
        return;
      }
      // Fallback for restricted clipboard contexts: still expose the URL in UI/log.
      setLastInviteCopyStatus("Clipboard unavailable. Copy the URL shown below.");
      appendLog(`clipboard unavailable; copy invite link manually: ${inviteToCopy}`);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      setLastInviteCopyStatus("Copy failed. Use the URL shown below.");
      appendLog(`failed to copy invite link: ${message}`);
    }
  }, [apiBaseUrl, appendLog, createRoomLink, isAuthenticated, roomProvisioned, roomName]);

  const pushVisualEvent = useCallback((event: VisualEvent) => {
    const events = eventsRef.current;
    events.push(event);
    if (events.length > MAX_VISUAL_EVENTS) {
      events.splice(0, events.length - MAX_VISUAL_EVENTS);
    }
  }, []);

  const updateServerClockOffsetSample = useCallback(
    (serverEpochMs: number, requestStartedAtMs: number, responseReceivedAtMs: number) => {
      const rttMs = Math.max(0, responseReceivedAtMs - requestStartedAtMs);
      if (!Number.isFinite(serverEpochMs) || rttMs > 2500) {
        return;
      }
      const midpointLocalMs = requestStartedAtMs + rttMs / 2;
      const sampleLocalMinusServerMs = midpointLocalMs - serverEpochMs;
      if (
        !Number.isFinite(sampleLocalMinusServerMs) ||
        Math.abs(sampleLocalMinusServerMs) > 10_000
      ) {
        return;
      }
      const alpha = rttMs <= 300 ? 0.2 : 0.12;
      const current = localMinusServerClockOffsetMsRef.current;
      const next = current + (sampleLocalMinusServerMs - current) * alpha;
      localMinusServerClockOffsetMsRef.current = next;
      setLocalMinusServerClockOffsetMs(next);
    },
    [],
  );

  const scheduleExecutionPulse = useCallback(
    (lane: number, op: DemoCommandOp, executeAtMs: number, marker?: string) => {
      const delay = Math.max(0, executeAtMs - Date.now());
      const timerId = window.setTimeout(() => {
        executionPulseTimersRef.current.delete(timerId);
        pushVisualEvent({
          atMs: Date.now(),
          lane,
          type: executeEventType(op),
          ...(marker ? { marker } : {}),
        });
      }, delay);
      executionPulseTimersRef.current.add(timerId);
    },
    [pushVisualEvent],
  );

  const updateClientConnected = useCallback((id: string, connected: boolean) => {
    const now = Date.now();
    setClients((prev) =>
      prev.map((client) =>
        client.id === id
          ? {
              ...client,
              connected,
              status: connected ? "connected" : "disconnected",
              lastSeenAtEpochMs: now,
            }
          : client,
      ),
    );
  }, []);

  const updateClientMeta = useCallback(
    (
      id: string,
      patch: Partial<
        Pick<
          ClientRow,
          "status" | "joinElapsedMs" | "errorMessage" | "connected" | "deviceType" | "latencyCompMs"
        >
      >,
    ) => {
      const now = Date.now();
      setClients((prev) =>
        prev.map((client) =>
          client.id === id ? { ...client, ...patch, lastSeenAtEpochMs: now } : client,
        ),
      );
    },
    [],
  );

  const ensureParticipantRow = useCallback(
    (participantId: string, connected: boolean, patch?: Partial<ClientRow>) => {
      setClients((prev) => {
        const existing = prev.find((client) => client.id === participantId);
        if (existing) {
          return prev.map((client) =>
            client.id === participantId
              ? {
                  ...client,
                  connected,
                  status: connected ? "connected" : "disconnected",
                  lastSeenAtEpochMs: Date.now(),
                  ...(patch ?? {}),
                }
              : client,
          );
        }
        const usedLanes = new Set(prev.map((client) => client.lane));
        let nextLane = 0;
        while (usedLanes.has(nextLane)) {
          nextLane += 1;
        }
        participantLaneByIdRef.current.set(participantId, nextLane);
        return [
          ...prev,
          {
            id: participantId,
            lane: nextLane,
            countryCode: "ZZ",
            deviceType: "Unknown device",
            connected,
            status: connected ? "connected" : "disconnected",
            isLocal: participantId === sessionParticipantId,
            lastSeenAtEpochMs: Date.now(),
            ...(patch ?? {}),
          },
        ];
      });
    },
    [sessionParticipantId],
  );

  const describeCommandBanner = useCallback((parsed: BenchPayload): string => {
    switch (parsed.op) {
      case "transport.seek":
        return `${parsed.issuerId} changed position to ${(parsed.toPositionSec ?? 0).toFixed(1)}s`;
      case "transport.setRate":
        return `${parsed.issuerId} changed tempo to ${(parsed.rate ?? 1).toFixed(2)}x`;
      case "transport.setPitch":
        return `${parsed.issuerId} changed key by ${parsed.pitchSemitones ?? 0} st`;
      case "transport.setSong":
        return `${parsed.issuerId} changed song to ${parsed.songTitle ?? parsed.songYoutubeId ?? "new song"}`;
      case "transport.setLead":
        return `${parsed.issuerId} changed scheduled lead to ${Math.round(parsed.leadMs ?? 0)}ms`;
      case "transport.setLatencyComp":
        return `${parsed.issuerId} turned latency comp ${parsed.latencyCompEnabled === false ? "OFF" : "ON"}`;
      case "transport.play":
        return `${parsed.issuerId} pressed play`;
      case "transport.pause":
        return `${parsed.issuerId} pressed pause`;
      default:
        return `${parsed.issuerId} issued a transport command`;
    }
  }, []);

  const registerCommandArbitration = useCallback((parsed: BenchPayload) => {
    setCommandArbitrationRows((prev) => applyLwwArbitrationRows(prev, parsed, Date.now()));
  }, []);

  const registerReceive = useCallback(
    (clientId: string, lane: number, parsed: BenchPayload) => {
      const previousSeq = lastSeqByIssuerIdRef.current.get(parsed.issuerId);
      if (previousSeq !== undefined && parsed.seq <= previousSeq) {
        appendLog(
          `ignored stale ${parsed.op} from ${parsed.issuerId} seq=${parsed.seq} <= ${previousSeq}`,
        );
        return;
      }
      lastSeqByIssuerIdRef.current.set(parsed.issuerId, parsed.seq);
      syncSeqFromAuthoritative(parsed.seq);
      const issuerLane = participantLaneByIdRef.current.get(parsed.issuerId) ?? lane;
      if (
        parsed.op === "transport.play" ||
        parsed.op === "transport.pause" ||
        parsed.op === "transport.seek" ||
        parsed.op === "transport.setRate" ||
        parsed.op === "transport.setPitch" ||
        parsed.op === "transport.setLead" ||
        parsed.op === "transport.setLatencyComp"
      ) {
        transportAuthorityParticipantIdRef.current = parsed.issuerId;
      }
      const deliveryLatencyMs = Math.max(0, Date.now() - parsed.issuedAtMs);
      const deliverySamples = deliveryLatencySamplesRef.current;
      deliverySamples.push(deliveryLatencyMs);
      if (deliverySamples.length > MAX_TELEMETRY_SAMPLES) {
        deliverySamples.splice(0, deliverySamples.length - MAX_TELEMETRY_SAMPLES);
      }
      setParticipantTelemetry((prev) => {
        const existing = prev[clientId] ?? {
          receives: 0,
          applies: 0,
          lastDeliveryLatencyMs: 0,
          lastApplyOffsetMs: 0,
        };
        return {
          ...prev,
          [clientId]: {
            ...existing,
            receives: existing.receives + 1,
            lastDeliveryLatencyMs: deliveryLatencyMs,
          },
        };
      });
      const marker = getScopeShiftMarker(parsed.op, {
        ...(parsed.rate !== undefined ? { rate: parsed.rate } : {}),
        ...(parsed.pitchSemitones !== undefined ? { pitchSemitones: parsed.pitchSemitones } : {}),
      });
      pushVisualEvent({
        atMs: Date.now(),
        lane: issuerLane,
        type: receiveEventType(parsed.op),
        ...(marker ? { marker } : {}),
      });
      const ackKey = `${parsed.issuerId}:${parsed.seq}`;
      const ackSet = ackMapRef.current.get(ackKey) ?? new Set<string>();
      ackSet.add(clientId);
      ackMapRef.current.set(ackKey, ackSet);
      while (ackMapRef.current.size > MAX_ACK_ENTRIES) {
        const oldestKey = ackMapRef.current.keys().next().value;
        if (oldestKey === undefined) {
          break;
        }
        ackMapRef.current.delete(oldestKey);
      }

      const expectedRecipients = Math.max(1, connectedParticipantCountRef.current);
      if (ackSet.size >= expectedRecipients) {
        pushVisualEvent({ atMs: Date.now(), lane: 0, type: "all-received" });
        appendLog(
          `[seq ${parsed.seq} issuer=${parsed.issuerId}] all clients received ${parsed.op}`,
        );
        ackMapRef.current.delete(ackKey);
      }

      if (parsed.issuerId !== sessionParticipantId) {
        ensureParticipantRow(parsed.issuerId, true, {
          countryCode:
            typeof parsed.issuerCountry === "string" && parsed.issuerCountry.trim().length > 0
              ? parsed.issuerCountry.trim().toUpperCase().slice(0, 2)
              : "ZZ",
        });
        setCommandBanner({
          message: `${describeCommandBanner(parsed)} (seq ${parsed.seq})`,
          tone: "warning",
        });
      }
      pushCommandToast(
        `received ${parsed.op} by ${parsed.issuerId} (seq ${parsed.seq})`,
        parsed.issuerId === sessionParticipantId ? "info" : "warning",
      );
      registerCommandArbitration(parsed);
      if (parsed.op === "transport.setSong" && parsed.songYoutubeId) {
        const songYoutubeId = parsed.songYoutubeId;
        const descriptorFromCommand: SessionSongDescriptor = {
          youtubeId: songYoutubeId,
          title: parsed.songTitle ?? "Session Song",
          ...(parsed.songSmamsUrl ? { smamsUrl: parsed.songSmamsUrl } : {}),
          ...(parsed.songStemUrlsByRole ? { stemUrlsByRole: parsed.songStemUrlsByRole } : {}),
        };
        setCurrentSongYoutubeId(songYoutubeId);
        setCurrentSongDescriptor(descriptorFromCommand);
        setPlaybackReady(false);
        setRoomSongLoadStatusByParticipant((prev) => {
          const now = Date.now();
          const next: Record<string, RoomSongLoadStatus> = { ...prev };
          for (const client of clients) {
            if (!client.connected) {
              continue;
            }
            const existing = prev[client.id];
            if (existing?.songYoutubeId === songYoutubeId && existing.phase === "ready") {
              continue;
            }
            next[client.id] = {
              participantId: client.id,
              songYoutubeId,
              phase: "queued",
              percentage: 0,
              updatedAtMs: now,
            };
          }
          return next;
        });
      }

      scheduleExecutionPulse(
        issuerLane,
        parsed.op,
        resolveAuthoritativeExecuteAtMs(parsed),
        marker,
      );
      capturePlaybackSnapshot("receive", parsed, clientId);
      scheduleAudioApply(parsed, clientId);
    },
    [
      appendLog,
      capturePlaybackSnapshot,
      describeCommandBanner,
      ensureParticipantRow,
      clients,
      scheduleAudioApply,
      scheduleExecutionPulse,
      sessionParticipantId,
      pushCommandToast,
      registerCommandArbitration,
      pushVisualEvent,
      syncSeqFromAuthoritative,
    ],
  );

  const onEvent = useCallback(
    (clientId: string, lane: number, event: LivekitTransportEvent) => {
      if (event.kind === "connected") {
        const batchStartedAt = connectBatchStartedAtRef.current;
        const joinElapsedMs =
          batchStartedAt !== null ? performance.now() - batchStartedAt : undefined;
        if (joinElapsedMs !== undefined) {
          joinElapsedByClientRef.current.set(clientId, joinElapsedMs);
        }
        updateClientMeta(clientId, {
          connected: true,
          status: "connected",
          joinElapsedMs,
          errorMessage: undefined,
        });
        pushVisualEvent({ atMs: Date.now(), lane, type: "join" });
        const runtime = runtimeClientsRef.current.find((client) => client.id === clientId);
        const countryCode = runtime?.countryCode ?? "ZZ";
        appendLog(
          `[${clientId}] joined ${countryCodeToFlagEmoji(countryCode)} ${countryCode}${
            joinElapsedMs !== undefined ? ` (+${Math.round(joinElapsedMs)}ms)` : ""
          }`,
        );
        if (clientId === sessionParticipantId) {
          const localRuntime = runtimeClientsRef.current.find((client) => client.id === clientId);
          const localDeviceType = detectDeviceType();
          const localLatencyCompMs = readLocalLatencyCompMs(playbackControllerRef.current);
          updateClientMeta(clientId, {
            deviceType: localDeviceType,
            ...(localLatencyCompMs !== undefined ? { latencyCompMs: localLatencyCompMs } : {}),
          });
          localRuntime?.transport?.publish({
            op: "transport.deviceInfo",
            commandId: `${clientId}:device-info:${Date.now()}`,
            payload: {
              participantId: clientId,
              deviceType: localDeviceType,
              ...(localLatencyCompMs !== undefined ? { latencyCompMs: localLatencyCompMs } : {}),
              sampledAtMs: Date.now(),
            },
          });
        }
        return;
      }
      if (event.kind === "disconnected") {
        updateClientConnected(clientId, false);
        appendLog(`[${clientId}] disconnected (${event.reason ?? "no reason"})`);
        return;
      }
      if (event.kind === "participant_connected") {
        ensureParticipantRow(event.participantId, true);
        appendLog(`[presence] ${event.participantId} connected`);
        return;
      }
      if (event.kind === "participants_snapshot") {
        for (const participantId of event.participantIds) {
          ensureParticipantRow(participantId, true);
        }
        appendLog(
          `[presence] initial snapshot received (${event.participantIds.length} participants)`,
        );
        return;
      }
      if (event.kind === "participant_disconnected") {
        updateClientConnected(event.participantId, false);
        appendLog(`[presence] ${event.participantId} disconnected`);
        return;
      }
      if (event.command.op === "transport.syncPulse") {
        handleSyncPulse(event.command.payload, clientId, lane, event.command.commandId);
        return;
      }
      if (event.command.op === "transport.songLoadProgress") {
        const payload = event.command.payload;
        if (payload && typeof payload === "object") {
          const raw = payload;
          const participantId =
            typeof raw.participantId === "string" ? raw.participantId.trim() : "";
          const songYoutubeId =
            typeof raw.songYoutubeId === "string" ? raw.songYoutubeId.trim() : "";
          const phaseRaw = typeof raw.phase === "string" ? raw.phase.trim() : "";
          const updatedAtMsRaw = typeof raw.updatedAtMs === "number" ? raw.updatedAtMs : Date.now();
          const percentageRaw = typeof raw.percentage === "number" ? raw.percentage : 0;
          const errorMessage =
            typeof raw.errorMessage === "string" && raw.errorMessage.trim().length > 0
              ? raw.errorMessage.trim()
              : undefined;
          const phase: RoomLoadPhase =
            phaseRaw === "queued" ||
            phaseRaw === "downloading" ||
            phaseRaw === "decoding" ||
            phaseRaw === "ready" ||
            phaseRaw === "error" ||
            phaseRaw === "timeout"
              ? phaseRaw
              : "queued";
          if (participantId && songYoutubeId) {
            ensureParticipantRow(participantId, true);
            const clampedPercentage = clampPercent(percentageRaw);
            setRoomSongLoadStatusByParticipant((prev) => ({
              ...prev,
              [participantId]: {
                participantId,
                songYoutubeId,
                phase,
                percentage: clampedPercentage,
                updatedAtMs: Number.isFinite(updatedAtMsRaw) ? updatedAtMsRaw : Date.now(),
                ...(errorMessage ? { errorMessage } : {}),
              },
            }));
            pushSongLoadScopePulse(participantId, phase, clampedPercentage);
          }
        }
        return;
      }
      if (event.command.op === "transport.deviceInfo") {
        const payload = event.command.payload;
        if (payload && typeof payload === "object") {
          const raw = payload;
          const participantId =
            typeof raw.participantId === "string" ? raw.participantId.trim() : "";
          const deviceType = typeof raw.deviceType === "string" ? raw.deviceType.trim() : "";
          const latencyCompMsRaw =
            typeof raw.latencyCompMs === "number" && Number.isFinite(raw.latencyCompMs)
              ? Math.max(0, Math.round(raw.latencyCompMs * 10) / 10)
              : undefined;
          if (participantId.length > 0) {
            ensureParticipantRow(participantId, true, {
              ...(deviceType.length > 0 ? { deviceType } : {}),
              ...(latencyCompMsRaw !== undefined ? { latencyCompMs: latencyCompMsRaw } : {}),
            });
          }
        }
        return;
      }
      if (event.command.op === "transport.skewReport") {
        const payload = event.command.payload;
        if (payload && typeof payload === "object") {
          const raw = payload;
          const participantId =
            typeof raw.participantId === "string" ? raw.participantId.trim() : "";
          const authorityParticipantId =
            typeof raw.authorityParticipantId === "string" ? raw.authorityParticipantId.trim() : "";
          const skewMs = typeof raw.skewMs === "number" ? raw.skewMs : NaN;
          const sampledAtMs = typeof raw.sampledAtMs === "number" ? raw.sampledAtMs : Date.now();
          if (
            participantId.length > 0 &&
            authorityParticipantId.length > 0 &&
            Number.isFinite(skewMs)
          ) {
            ensureParticipantRow(participantId, true);
            recordSkewSample(participantId, skewMs, sampledAtMs, authorityParticipantId);
          }
        }
        return;
      }
      const parsed = parseBenchPayload(event.command);
      if (!parsed) {
        return;
      }
      registerReceive(clientId, lane, parsed);
    },
    [
      appendLog,
      ensureParticipantRow,
      handleSyncPulse,
      pushSongLoadScopePulse,
      pushVisualEvent,
      recordSkewSample,
      registerReceive,
      sessionParticipantId,
      updateClientConnected,
      updateClientMeta,
    ],
  );

  const disconnectAll = useCallback(() => {
    connectAttemptIdRef.current += 1;
    if (connectAbortControllerRef.current) {
      connectAbortControllerRef.current.abort();
      connectAbortControllerRef.current = null;
    }
    if (activeSongLoadAbortControllerRef.current) {
      activeSongLoadAbortControllerRef.current.abort();
      activeSongLoadAbortControllerRef.current = null;
    }
    setIsConnecting(false);
    const apiBase = apiBaseUrl.trim().replace(/\/$/, "");
    if (apiBase && roomName.trim() && sessionParticipantId.trim()) {
      void fetch(`${apiBase}/v2/band-sync/livekit/session/leave-link`, {
        method: "POST",
        headers: {
          "content-type": "application/json",
        },
        body: JSON.stringify({
          sessionId: roomName.trim(),
          participantId: sessionParticipantId.trim(),
        }),
      }).catch(() => undefined);
    }
    if (randomTimerRef.current !== null) {
      window.clearInterval(randomTimerRef.current);
      randomTimerRef.current = null;
    }
    if (lateJoinCorrectionTimerRef.current !== null) {
      window.clearTimeout(lateJoinCorrectionTimerRef.current);
      lateJoinCorrectionTimerRef.current = null;
    }
    const playback = playbackControllerRef.current;
    if (playback) {
      try {
        playback.applyCommand({
          commandId: `local-leave:${Date.now()}`,
          seq: 0,
          op: "transport.pause",
          issuedAtMs: Date.now(),
          executeAtMs: Date.now(),
          issuerId: sessionParticipantId,
        });
      } catch {
        // Ignore local pause failure during disconnect teardown.
      }
    }
    setRandomCycleOn(false);
    localPlaybackClientIdRef.current = null;
    playbackSchedulerRef.current?.cancelAll();
    playbackSchedulerRef.current?.resetAppliedCache();
    for (const timerId of executionPulseTimersRef.current) {
      window.clearTimeout(timerId);
    }
    executionPulseTimersRef.current.clear();
    deliveryLatencySamplesRef.current = [];
    applyOffsetSamplesRef.current = [];
    setParticipantTelemetry({});
    setRoomSongLoadStatusByParticipant({});
    setCommandArbitrationRows([]);
    setCommandToasts([]);
    const current = runtimeClientsRef.current;
    runtimeClientsRef.current = [];
    joinElapsedByClientRef.current.clear();
    participantLaneByIdRef.current.clear();
    for (const runtime of current) {
      runtime.unsubscribe?.();
      runtime.transport?.disconnect("user_requested");
    }
    setClients([]);
    setLocalSessionRole(null);
    setLastCatchUpSummary(null);
    ackMapRef.current.clear();
    lastSeqByIssuerIdRef.current.clear();
    lastSyncNudgeAtRef.current = 0;
    lastSyncNudgeLogAtRef.current = 0;
    lastSongLoadScopePulseByParticipantRef.current.clear();
    appendLog("all clients disconnected");
  }, [apiBaseUrl, appendLog, roomName, sessionParticipantId]);

  useEffect(() => {
    return () => {
      disconnectAll();
    };
  }, [disconnectAll]);

  const connectClients = useCallback(async () => {
    if (isConnecting) {
      return;
    }
    disconnectAll();
    setIsConnecting(true);
    const connectAttemptId = connectAttemptIdRef.current + 1;
    connectAttemptIdRef.current = connectAttemptId;
    const connectAbortController = new AbortController();
    connectAbortControllerRef.current = connectAbortController;
    const throwIfJoinCancelled = () => {
      if (
        connectAttemptIdRef.current !== connectAttemptId ||
        connectAbortController.signal.aborted
      ) {
        throw new Error("JOIN_CANCELLED");
      }
    };
    setLastConnectError(null);
    appendLog(`connecting participant ${sessionParticipantId}...`);
    appendLog(`connect using API base: ${apiBaseUrl.trim()}`);
    if (apiBaseUrl.trim() === "/") {
      setApiBaseUrl(DEFAULT_BAND_SYNC_V2_API_BASE_URL);
      appendLog(`livekit mode requires API URL; switched to ${DEFAULT_BAND_SYNC_V2_API_BASE_URL}`);
      setIsConnecting(false);
      return;
    }
    const authIdToken = cognitoAuth ? await cognitoAuth.getIdToken() : undefined;
    setIsAuthenticated(Boolean(authIdToken));
    const normalizedCountryRaw = participantCountryCode.trim().toUpperCase().slice(0, 2);
    const normalizedCountry = normalizedCountryRaw.length > 0 ? normalizedCountryRaw : "ZZ";
    const localRow: ClientRow = {
      id: sessionParticipantId,
      lane: 0,
      countryCode: normalizedCountry,
      deviceType: detectDeviceType(),
      connected: false,
      status: "connecting",
      isLocal: true,
      lastSeenAtEpochMs: Date.now(),
    };
    participantLaneByIdRef.current = new Map([[sessionParticipantId, 0]]);
    setClients([localRow]);
    setLastJoinSummary(null);
    connectBatchStartedAtRef.current = performance.now();

    try {
      const runWithRetry = async <T,>(
        label: string,
        fn: () => Promise<T>,
        attempts = 2,
      ): Promise<T> => {
        let lastError: unknown = null;
        for (let attempt = 1; attempt <= attempts; attempt += 1) {
          throwIfJoinCancelled();
          try {
            if (attempt > 1) {
              appendLog(`${label}: retry ${attempt}/${attempts}`);
            }
            const result = await fn();
            throwIfJoinCancelled();
            return result;
          } catch (error) {
            if (error instanceof Error && error.message === "JOIN_CANCELLED") {
              throw error;
            }
            if (error instanceof DOMException && error.name === "AbortError") {
              throw new Error("JOIN_CANCELLED");
            }
            lastError = error;
          }
        }
        throw lastError;
      };

      appendLog("joining room as a real participant...");
      const apiBase = apiBaseUrl.trim().replace(/\/$/, "");
      const ensureSessionMembership = async (
        participantId: string,
      ): Promise<{
        role: "follower";
        currentSongYoutubeId?: string;
        currentSongTitle?: string;
        currentSongDescriptor?: SessionSongDescriptor;
        transportSnapshot?: SessionTransportSnapshot;
      }> => {
        const joinResponse = await fetch(`${apiBase}/v2/band-sync/livekit/session/join-link`, {
          method: "POST",
          headers: {
            "content-type": "application/json",
          },
          signal: connectAbortController.signal,
          body: JSON.stringify({
            sessionId: roomName,
            participantId,
          }),
        });
        if (!joinResponse.ok) {
          const body = await joinResponse.text().catch(() => "");
          if (joinResponse.status === 404) {
            throw new Error("room not found; ask a signed-in creator for a valid room link");
          }
          if (joinResponse.status === 410) {
            throw new Error("room expired; ask a signed-in creator for a fresh room link");
          }
          throw new Error(
            `session bootstrap failed status=${joinResponse.status}${body ? ` body=${body}` : ""}`,
          );
        }
        const body: unknown = await joinResponse.json().catch((): unknown => ({}));
        const joinedBootstrap = parseSessionJoinBootstrap(body, "follower");
        return {
          role: "follower",
          ...(joinedBootstrap.currentSongYoutubeId
            ? { currentSongYoutubeId: joinedBootstrap.currentSongYoutubeId }
            : {}),
          ...(joinedBootstrap.currentSongTitle
            ? { currentSongTitle: joinedBootstrap.currentSongTitle }
            : {}),
          ...(joinedBootstrap.currentSongDescriptor
            ? { currentSongDescriptor: joinedBootstrap.currentSongDescriptor }
            : {}),
          ...(joinedBootstrap.transportSnapshot
            ? { transportSnapshot: joinedBootstrap.transportSnapshot }
            : {}),
        };
      };

      const membership = await runWithRetry(
        "session membership bootstrap",
        () => ensureSessionMembership(sessionParticipantId),
        2,
      );
      throwIfJoinCancelled();
      const minted: TokenBootstrapResponse = await runWithRetry(
        "mint LiveKit token",
        () =>
          tokenService.mintToken({
            roomName,
            participantId: sessionParticipantId,
            role: membership.role,
          }),
        2,
      );
      throwIfJoinCancelled();
      const lkUrl =
        minted.livekitUrl ??
        (import.meta.env.VITE_BAND_SYNC_V2_LIVEKIT_URL_OVERRIDE as string | undefined) ??
        "";
      if (!lkUrl) {
        throw new Error("LiveKit URL unavailable from mint response and env override");
      }
      const transport = createTransportAdapter({
        kind: "livekit",
        roomName,
        participantId: sessionParticipantId,
        token: minted.token,
        livekitUrl: lkUrl,
      });
      const unsubscribe = transport.onEvent((event) => onEvent(sessionParticipantId, 0, event));
      await runWithRetry("connect transport", () => transport.connect(), 2);
      throwIfJoinCancelled();

      const connectedRuntimeClient: RuntimeClient = {
        id: sessionParticipantId,
        lane: 0,
        countryCode: normalizedCountry,
        transport,
        unsubscribe,
      };
      runtimeClientsRef.current = [connectedRuntimeClient];
      localPlaybackClientIdRef.current = connectedRuntimeClient.id;
      setLocalSessionRole(membership.role);
      setRoomProvisioned(true);
      const totalMs = performance.now() - connectBatchStartedAtRef.current;
      const joinTimes = [joinElapsedByClientRef.current.get(sessionParticipantId) ?? totalMs];
      const joinSummary = summarizeJoinMs(joinTimes);
      setLastJoinSummary({
        totalMs,
        connectedCount: 1,
        failedCount: 0,
        p95Ms: joinSummary.p95,
      });
      appendLog(
        `connected ${sessionParticipantId} (${membership.role}) in ${Math.round(totalMs)}ms, p95 ${Math.round(joinSummary.p95)}ms`,
      );
      await loadMemberProfileFromBackend();
      throwIfJoinCancelled();
      const joinSongDescriptor =
        membership.currentSongDescriptor ??
        (membership.currentSongYoutubeId
          ? {
              youtubeId: membership.currentSongYoutubeId,
              title: membership.currentSongTitle ?? "Session Song",
            }
          : undefined);
      const hasJoinStemUrls =
        joinSongDescriptor?.stemUrlsByRole !== undefined &&
        Object.values(joinSongDescriptor.stemUrlsByRole).some(
          (value) => typeof value === "string" && value.length > 0,
        );
      const hasCurrentStemUrls =
        currentSongDescriptor?.stemUrlsByRole !== undefined &&
        Object.values(currentSongDescriptor.stemUrlsByRole).some(
          (value) => typeof value === "string" && value.length > 0,
        );
      const shouldLoadJoinSong =
        joinSongDescriptor !== undefined &&
        joinSongDescriptor.youtubeId.length > 0 &&
        (!playbackReady ||
          joinSongDescriptor.youtubeId !== currentSongYoutubeId ||
          (hasJoinStemUrls && !hasCurrentStemUrls));
      if (shouldLoadJoinSong) {
        appendLog(`join sync: fetching stems for "${joinSongDescriptor.youtubeId}"`);
        await runWithRetry(
          "load session song on join",
          () => loadSongByDescriptor(joinSongDescriptor, "join"),
          2,
        );
        throwIfJoinCancelled();
      }
      if (membership.transportSnapshot) {
        const transportSnapshot = membership.transportSnapshot;
        syncSeqFromAuthoritative(transportSnapshot.seq);
        const catchUpResult = await runWithRetry(
          "apply transport snapshot on join",
          () => applyCatchUpTransportSnapshot(transportSnapshot),
          2,
        );
        if (lateJoinCorrectionTimerRef.current !== null) {
          window.clearTimeout(lateJoinCorrectionTimerRef.current);
          lateJoinCorrectionTimerRef.current = null;
        }
        appendLog(
          `scheduled joiner late-join correction in ${LATE_JOIN_SECOND_PASS_DELAY_MS}ms (threshold ${LATE_JOIN_CORRECTION_DRIFT_THRESHOLD_MS}ms)${
            catchUpResult
              ? ` after catch-up pos=${catchUpResult.projectedPositionSec.toFixed(3)}s`
              : ""
          }`,
        );
        lateJoinCorrectionTimerRef.current = window.setTimeout(() => {
          lateJoinCorrectionTimerRef.current = null;
          void applyJoinerSecondPassCorrection(transportSnapshot).catch((error: unknown) => {
            const message = error instanceof Error ? error.message : String(error);
            appendLog(`late-join correction failed: ${message}`);
          });
        }, LATE_JOIN_SECOND_PASS_DELAY_MS);
      }
    } catch (error) {
      if (error instanceof Error && error.message === "JOIN_CANCELLED") {
        appendLog("join cancelled");
        return;
      }
      const message = error instanceof Error ? error.message : "unknown error";
      setLastConnectError(message);
      setClients((prev) =>
        prev.map((client) =>
          client.status === "connecting"
            ? {
                ...client,
                status: "failed",
                connected: false,
                errorMessage: message,
              }
            : client,
        ),
      );
      setLastJoinSummary({
        totalMs: performance.now() - connectBatchStartedAtRef.current,
        connectedCount: runtimeClientsRef.current.length,
        failedCount: Math.max(0, 1 - runtimeClientsRef.current.length),
        p95Ms: 0,
      });
      appendLog(`connect failed: ${message}`);
    } finally {
      connectBatchStartedAtRef.current = null;
      if (connectAttemptIdRef.current === connectAttemptId) {
        setIsConnecting(false);
        connectAbortControllerRef.current = null;
      }
    }
  }, [
    apiBaseUrl,
    applyCatchUpTransportSnapshot,
    applyJoinerSecondPassCorrection,
    appendLog,
    cognitoAuth,
    currentSongDescriptor,
    currentSongYoutubeId,
    disconnectAll,
    isConnecting,
    loadMemberProfileFromBackend,
    loadSongByDescriptor,
    onEvent,
    playbackReady,
    participantCountryCode,
    roomName,
    sessionParticipantId,
    syncSeqFromAuthoritative,
    tokenService,
  ]);

  useEffect(() => {
    if (!autoReconnectEnabled || hasAutoReconnectAttempted) {
      return;
    }
    if (!isAuthReady || isConnecting || clients.length > 0) {
      return;
    }
    setHasAutoReconnectAttempted(true);
    appendLog("auto-reconnect enabled: attempting session join");
    void connectClients();
  }, [
    appendLog,
    autoReconnectEnabled,
    connectClients,
    clients.length,
    hasAutoReconnectAttempted,
    isAuthReady,
    isConnecting,
  ]);

  useEffect(() => {
    if (!localSessionRole || clients.length === 0) {
      return;
    }
    const apiBase = apiBaseUrl.trim().replace(/\/$/, "");
    if (!apiBase || !roomName.trim() || !sessionParticipantId.trim()) {
      return;
    }
    const sendHeartbeat = () => {
      const startedAtMs = Date.now();
      void fetch(`${apiBase}/v2/band-sync/livekit/heartbeat`, {
        method: "POST",
        headers: {
          "content-type": "application/json",
        },
        body: JSON.stringify({
          sessionId: roomName.trim(),
          participantId: sessionParticipantId.trim(),
        }),
      })
        .then(async (response) => {
          const receivedAtMs = Date.now();
          if (!response.ok) {
            return;
          }
          const body: unknown = await response.json().catch((): unknown => null);
          if (body === null || typeof body !== "object") {
            return;
          }
          const rawServerEpoch = (body as { heartbeatAtEpochMs?: unknown }).heartbeatAtEpochMs;
          if (typeof rawServerEpoch !== "number" || !Number.isFinite(rawServerEpoch)) {
            return;
          }
          updateServerClockOffsetSample(rawServerEpoch, startedAtMs, receivedAtMs);
        })
        .catch(() => undefined);
    };
    sendHeartbeat();
    const intervalId = window.setInterval(sendHeartbeat, 10_000);
    return () => {
      window.clearInterval(intervalId);
    };
  }, [
    apiBaseUrl,
    clients.length,
    localSessionRole,
    roomName,
    sessionParticipantId,
    updateServerClockOffsetSample,
  ]);

  useEffect(() => {
    if (!localSessionRole || clients.length === 0) {
      return;
    }
    const participantId = sessionParticipantId.trim();
    if (!participantId) {
      return;
    }
    const intervalId = window.setInterval(() => {
      if (transportAuthorityParticipantIdRef.current !== participantId) {
        return;
      }
      const playback = playbackControllerRef.current;
      if (!playback) {
        return;
      }
      let snapshot: PlayerSnapshot;
      try {
        snapshot = playback.snapshot();
      } catch {
        return;
      }
      const localRuntime = runtimeClientsRef.current.find((client) => client.id === participantId);
      if (!localRuntime?.transport) {
        return;
      }
      const sampledAtEpochMs = Date.now();
      localRuntime.transport.publish({
        op: "transport.syncPulse",
        commandId: `${participantId}:sync-pulse:${sampledAtEpochMs}`,
        payload: {
          participantId,
          sampledAtEpochMs,
          positionSec: snapshot.positionSec,
          playbackRate: snapshot.playbackRate,
          pitchSemitones: snapshot.pitchSemitones,
          state: snapshot.state,
        },
      });
    }, SYNC_PULSE_INTERVAL_MS);
    return () => {
      window.clearInterval(intervalId);
    };
  }, [clients.length, localSessionRole, sessionParticipantId]);

  useEffect(() => {
    if (!localSessionRole || clients.length === 0) {
      return;
    }
    const participantId = sessionParticipantId.trim();
    if (!participantId) {
      return;
    }
    const intervalId = window.setInterval(() => {
      const localRuntime = runtimeClientsRef.current.find((client) => client.id === participantId);
      if (!localRuntime?.transport) {
        return;
      }
      const localDeviceType = detectDeviceType();
      const localLatencyCompMs = readLocalLatencyCompMs(playbackControllerRef.current);
      updateClientMeta(participantId, {
        deviceType: localDeviceType,
        ...(localLatencyCompMs !== undefined ? { latencyCompMs: localLatencyCompMs } : {}),
      });
      localRuntime.transport.publish({
        op: "transport.deviceInfo",
        commandId: `${participantId}:device-info-live:${Date.now()}`,
        payload: {
          participantId,
          deviceType: localDeviceType,
          ...(localLatencyCompMs !== undefined ? { latencyCompMs: localLatencyCompMs } : {}),
          sampledAtMs: Date.now(),
        },
      });
    }, 2000);
    return () => {
      window.clearInterval(intervalId);
    };
  }, [clients.length, localSessionRole, sessionParticipantId, updateClientMeta]);

  useEffect(() => {
    if (!localSessionRole || clients.length === 0) {
      return;
    }
    const participantId = sessionParticipantId.trim();
    if (!participantId) {
      return;
    }
    const intervalId = window.setInterval(() => {
      const localRuntime = runtimeClientsRef.current.find((client) => client.id === participantId);
      if (!localRuntime?.transport) {
        return;
      }
      const authorityParticipantId = transportAuthorityParticipantIdRef.current ?? participantId;
      const sampledAtMs = Date.now();
      const skewMs = authorityParticipantId === participantId ? 0 : latestLocalSkewMsRef.current;
      recordSkewSample(participantId, skewMs, sampledAtMs, authorityParticipantId);
      localRuntime.transport.publish({
        op: "transport.skewReport",
        commandId: `${participantId}:skew-report:${sampledAtMs}`,
        payload: {
          participantId,
          authorityParticipantId,
          skewMs,
          sampledAtMs,
        },
      });
    }, SKEW_REPORT_INTERVAL_MS);
    return () => {
      window.clearInterval(intervalId);
    };
  }, [clients.length, localSessionRole, recordSkewSample, sessionParticipantId]);

  useEffect(() => {
    if (!localSessionRole || clients.length === 0) {
      return;
    }
    if (localSessionRole !== "follower") {
      return;
    }
    const participantId = sessionParticipantId.trim();
    if (!participantId) {
      return;
    }
    const intervalId = window.setInterval(() => {
      if (transportAuthorityParticipantIdRef.current !== participantId) {
        return;
      }
      const playback = playbackControllerRef.current;
      if (!playback) {
        return;
      }
      let snapshot: PlayerSnapshot;
      try {
        snapshot = playback.snapshot();
      } catch {
        return;
      }
      if (snapshot.state !== "playing") {
        return;
      }
      const now = Date.now();
      if (now - lastTransportPulseAtRef.current < 2000) {
        return;
      }
      if (
        lastTransportPulsePositionRef.current >= 0 &&
        snapshot.positionSec - lastTransportPulsePositionRef.current < 0.2
      ) {
        return;
      }
      lastTransportPulseAtRef.current = now;
      lastTransportPulsePositionRef.current = snapshot.positionSec;
      void sessionTransportService
        .upsertSessionTransport({
          sessionId: roomName,
          participantId: sessionParticipantId,
          positionSec: snapshot.positionSec,
          playbackRate: snapshot.playbackRate,
          pitchSemitones: snapshot.pitchSemitones,
          state: "playing",
        })
        .then((authoritative) => {
          syncSeqFromAuthoritative(authoritative.seq);
        })
        .catch(() => undefined);
    }, 1000);
    return () => {
      window.clearInterval(intervalId);
    };
  }, [
    clients.length,
    localSessionRole,
    roomName,
    sessionParticipantId,
    sessionTransportService,
    syncSeqFromAuthoritative,
  ]);

  const persistTransportSnapshot = useCallback(
    (op: DemoCommandOp, payload: BenchPayload) => {
      if (
        op !== "transport.play" &&
        op !== "transport.pause" &&
        op !== "transport.seek" &&
        op !== "transport.setRate" &&
        op !== "transport.setPitch"
      ) {
        return;
      }
      const playback = playbackControllerRef.current;
      if (!playback) {
        return;
      }
      let snapshot: PlayerSnapshot;
      try {
        snapshot = playback.snapshot();
      } catch {
        return;
      }
      const nextState: "playing" | "paused" | "stopped" =
        op === "transport.play"
          ? "playing"
          : op === "transport.pause"
            ? "paused"
            : snapshot.state === "playing"
              ? "playing"
              : snapshot.state === "paused"
                ? "paused"
                : "stopped";
      const nextPositionSec =
        op === "transport.seek"
          ? (payload.toPositionSec ?? snapshot.positionSec)
          : snapshot.positionSec;
      const nextPlaybackRate =
        op === "transport.setRate"
          ? (payload.rate ?? snapshot.playbackRate)
          : snapshot.playbackRate;
      const nextPitchSemitones =
        op === "transport.setPitch"
          ? (payload.pitchSemitones ?? snapshot.pitchSemitones)
          : snapshot.pitchSemitones;
      void sessionTransportService
        .upsertSessionTransport({
          sessionId: roomName,
          participantId: sessionParticipantId,
          positionSec: nextPositionSec,
          playbackRate: nextPlaybackRate,
          pitchSemitones: nextPitchSemitones,
          state: nextState,
        })
        .then((authoritative) => {
          syncSeqFromAuthoritative(authoritative.seq);
        })
        .catch((error: unknown) => {
          const message = error instanceof Error ? error.message : String(error);
          appendLog(`session transport persistence failed: ${message}`);
        });
    },
    [appendLog, roomName, sessionParticipantId, sessionTransportService, syncSeqFromAuthoritative],
  );

  const resolveTransportSnapshotForCommand = useCallback(
    (
      op: DemoCommandOp,
      payload: {
        toPositionSec?: number;
        rate?: number;
        pitchSemitones?: number;
      },
    ): {
      positionSec: number;
      playbackRate: number;
      pitchSemitones: number;
      state: "playing" | "paused" | "stopped";
    } | null => {
      if (
        op !== "transport.play" &&
        op !== "transport.pause" &&
        op !== "transport.seek" &&
        op !== "transport.setRate" &&
        op !== "transport.setPitch"
      ) {
        return null;
      }
      const playback = playbackControllerRef.current;
      if (!playback) {
        return null;
      }
      let snapshot: PlayerSnapshot;
      try {
        snapshot = playback.snapshot();
      } catch {
        return null;
      }
      const nextState: "playing" | "paused" | "stopped" =
        op === "transport.play"
          ? "playing"
          : op === "transport.pause"
            ? "paused"
            : snapshot.state === "playing"
              ? "playing"
              : snapshot.state === "paused"
                ? "paused"
                : "stopped";
      const nextPositionSec =
        op === "transport.seek"
          ? (payload.toPositionSec ?? snapshot.positionSec)
          : snapshot.positionSec;
      const nextPlaybackRate =
        op === "transport.setRate"
          ? (payload.rate ?? snapshot.playbackRate)
          : snapshot.playbackRate;
      const nextPitchSemitones =
        op === "transport.setPitch"
          ? (payload.pitchSemitones ?? snapshot.pitchSemitones)
          : snapshot.pitchSemitones;
      return {
        positionSec: nextPositionSec,
        playbackRate: nextPlaybackRate,
        pitchSemitones: nextPitchSemitones,
        state: nextState,
      };
    },
    [],
  );

  const issueCommand = useCallback(
    async (
      op: DemoCommandOp,
      issuer: RuntimeClient,
      extraPayload?: {
        toPositionSec?: number;
        rate?: number;
        pitchSemitones?: number;
        songYoutubeId?: string;
        songTitle?: string;
        songSmamsUrl?: string;
        songStemUrlsByRole?: Readonly<Record<string, string>>;
        leadMs?: number;
        latencyCompEnabled?: boolean;
        authoritativeExecuteAtMs?: number;
      },
    ) => {
      if (op === "transport.play" && playBlockReason !== null) {
        appendLog(`blocked ${op}: ${playBlockReason}`);
        pushCommandToast(`blocked ${op}: ${playBlockReason}`, "warning");
        return;
      }
      if (requiresPlaybackGate(op) && playbackIssueBlockReason !== null) {
        appendLog(`blocked ${op}: ${playbackIssueBlockReason}`);
        pushCommandToast(`blocked ${op}: ${playbackIssueBlockReason}`, "warning");
        return;
      }
      const seq = ++seqRef.current;
      const issuedAtMs = Date.now();
      lastSeqByIssuerIdRef.current.set(issuer.id, seq);
      const fallbackExecuteAtMs = issuedAtMs + leadMs;
      let authoritativeExecuteAtMs = extraPayload?.authoritativeExecuteAtMs ?? fallbackExecuteAtMs;
      const commandId = `${issuer.id}:${seq}`;
      const normalizedSongStemUrls = normalizeStemUrlsByRole(extraPayload?.songStemUrlsByRole);
      const transportPayload =
        op === "transport.seek"
          ? extraPayload?.toPositionSec !== undefined
            ? { toPositionSec: extraPayload.toPositionSec }
            : {}
          : op === "transport.setRate"
            ? extraPayload?.rate !== undefined
              ? { rate: extraPayload.rate }
              : {}
            : op === "transport.setPitch"
              ? extraPayload?.pitchSemitones !== undefined
                ? { pitchSemitones: extraPayload.pitchSemitones }
                : {}
              : op === "transport.setSong"
                ? {
                    ...(extraPayload?.songYoutubeId !== undefined
                      ? { songYoutubeId: extraPayload.songYoutubeId }
                      : {}),
                    ...(extraPayload?.songTitle !== undefined
                      ? { songTitle: extraPayload.songTitle }
                      : {}),
                    ...(extraPayload?.songSmamsUrl !== undefined
                      ? { songSmamsUrl: extraPayload.songSmamsUrl }
                      : {}),
                    ...(normalizedSongStemUrls !== undefined
                      ? { songStemUrlsByRole: normalizedSongStemUrls }
                      : {}),
                  }
                : op === "transport.setLead"
                  ? extraPayload?.leadMs !== undefined
                    ? { leadMs: clampScheduledLeadMs(extraPayload.leadMs) }
                    : {}
                  : op === "transport.setLatencyComp"
                    ? extraPayload?.latencyCompEnabled !== undefined
                      ? { latencyCompEnabled: extraPayload.latencyCompEnabled }
                      : {}
                    : {};
      const payload = {
        commandId,
        seq,
        op,
        issuedAtMs,
        executeAtMs: fallbackExecuteAtMs,
        ...(authoritativeExecuteAtMs !== fallbackExecuteAtMs ? { authoritativeExecuteAtMs } : {}),
        issuerId: issuer.id,
        issuerCountry: issuer.countryCode,
        ...transportPayload,
      };
      if (hasMixPayloadKeys(payload)) {
        appendLog(`blocked ${op}: transport payload cannot include local mix keys`);
        return;
      }
      const transportSnapshotForCommand = resolveTransportSnapshotForCommand(op, {
        ...(payload.toPositionSec !== undefined ? { toPositionSec: payload.toPositionSec } : {}),
        ...(payload.rate !== undefined ? { rate: payload.rate } : {}),
        ...(payload.pitchSemitones !== undefined ? { pitchSemitones: payload.pitchSemitones } : {}),
      });
      if (transportSnapshotForCommand) {
        const issueMonoMs = performance.now();
        const stampStartedMs = performance.now();
        try {
          const authoritative = await sessionTransportService.upsertSessionTransport({
            sessionId: roomName,
            participantId: sessionParticipantId,
            ...transportSnapshotForCommand,
            commandId,
            seq,
            issuerId: issuer.id,
            issuedAtMono: issueMonoMs,
            issuedAtEpochMs: issuedAtMs,
            op: "transport.update",
            payload: transportSnapshotForCommand,
          });
          syncSeqFromAuthoritative(authoritative.seq);
          if (
            typeof authoritative.executeAtEpochMs === "number" &&
            Number.isFinite(authoritative.executeAtEpochMs)
          ) {
            authoritativeExecuteAtMs = authoritative.executeAtEpochMs;
            payload.authoritativeExecuteAtMs = authoritative.executeAtEpochMs;
          }
          appendLog(
            `authoritative schedule ${op}: executeAt=${Math.round(authoritativeExecuteAtMs)} stampRtt=${Math.round(
              performance.now() - stampStartedMs,
            )}ms`,
          );
        } catch (error) {
          const message = error instanceof Error ? error.message : String(error);
          if (isTransportOutOfOrderError(error)) {
            appendLog(`authoritative schedule stale-seq detected (${op}): ${message}`);
            try {
              const recovered = await sessionTransportService.upsertSessionTransport({
                sessionId: roomName,
                participantId: sessionParticipantId,
                ...transportSnapshotForCommand,
              });
              syncSeqFromAuthoritative(recovered.seq);
              if (
                typeof recovered.executeAtEpochMs === "number" &&
                Number.isFinite(recovered.executeAtEpochMs)
              ) {
                authoritativeExecuteAtMs = recovered.executeAtEpochMs;
                payload.authoritativeExecuteAtMs = recovered.executeAtEpochMs;
              }
              appendLog(
                `authoritative schedule recovered via legacy transport update (${op}) seq=${recovered.seq ?? "n/a"} executeAt=${Math.round(authoritativeExecuteAtMs)}`,
              );
            } catch (recoveryError) {
              const recoveryMessage =
                recoveryError instanceof Error ? recoveryError.message : String(recoveryError);
              appendLog(`authoritative schedule recovery failed (${op}): ${recoveryMessage}`);
            }
          } else {
            appendLog(`authoritative schedule fallback (${op}): ${message}`);
          }
        }
      }
      transportAuthorityParticipantIdRef.current = issuer.id;
      const command: TransportCommandEnvelope = {
        op,
        commandId,
        ...(authoritativeExecuteAtMs !== fallbackExecuteAtMs
          ? { authoritativeExecuteAtMs, executeAtServerMonoMs: authoritativeExecuteAtMs }
          : {}),
        payload,
      };
      const ackKey = `${issuer.id}:${seq}`;
      ackMapRef.current.set(ackKey, new Set([issuer.id]));
      const marker = getScopeShiftMarker(op, {
        ...(payload.rate !== undefined ? { rate: payload.rate } : {}),
        ...(payload.pitchSemitones !== undefined ? { pitchSemitones: payload.pitchSemitones } : {}),
      });
      pushVisualEvent({
        atMs: issuedAtMs,
        lane: issuer.lane,
        type: issueEventType(op),
        ...(marker ? { marker } : {}),
      });
      scheduleExecutionPulse(issuer.lane, op, resolveAuthoritativeExecuteAtMs(payload), marker);
      if (issuer.id === localPlaybackClientIdRef.current) {
        capturePlaybackSnapshot("receive", payload, issuer.id);
        scheduleAudioApply(payload, issuer.id);
      }
      if (issuer.transport) {
        issuer.transport.publish(command);
      }
      if (issuer.id === sessionParticipantId && !transportSnapshotForCommand) {
        persistTransportSnapshot(op, payload);
      }
      setCommandBanner({
        message: `You issued ${op} (seq ${seq})`,
        tone: "info",
      });
      pushCommandToast(
        `issued ${op} by ${issuer.id} (seq ${seq})`,
        issuer.id === sessionParticipantId ? "info" : "warning",
      );
      appendLog(
        `[${issuer.id} ${countryCodeToFlagEmoji(issuer.countryCode)}] issued ${op} seq=${seq} commandId=${commandId} executeAt=${authoritativeExecuteAtMs}${
          payload.toPositionSec !== undefined ? ` target=${payload.toPositionSec.toFixed(3)}s` : ""
        }${payload.leadMs !== undefined ? ` lead=${Math.round(payload.leadMs)}ms` : ""}${
          payload.latencyCompEnabled !== undefined
            ? ` latencyComp=${payload.latencyCompEnabled ? "on" : "off"}`
            : ""
        }`,
      );
    },
    [
      appendLog,
      capturePlaybackSnapshot,
      leadMs,
      playBlockReason,
      playbackIssueBlockReason,
      persistTransportSnapshot,
      pushCommandToast,
      resolveTransportSnapshotForCommand,
      scheduleAudioApply,
      scheduleExecutionPulse,
      sessionParticipantId,
      sessionTransportService,
      syncSeqFromAuthoritative,
      pushVisualEvent,
      roomName,
    ],
  );

  const runRandomCommand = () => {
    const candidates = runtimeClientsRef.current;
    if (!candidates.length) {
      appendLog("no connected clients for random trigger");
      return;
    }
    const randomWord = new Uint32Array(1);
    crypto.getRandomValues(randomWord);
    const index = (randomWord[0] ?? 0) % candidates.length;
    const issuer = candidates[index];
    if (!issuer) {
      return;
    }
    const op = nextRandomOpRef.current;
    nextRandomOpRef.current = op === "transport.play" ? "transport.pause" : "transport.play";
    void issueCommand(op, issuer);
  };

  const toggleRandomCycle = () => {
    if (randomCycleOn) {
      if (randomTimerRef.current !== null) {
        window.clearInterval(randomTimerRef.current);
        randomTimerRef.current = null;
      }
      setRandomCycleOn(false);
      appendLog("random command cycle stopped");
      return;
    }
    if (!runtimeClientsRef.current.length) {
      appendLog("connect clients first");
      return;
    }
    if (playBlockReason !== null) {
      appendLog(`random command cycle blocked: ${playBlockReason}`);
      return;
    }
    randomTimerRef.current = window.setInterval(runRandomCommand, cycleMs);
    setRandomCycleOn(true);
    appendLog(`random command cycle started (${cycleMs} ms interval)`);
  };

  const issueFromOperator = (
    op: DemoCommandOp,
    overrides?: {
      toPositionSec?: number;
      rate?: number;
      pitchSemitones?: number;
    },
  ) => {
    const localRuntime = runtimeClientsRef.current.find(
      (client) => client.id === sessionParticipantId,
    );
    if (!localRuntime) {
      appendLog("connect clients first");
      return;
    }
    if (op === "transport.play" && playBlockReason) {
      setCommandBanner({
        message: playBlockReason,
        tone: "warning",
      });
      appendLog(`play blocked: ${playBlockReason}`);
      return;
    }
    void issueCommand(op, localRuntime, {
      ...(op === "transport.seek"
        ? { toPositionSec: overrides?.toPositionSec ?? seekTargetSec }
        : {}),
      ...(op === "transport.setRate" ? { rate: overrides?.rate ?? tempoRate } : {}),
      ...(op === "transport.setPitch"
        ? { pitchSemitones: overrides?.pitchSemitones ?? keySemitones }
        : {}),
    });
  };

  const issueSeekTo = (targetSec: number) => {
    const duration = localSnapshot?.durationSec;
    const clampedTarget =
      duration !== undefined && duration > 0
        ? Math.max(0, Math.min(duration, targetSec))
        : Math.max(0, targetSec);
    setSeekTargetSec(clampedTarget);
    setScrubPositionSec(clampedTarget);
    const localRuntime = runtimeClientsRef.current.find(
      (client) => client.id === sessionParticipantId,
    );
    if (!localRuntime) {
      appendLog("connect clients first");
      return;
    }
    void issueCommand("transport.seek", localRuntime, { toPositionSec: clampedTarget });
  };

  const broadcastScheduledLeadMs = useCallback(
    (nextLeadMs: number) => {
      const localRuntime = runtimeClientsRef.current.find(
        (client) => client.id === sessionParticipantId,
      );
      if (!localRuntime) {
        return false;
      }
      void issueCommand("transport.setLead", localRuntime, { leadMs: nextLeadMs });
      return true;
    },
    [issueCommand, sessionParticipantId],
  );

  const syncScheduledLeadMs = useCallback(
    (requestedLeadMs: number) => {
      const nextLeadMs = clampScheduledLeadMs(requestedLeadMs);
      setLeadMs(nextLeadMs);
      const didBroadcast = broadcastScheduledLeadMs(nextLeadMs);
      if (!didBroadcast) {
        appendLog("scheduled lead update is local-only until connected");
      }
    },
    [appendLog, broadcastScheduledLeadMs],
  );

  const broadcastLatencyCompEnabled = useCallback(
    (nextEnabled: boolean) => {
      const localRuntime = runtimeClientsRef.current.find(
        (client) => client.id === sessionParticipantId,
      );
      if (!localRuntime) {
        return false;
      }
      void issueCommand("transport.setLatencyComp", localRuntime, {
        latencyCompEnabled: nextEnabled,
      });
      return true;
    },
    [issueCommand, sessionParticipantId],
  );

  const syncLatencyCompEnabled = useCallback(
    (requestedEnabled: boolean) => {
      setLatencyCompEnabled(requestedEnabled);
      const didBroadcast = broadcastLatencyCompEnabled(requestedEnabled);
      if (!didBroadcast) {
        appendLog("latency compensation update is local-only until connected");
      }
    },
    [appendLog, broadcastLatencyCompEnabled],
  );

  useEffect(() => {
    const connectedOthers = clients.filter(
      (client) => client.connected && client.id !== sessionParticipantId,
    ).length;
    if (connectedOthers <= 0) {
      lastLeadResyncConnectedOthersRef.current = 0;
      return;
    }
    if (connectedOthers === lastLeadResyncConnectedOthersRef.current) {
      return;
    }
    lastLeadResyncConnectedOthersRef.current = connectedOthers;
    broadcastScheduledLeadMs(leadMs);
  }, [broadcastScheduledLeadMs, clients, leadMs, sessionParticipantId]);

  useEffect(() => {
    const connectedOthers = clients.filter(
      (client) => client.connected && client.id !== sessionParticipantId,
    ).length;
    if (connectedOthers <= 0) {
      lastLatencyCompResyncConnectedOthersRef.current = 0;
      return;
    }
    if (connectedOthers === lastLatencyCompResyncConnectedOthersRef.current) {
      return;
    }
    lastLatencyCompResyncConnectedOthersRef.current = connectedOthers;
    broadcastLatencyCompEnabled(latencyCompEnabled);
  }, [broadcastLatencyCompEnabled, clients, latencyCompEnabled, sessionParticipantId]);

  const clearEvents = () => {
    eventsRef.current = [];
    ackMapRef.current.clear();
    for (const timerId of executionPulseTimersRef.current) {
      window.clearTimeout(timerId);
    }
    executionPulseTimersRef.current.clear();
    playbackSchedulerRef.current?.resetAppliedCache();
    deliveryLatencySamplesRef.current = [];
    applyOffsetSamplesRef.current = [];
    setParticipantTelemetry({});
    setCommandArbitrationRows([]);
    setCommandToasts([]);
    appendLog("oscilloscope cleared");
  };

  useEffect(() => {
    const draw = () => {
      const canvas = canvasRef.current;
      if (!canvas) {
        drawFrameRef.current = window.requestAnimationFrame(draw);
        return;
      }
      const width = canvas.clientWidth;
      const laneHeight = 20;
      const topPad = 24;
      const bottomPad = 24;
      const height = Math.max(260, topPad + bottomPad + laneHeight * Math.max(1, clients.length));
      const pixelRatio = window.devicePixelRatio || 1;
      if (
        canvas.width !== Math.floor(width * pixelRatio) ||
        canvas.height !== Math.floor(height * pixelRatio)
      ) {
        canvas.width = Math.floor(width * pixelRatio);
        canvas.height = Math.floor(height * pixelRatio);
      }
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        drawFrameRef.current = window.requestAnimationFrame(draw);
        return;
      }
      ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
      ctx.clearRect(0, 0, width, height);

      ctx.fillStyle = "#000000";
      ctx.fillRect(0, 0, width, height);

      ctx.fillStyle = "#00ff66";
      ctx.font = "12px monospace";
      ctx.fillText("Band Sync Oscilloscope - join/issue/receive/execute", 12, 16);

      for (const client of clients) {
        const y = topPad + client.lane * laneHeight;
        ctx.strokeStyle = "#0d2d14";
        ctx.beginPath();
        ctx.moveTo(0, y + laneHeight / 2);
        ctx.lineTo(width, y + laneHeight / 2);
        ctx.stroke();

        if (client.status === "failed") {
          ctx.fillStyle = "#ff6b6b";
        } else if (client.connected) {
          ctx.fillStyle = "#00ff66";
        } else {
          ctx.fillStyle = "#4a4a4a";
        }
        const flag = countryCodeToFlagEmoji(client.countryCode);
        const joinSuffix =
          client.joinElapsedMs !== undefined
            ? ` [JOIN +${Math.round(client.joinElapsedMs)}ms]`
            : "";
        const failReason =
          client.errorMessage !== undefined ? ` (${client.errorMessage.slice(0, 52)})` : "";
        const failSuffix = client.errorMessage ? ` FAILED${failReason}` : "";
        const deviceSuffix =
          client.deviceType && client.deviceType.trim().length > 0 ? ` [${client.deviceType}]` : "";
        const latencyCompSuffix = !latencyCompEnabled
          ? " [LATENCY COMP OFF]"
          : client.latencyCompMs !== undefined
            ? ` [LATENCY COMP ${client.latencyCompMs.toFixed(1)}ms]`
            : " [LATENCY COMP n/a]";
        ctx.fillText(
          `${flag} ${client.countryCode} ${client.id}${deviceSuffix}${latencyCompSuffix}${joinSuffix}${failSuffix}`,
          10,
          y + laneHeight / 2 - 4,
        );
      }

      const now = Date.now();
      const leftX = 145;
      const usableWidth = Math.max(1, width - leftX - 8);
      const gridStepMs = 2_000;
      const majorGridStepMs = 5_000;

      // Time-axis frame.
      ctx.strokeStyle = "#1b3f27";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(leftX, topPad);
      ctx.lineTo(leftX, height - bottomPad);
      ctx.lineTo(width - 8, height - bottomPad);
      ctx.stroke();

      // Vertical time grid (minor + major lines).
      for (let ageMs = 0; ageMs <= OSC_WINDOW_MS; ageMs += gridStepMs) {
        const x = leftX + usableWidth * (1 - ageMs / OSC_WINDOW_MS);
        const isMajor = ageMs % majorGridStepMs === 0;
        ctx.strokeStyle = isMajor ? "#1d4b2e" : "#0f2318";
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(x, topPad);
        ctx.lineTo(x, height - bottomPad);
        ctx.stroke();

        if (isMajor) {
          const secondsAgo = Math.round(ageMs / 1000);
          ctx.fillStyle = "#7fc595";
          ctx.font = "11px monospace";
          ctx.fillText(
            secondsAgo === 0 ? "now" : `-${secondsAgo}s`,
            Math.max(leftX, x - 16),
            height - 8,
          );
        }
      }

      const visualEvents = eventsRef.current;
      let firstValidIndex = 0;
      while (firstValidIndex < visualEvents.length) {
        const oldestEvent = visualEvents[firstValidIndex];
        if (!oldestEvent || now - oldestEvent.atMs <= OSC_WINDOW_MS) {
          break;
        }
        firstValidIndex += 1;
      }
      if (firstValidIndex > 0) {
        visualEvents.splice(0, firstValidIndex);
      }

      for (const event of visualEvents) {
        const ageMs = now - event.atMs;
        const x = leftX + usableWidth * (1 - ageMs / OSC_WINDOW_MS);
        if (x < leftX || x > width) {
          continue;
        }
        const laneY = topPad + event.lane * laneHeight + laneHeight / 2;
        const color = eventColor(event.type);

        if (event.type === "all-received") {
          ctx.strokeStyle = color;
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.moveTo(x, topPad);
          ctx.lineTo(x, height - bottomPad);
          ctx.stroke();
          continue;
        }

        if (event.type === "issue-play" || event.type === "issue-pause") {
          ctx.strokeStyle = "#2ea3ff";
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(x, topPad);
          ctx.lineTo(x, height - bottomPad);
          ctx.stroke();
          ctx.strokeStyle = color;
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.moveTo(x, laneY - 8);
          ctx.lineTo(x, laneY + 8);
          ctx.stroke();

          // Issuer arrow on the lane to make the button-presser obvious.
          ctx.fillStyle = color;
          ctx.beginPath();
          ctx.moveTo(x + 2, laneY);
          ctx.lineTo(x + 12, laneY - 6);
          ctx.lineTo(x + 12, laneY + 6);
          ctx.closePath();
          ctx.fill();
          continue;
        }

        if (event.type === "join") {
          ctx.fillStyle = color;
          ctx.beginPath();
          ctx.arc(x, laneY, 4, 0, Math.PI * 2);
          ctx.fill();
          continue;
        }

        if (event.type === "download-progress") {
          const barPercent = Math.max(0, Math.min(100, event.barPercent ?? 0));
          const barWidth = Math.max(10, Math.round((barPercent / 100) * 34));
          ctx.fillStyle = color;
          ctx.fillRect(x - 2, laneY - 5, barWidth, 10);
          ctx.fillStyle = "#06210e";
          ctx.font = "11px monospace";
          ctx.fillText("D", x + 2, laneY + 4);
          continue;
        }

        if (event.type === "receive-play" || event.type === "receive-pause") {
          ctx.strokeStyle = color;
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.moveTo(x, laneY - 6);
          ctx.lineTo(x, laneY + 6);
          ctx.stroke();
          continue;
        }

        ctx.fillStyle = color;
        ctx.fillRect(x - 2, laneY - 2, 4, 4);
        if (event.marker) {
          ctx.fillStyle = color;
          ctx.font = "11px monospace";
          ctx.fillText(event.marker, x + 6, laneY + 4);
        }
      }

      drawFrameRef.current = window.requestAnimationFrame(draw);
    };

    drawFrameRef.current = window.requestAnimationFrame(draw);
    return () => {
      if (drawFrameRef.current !== null) {
        window.cancelAnimationFrame(drawFrameRef.current);
      }
    };
  }, [clients, latencyCompEnabled]);

  const authStatus = freshGuestMode
    ? isAuthenticated
      ? "Fresh guest mode | Signed in"
      : "Fresh guest mode | Signed out"
    : !cognitoAuth
      ? "Auth not configured"
      : isAuthReady
        ? isAuthenticated
          ? "Signed in with Cognito"
          : "Signed out"
        : "Checking session...";
  const roomStatus = roomProvisioned ? "Room link is ready to share" : "Create room link first";
  const sessionStatus =
    clients.length > 0
      ? `In session (${connectedCount}/${clients.length} connected)`
      : "Not in session yet";
  const nextActionHint = roomProvisioned
    ? clients.length > 0
      ? "Now load a song and start transport controls."
      : "Next: join this room from this tab or another tab."
    : "Next: create a room link (requires sign-in), then share it.";

  return (
    <main
      style={{
        background:
          "radial-gradient(circle at top, rgba(26, 58, 40, 0.35) 0%, rgba(7, 10, 9, 1) 42%, rgba(5, 6, 6, 1) 100%)",
        color: "#d7ffd7",
        minHeight: "100vh",
        padding: "20px",
        fontFamily: "Inter, system-ui, -apple-system, sans-serif",
      }}
    >
      <section
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: "12px",
          marginBottom: "10px",
          flexWrap: "wrap",
        }}
      >
        <div style={{ flex: "1 1 320px", minWidth: "280px" }}>
          <h1 style={{ marginBottom: "4px", fontSize: "30px" }}>Band Session Live Sync Lab</h1>
          <p style={{ marginTop: 0, color: "#9be8ba", lineHeight: 1.45 }}>
            Create a room link, share it, and sync playback instantly with collaborators.
          </p>
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "8px" }}>
            <span
              style={{
                fontSize: "12px",
                color: "#caebff",
                border: "1px solid rgba(137, 188, 228, 0.45)",
                borderRadius: "999px",
                padding: "4px 10px",
                background: "rgba(12, 31, 45, 0.5)",
              }}
            >
              {authStatus}
            </span>
            <span
              style={{
                fontSize: "12px",
                color: "#caebff",
                border: "1px solid rgba(137, 188, 228, 0.45)",
                borderRadius: "999px",
                padding: "4px 10px",
                background: "rgba(12, 31, 45, 0.5)",
              }}
            >
              {roomStatus}
            </span>
            <span
              style={{
                fontSize: "12px",
                color: "#caebff",
                border: "1px solid rgba(137, 188, 228, 0.45)",
                borderRadius: "999px",
                padding: "4px 10px",
                background: "rgba(12, 31, 45, 0.5)",
              }}
            >
              {sessionStatus}
            </span>
          </div>
          <div
            style={{
              border: "1px solid rgba(75, 135, 96, 0.5)",
              borderRadius: "10px",
              padding: "8px 10px",
              background: "rgba(8, 24, 14, 0.7)",
              color: "#c5f3d7",
              fontSize: "13px",
            }}
          >
            <strong>What to do next:</strong> {nextActionHint}
          </div>
        </div>
        <section
          style={{
            width: "180px",
            border: "1px solid rgba(52, 124, 81, 0.8)",
            background: "rgba(5, 14, 8, 0.96)",
            borderRadius: "10px",
            padding: "10px",
            flexShrink: 0,
          }}
        >
          <div
            style={{ fontSize: "12px", color: "#9fd4ff", marginBottom: "8px", textAlign: "center" }}
          >
            Scan to join room
          </div>
          {inviteQrUrl ? (
            <img
              src={inviteQrUrl}
              alt="Room invite QR"
              style={{
                width: "140px",
                height: "140px",
                display: "block",
                margin: "0 auto 8px auto",
                borderRadius: "8px",
              }}
            />
          ) : null}
          <button
            type="button"
            onClick={() => {
              void copyInviteLink();
            }}
            style={{
              width: "100%",
              fontSize: "12px",
              borderRadius: "8px",
              padding: "7px",
              border: "1px solid rgba(72, 165, 107, 0.85)",
              background: "linear-gradient(180deg, #2f7f4e 0%, #24663f 100%)",
              color: "#ecfff3",
              fontWeight: 600,
            }}
          >
            Copy invite link
          </button>
          {lastInviteCopyUrl ? (
            <div style={{ marginTop: "8px", display: "grid", gap: "6px" }}>
              <div style={{ fontSize: "11px", color: "#b7ebc8" }}>
                {lastInviteCopyStatus || "Invite link ready."}
              </div>
              <input
                type="text"
                readOnly
                value={lastInviteCopyUrl}
                onFocus={(event) => event.currentTarget.select()}
                style={{
                  width: "100%",
                  fontSize: "11px",
                  borderRadius: "6px",
                  padding: "6px",
                  border: "1px solid rgba(137, 188, 228, 0.45)",
                  background: "rgba(12, 31, 45, 0.45)",
                  color: "#caebff",
                }}
              />
            </div>
          ) : null}
        </section>
      </section>

      <section
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
          gap: "10px",
          marginBottom: "14px",
        }}
      >
        {[
          ["1", "Sign in", "Only signed-in users can create a new share link."],
          ["2", "Create room link", "Click Create room link, then copy/share the link or QR."],
          ["3", "Join session", "Click Join session to enter and start syncing playback."],
        ].map(([step, title, description]) => (
          <div
            key={step}
            style={{
              border: "1px solid rgba(72, 165, 107, 0.5)",
              borderRadius: "10px",
              background: "rgba(9, 18, 14, 0.85)",
              padding: "10px",
            }}
          >
            <div style={{ color: "#86e5aa", fontSize: "12px", fontWeight: 700 }}>STEP {step}</div>
            <div style={{ color: "#d7ffd7", marginTop: "4px", fontWeight: 600 }}>{title}</div>
            <div style={{ color: "#9fd4ff", fontSize: "12px", marginTop: "4px" }}>
              {description}
            </div>
          </div>
        ))}
      </section>

      <section
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))",
          gap: "12px",
          marginBottom: "14px",
          border: "1px solid rgba(72, 165, 107, 0.35)",
          borderRadius: "12px",
          background: "rgba(8, 16, 13, 0.84)",
          padding: "12px",
        }}
      >
        <label>
          Advanced: API base URL
          <input
            style={{ width: "100%" }}
            value={apiBaseUrl}
            onChange={(event) => setApiBaseUrl(event.target.value)}
          />
        </label>
        <div>
          <div style={{ fontWeight: 700, color: "#9be8ba" }}>Step 1: Auth</div>
          <div style={{ marginTop: "6px", color: "#9fd4ff", minHeight: "20px" }}>
            {freshGuestMode
              ? isAuthenticated
                ? "Fresh guest mode (stateless on reload) | Signed in for this tab"
                : "Fresh guest mode (stateless on reload) | Signed out"
              : !cognitoAuth
                ? "Not configured"
                : isAuthReady
                  ? isAuthenticated
                    ? "Signed in with Cognito"
                    : "Signed out"
                  : "Checking session..."}
          </div>
          {authError ? (
            <div style={{ marginTop: "4px", color: "#ff9a9a", fontSize: "12px" }}>{authError}</div>
          ) : null}
          <div style={{ display: "flex", gap: "6px", marginTop: "6px" }}>
            <button
              type="button"
              onClick={() => void signIn()}
              disabled={!cognitoAuth || !isAuthReady}
            >
              Sign in
            </button>
            <button type="button" onClick={signOut} disabled={!cognitoAuth || !isAuthenticated}>
              Sign out
            </button>
          </div>
        </div>
        <label>
          Step 2: Room name
          <input
            style={{ width: "100%" }}
            value={roomName}
            onChange={(e) => {
              setRoomName(e.target.value);
              setRoomProvisioned(false);
              setHasAutoReconnectAttempted(false);
            }}
          />
          <div style={{ display: "grid", gap: "6px", marginTop: "6px" }}>
            <div style={{ display: "flex", gap: "6px" }}>
              <button
                type="button"
                onClick={() => {
                  void createRoomLink();
                }}
                disabled={isProvisioningRoom}
              >
                {isProvisioningRoom ? "Creating..." : "Create room link"}
              </button>
              <button type="button" onClick={generateNewRoom}>
                Generate new room
              </button>
            </div>
            <div style={{ color: "#9fd4ff", fontSize: "12px" }}>{roomDiscoveryMessage}</div>
          </div>
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "22px" }}>
          <input
            type="checkbox"
            checked={autoReconnectEnabled}
            onChange={(event) => {
              setAutoReconnectEnabled(event.currentTarget.checked);
              setHasAutoReconnectAttempted(false);
            }}
          />
          Auto reconnect this tab after refresh
        </label>
        <label>
          Step 3: Participant ID (this tab)
          <div style={{ display: "grid", gap: "6px", gridTemplateColumns: "1fr auto" }}>
            <input
              style={{ width: "100%" }}
              value={sessionParticipantId}
              onChange={(event) => {
                const raw = event.currentTarget.value.trim();
                const nextId = freshGuestMode ? raw : saveTabParticipantId(raw);
                setSessionParticipantId(nextId);
              }}
            />
            <button
              type="button"
              onClick={() => {
                const regenerated = freshGuestMode
                  ? createEphemeralTabParticipantId(participantProfile.participantId)
                  : regenerateTabParticipantId(participantProfile.participantId);
                setSessionParticipantId(regenerated);
                appendLog(`regenerated tab participant id: ${regenerated}`);
              }}
            >
              Regenerate
            </button>
          </div>
        </label>
        <label>
          Nickname
          <input
            style={{ width: "100%" }}
            value={participantProfile.nickname}
            onChange={(event) => {
              const nextNickname = event.currentTarget.value;
              if (freshGuestMode) {
                setParticipantProfile((prev) => ({
                  ...prev,
                  nickname: nextNickname.trim() || "Guest",
                }));
              } else {
                setParticipantProfile((prev) => updateParticipantNickname(prev, nextNickname));
              }
            }}
            onBlur={() => {
              void syncMemberProfileToBackend(participantProfile.nickname, mixPresets);
            }}
            placeholder="Enter nickname"
          />
        </label>
        <label>
          Country code (2 letters)
          <input
            style={{ width: "100%" }}
            value={participantCountryCode}
            onChange={(event) => setParticipantCountryCode(event.currentTarget.value.toUpperCase())}
            placeholder={DEFAULT_COUNTRY_CODES[0] ?? "AU"}
            maxLength={2}
          />
        </label>
        <label>
          Scheduled lead ms
          <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
            <button type="button" onClick={() => syncScheduledLeadMs(leadMs - 50)}>
              -
            </button>
            <input
              type="number"
              min={0}
              step={50}
              value={leadMs}
              onFocus={(event) => event.currentTarget.select()}
              onChange={(event) => {
                const next = event.target.valueAsNumber;
                if (Number.isNaN(next)) {
                  return;
                }
                setLeadMs(clampScheduledLeadMs(next));
              }}
              onBlur={(event) => syncScheduledLeadMs(event.currentTarget.valueAsNumber)}
              onKeyDown={(event) => {
                if (event.key !== "Enter") {
                  return;
                }
                event.preventDefault();
                syncScheduledLeadMs(event.currentTarget.valueAsNumber);
              }}
            />
            <button type="button" onClick={() => syncScheduledLeadMs(leadMs + 50)}>
              +
            </button>
          </div>
        </label>
        <label>
          Latency compensation (global)
          <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
            <button
              type="button"
              onClick={() => syncLatencyCompEnabled(!latencyCompEnabled)}
              style={{
                borderRadius: "999px",
                border: "1px solid rgba(132, 222, 153, 0.5)",
                background: latencyCompEnabled
                  ? "rgba(26, 95, 46, 0.75)"
                  : "rgba(44, 44, 44, 0.72)",
                color: latencyCompEnabled ? "#c7ffd2" : "#d2d2d2",
                padding: "4px 10px",
                fontWeight: 700,
              }}
            >
              {latencyCompEnabled ? "ON" : "OFF"}
            </button>
            <span style={{ color: "#9fd4ff", fontSize: "12px" }}>
              Syncs across all participants.
            </span>
          </div>
        </label>
        <label>
          Trigger mode
          <select
            value={triggerMode}
            onChange={(event) => setTriggerMode(event.target.value as TriggerMode)}
          >
            <option value="operator">I press play/pause</option>
            {debugSyntheticModeEnabled ? (
              <option value="random-client">Random connected client presses (debug)</option>
            ) : null}
          </select>
        </label>
        {debugSyntheticModeEnabled ? (
          <label>
            Random cycle ms
            <input
              type="number"
              min={100}
              step={100}
              value={cycleMs}
              onFocus={(event) => event.currentTarget.select()}
              onChange={(event) => {
                const next = event.target.valueAsNumber;
                if (Number.isNaN(next)) {
                  return;
                }
                setCycleMs(Math.max(100, next));
              }}
            />
          </label>
        ) : null}
      </section>

      <section style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "10px" }}>
        <button
          onClick={() => {
            void connectClients();
          }}
          disabled={isConnecting || localClientConnected}
          style={{
            borderRadius: "10px",
            padding: "8px 14px",
            border: "1px solid rgba(72, 165, 107, 0.85)",
            background: "linear-gradient(180deg, #2f7f4e 0%, #24663f 100%)",
            color: "#ecfff3",
            fontWeight: 700,
          }}
        >
          {isConnecting ? "Joining..." : localClientConnected ? "JOINED" : "Join session"}
        </button>
        <button
          onClick={disconnectAll}
          disabled={!clients.length && !isConnecting}
          style={{
            borderRadius: "10px",
            padding: "8px 14px",
            border: "1px solid rgba(169, 88, 88, 0.85)",
            background: "#3b1f1f",
            color: "#ffdede",
          }}
        >
          {isConnecting ? "Cancel join" : "Leave session"}
        </button>
        <button
          type="button"
          onClick={() => void loadRandomBackendSong()}
          disabled={isLoadingBackendSong}
          style={{ borderRadius: "10px", padding: "8px 14px" }}
        >
          {isLoadingBackendSong ? "Loading backend song..." : "Load random backend song (6 stems)"}
        </button>
        <button
          type="button"
          onClick={cancelSongLoad}
          disabled={!isLoadingBackendSong}
          style={{ borderRadius: "10px", padding: "8px 14px" }}
        >
          Cancel load
        </button>
        <button
          type="button"
          onClick={retrySongLoad}
          disabled={isLoadingBackendSong || !hasRetryableSongLoad}
          style={{ borderRadius: "10px", padding: "8px 14px" }}
        >
          Retry load
        </button>
      </section>

      <section
        style={{
          marginBottom: "12px",
          border: "1px solid #1f4b2a",
          background: "#0b1110",
          borderRadius: "12px",
          padding: "10px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "8px",
            marginBottom: "8px",
          }}
        >
          <strong style={{ fontSize: "14px" }}>Oscilloscope</strong>
          <button onClick={clearEvents} style={{ borderRadius: "10px", padding: "8px 14px" }}>
            Clear oscilloscope
          </button>
        </div>
        <canvas
          ref={canvasRef}
          style={{
            width: "100%",
            height: `${Math.max(280, clients.length * 20 + 60)}px`,
            border: "1px solid #1f4b2a",
            background: "#000",
            display: "block",
          }}
        />
        <div style={{ marginTop: "8px", color: "#9fe89f", fontSize: "12px" }}>
          Green pulse = join, Blue vertical = command issued, Green/Red issuer mark = play/pause
          issue, Yellow = received, Aqua/Orange pulse = scheduled execute. `A→`/`A←` = auto-adjust
          fast-forward/rewind correction. Green `D` bar = song stem download activity.
        </div>
      </section>

      <section
        style={{
          marginBottom: "12px",
          border: "1px solid #214f31",
          background: "linear-gradient(180deg, #0c1511 0%, #09100d 100%)",
          borderRadius: "14px",
          padding: "12px",
          display: "grid",
          gap: "10px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "8px",
          }}
        >
          <strong style={{ fontSize: "14px" }}>Transport</strong>
          <span
            style={{
              color: "#9fd4ff",
              fontSize: "12px",
              padding: "3px 8px",
              borderRadius: "999px",
              border: "1px solid rgba(159, 212, 255, 0.25)",
              background: "rgba(40, 83, 110, 0.22)",
            }}
          >
            {triggerMode === "operator" ? "Operator mode" : "Random client mode"}
          </span>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "8px",
            color: "#ccebd6",
            fontSize: "12px",
          }}
        >
          <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <input
              type="checkbox"
              checked={roomReadinessGateEnabled}
              onChange={(event) => setRoomReadinessGateEnabled(event.currentTarget.checked)}
            />
            Require room-wide readiness before play
          </label>
          <span style={{ color: playBlockReason ? "#ffcf9f" : "#95d7ab" }}>
            {playBlockReason ?? "Playback gate clear"}
          </span>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr auto 1fr",
            alignItems: "center",
            gap: "12px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px", justifySelf: "start" }}>
            <button
              type="button"
              onClick={() => issueSeekTo(scrubPositionSec - 5)}
              disabled={!canIssuePlaybackCommands}
              style={{
                borderRadius: "999px",
                border: "1px solid rgba(177, 215, 191, 0.35)",
                background: "rgba(31, 58, 40, 0.8)",
                color: "#d5f5df",
                minWidth: "54px",
                height: "34px",
                fontWeight: 700,
              }}
              title="Rewind 5 seconds"
            >
              -5s
            </button>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "8px", justifySelf: "center" }}>
            <button
              type="button"
              onClick={() =>
                issueFromOperator(
                  localSnapshot?.state === "playing" ? "transport.pause" : "transport.play",
                )
              }
              disabled={
                !canIssuePlaybackCommands ||
                (localSnapshot?.state !== "playing" && playBlockReason !== null)
              }
              style={{
                width: "56px",
                height: "56px",
                borderRadius: "999px",
                border: "none",
                background: "linear-gradient(180deg, #f8fff9 0%, #d3f6db 100%)",
                color: "#112417",
                fontWeight: 800,
                fontSize: "14px",
                cursor: "pointer",
                boxShadow: "0 6px 20px rgba(0,0,0,0.28)",
              }}
              title={localSnapshot?.state === "playing" ? "Pause" : (playBlockReason ?? "Play")}
            >
              {localSnapshot?.state === "playing" ? "II" : "▶"}
            </button>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "8px", justifySelf: "end" }}>
            <button
              type="button"
              onClick={() => issueSeekTo(scrubPositionSec + 5)}
              disabled={!canIssuePlaybackCommands}
              style={{
                borderRadius: "999px",
                border: "1px solid rgba(177, 215, 191, 0.35)",
                background: "rgba(31, 58, 40, 0.8)",
                color: "#d5f5df",
                minWidth: "54px",
                height: "34px",
                fontWeight: 700,
              }}
              title="Skip forward 5 seconds"
            >
              +5s
            </button>
          </div>
        </div>

        <div style={{ display: "grid", gap: "6px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              color: "#c7efd1",
              fontSize: "12px",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            <span>{formatClock(scrubPositionSec)}</span>
            <span>{formatClock(localSnapshot?.durationSec)}</span>
          </div>
          <input
            type="range"
            min={0}
            max={Math.max(1, localSnapshot?.durationSec ?? 1)}
            step={0.05}
            value={Math.max(0, scrubPositionSec)}
            onPointerDown={() => setIsScrubbing(true)}
            onMouseDown={() => setIsScrubbing(true)}
            onTouchStart={() => setIsScrubbing(true)}
            onPointerUp={(event) => {
              setIsScrubbing(false);
              const next = Math.max(0, event.currentTarget.valueAsNumber || 0);
              issueSeekTo(next);
            }}
            onMouseUp={(event) => {
              setIsScrubbing(false);
              const next = Math.max(0, event.currentTarget.valueAsNumber || 0);
              issueSeekTo(next);
            }}
            onTouchEnd={(event) => {
              setIsScrubbing(false);
              const next = Math.max(0, event.currentTarget.valueAsNumber || 0);
              issueSeekTo(next);
            }}
            onPointerCancel={() => {
              setIsScrubbing(false);
            }}
            onChange={(event) => {
              const next = Math.max(0, event.currentTarget.valueAsNumber || 0);
              setScrubPositionSec(next);
              setSeekTargetSec(next);
            }}
            disabled={!canIssuePlaybackCommands}
            style={{ width: "100%" }}
          />
          <div style={{ color: "#84b792", fontSize: "11px" }}>
            Drag to scrub and release to broadcast seek to all participants.
          </div>
          {playBlockReason ? (
            <div style={{ color: "#ffb0b0", fontSize: "11px" }}>
              Play gate active: {playBlockReason}
            </div>
          ) : (
            <div style={{ color: "#8cff8c", fontSize: "11px" }}>Play gate open.</div>
          )}
        </div>

        <div style={{ display: "grid", gap: "10px" }}>
          <label style={{ display: "grid", gap: "4px", color: "#d8e8ff", fontSize: "12px" }}>
            <span>Tempo {tempoRate.toFixed(2)}x</span>
            <input
              type="range"
              min={TEMPO_SLIDER_MIN}
              max={TEMPO_SLIDER_MAX}
              step={TEMPO_SLIDER_STEP}
              value={tempoRateToSliderValue(tempoRate)}
              list="tempo-slider-ticks"
              onPointerDown={() => setIsAdjustingTempo(true)}
              onMouseDown={() => setIsAdjustingTempo(true)}
              onTouchStart={() => setIsAdjustingTempo(true)}
              onChange={(event) => {
                const nextSlider = Number(event.currentTarget.value);
                if (Number.isNaN(nextSlider)) {
                  return;
                }
                const next = tempoSliderValueToRate(nextSlider);
                setTempoRate(next);
              }}
              onPointerUp={(event) => {
                setIsAdjustingTempo(false);
                const nextSlider = Number(event.currentTarget.value);
                if (Number.isNaN(nextSlider)) {
                  return;
                }
                const next = tempoSliderValueToRate(nextSlider);
                setTempoRate(next);
                issueFromOperator("transport.setRate", { rate: next });
              }}
              onMouseUp={(event) => {
                setIsAdjustingTempo(false);
                const nextSlider = Number(event.currentTarget.value);
                if (Number.isNaN(nextSlider)) {
                  return;
                }
                const next = tempoSliderValueToRate(nextSlider);
                setTempoRate(next);
                issueFromOperator("transport.setRate", { rate: next });
              }}
              onTouchEnd={(event) => {
                setIsAdjustingTempo(false);
                const nextSlider = Number(event.currentTarget.value);
                if (Number.isNaN(nextSlider)) {
                  return;
                }
                const next = tempoSliderValueToRate(nextSlider);
                setTempoRate(next);
                issueFromOperator("transport.setRate", { rate: next });
              }}
              onPointerCancel={() => setIsAdjustingTempo(false)}
              disabled={!canIssuePlaybackCommands}
              style={{ width: "100%" }}
            />
            <datalist id="tempo-slider-ticks">
              <option value={tempoRateToSliderValue(0.5)} />
              <option value={tempoRateToSliderValue(0.75)} />
              <option value={tempoRateToSliderValue(1)} />
              <option value={tempoRateToSliderValue(1.25)} />
              <option value={tempoRateToSliderValue(1.5)} />
              <option value={tempoRateToSliderValue(1.75)} />
              <option value={tempoRateToSliderValue(2)} />
            </datalist>
          </label>
          <label style={{ display: "grid", gap: "4px", color: "#eddfff", fontSize: "12px" }}>
            <span>
              Key {keySemitones >= 0 ? "+" : ""}
              {keySemitones} st
            </span>
            <input
              type="range"
              min={-12}
              max={12}
              step={1}
              value={keySemitones}
              list="key-slider-ticks"
              onPointerDown={() => setIsAdjustingKey(true)}
              onMouseDown={() => setIsAdjustingKey(true)}
              onTouchStart={() => setIsAdjustingKey(true)}
              onChange={(event) => {
                const next = Number(event.currentTarget.value);
                if (Number.isNaN(next)) {
                  return;
                }
                setKeySemitones(next);
              }}
              onPointerUp={(event) => {
                setIsAdjustingKey(false);
                const next = Number(event.currentTarget.value);
                if (Number.isNaN(next)) {
                  return;
                }
                setKeySemitones(next);
                issueFromOperator("transport.setPitch", { pitchSemitones: next });
              }}
              onMouseUp={(event) => {
                setIsAdjustingKey(false);
                const next = Number(event.currentTarget.value);
                if (Number.isNaN(next)) {
                  return;
                }
                setKeySemitones(next);
                issueFromOperator("transport.setPitch", { pitchSemitones: next });
              }}
              onTouchEnd={(event) => {
                setIsAdjustingKey(false);
                const next = Number(event.currentTarget.value);
                if (Number.isNaN(next)) {
                  return;
                }
                setKeySemitones(next);
                issueFromOperator("transport.setPitch", { pitchSemitones: next });
              }}
              onPointerCancel={() => setIsAdjustingKey(false)}
              disabled={!canIssuePlaybackCommands}
              style={{ width: "100%" }}
            />
            <datalist id="key-slider-ticks">
              <option value={-12} />
              <option value={-9} />
              <option value={-6} />
              <option value={-3} />
              <option value={0} />
              <option value={3} />
              <option value={6} />
              <option value={9} />
              <option value={12} />
            </datalist>
          </label>

          {triggerMode !== "operator" ? (
            <button
              onClick={toggleRandomCycle}
              disabled={!canIssuePlaybackCommands && !randomCycleOn}
              style={{
                borderRadius: "999px",
                border: "1px solid rgba(236, 178, 255, 0.45)",
                background: randomCycleOn ? "rgba(89, 38, 102, 0.8)" : "rgba(45, 31, 60, 0.72)",
                color: "#f5ddff",
                padding: "5px 10px",
                fontWeight: 700,
              }}
            >
              {randomCycleOn ? "Stop random cycle" : "Start random cycle"}
            </button>
          ) : null}
        </div>
      </section>

      <section
        style={{
          marginBottom: "12px",
          border: "1px solid #1f4b2a",
          background: "#0b1110",
          padding: "10px",
        }}
      >
        <div style={{ display: "flex", gap: "8px", alignItems: "center", marginBottom: "6px" }}>
          <strong>Stem loading</strong>
          <span
            style={{
              padding: "2px 8px",
              borderRadius: "999px",
              border: "1px solid #2a2a2a",
              color:
                backendLoadState === "ready"
                  ? "#8cff8c"
                  : backendLoadState === "loading"
                    ? "#ffe08a"
                    : backendLoadState === "error"
                      ? "#ff9a9a"
                      : "#b0b0b0",
              background:
                backendLoadState === "ready"
                  ? "#12301a"
                  : backendLoadState === "loading"
                    ? "#2f2a12"
                    : backendLoadState === "error"
                      ? "#301212"
                      : "#1b1b1b",
            }}
          >
            {backendLoadState.toUpperCase()}
          </span>
        </div>
        <div style={{ color: "#d7ffd7", marginBottom: "4px" }}>{backendLoadMessage}</div>
        <div style={{ color: "#9fd4ff", fontSize: "12px" }}>Current source: {backendSongLabel}</div>
        {roomLoadStatuses.length > 0 ? (
          <div style={{ marginTop: "8px", display: "grid", gap: "6px" }}>
            <div style={{ color: "#a8dfb5", fontSize: "12px", fontWeight: 700 }}>
              Room-wide load status
            </div>
            {roomLoadStatuses.map(({ client, status }) => (
              <div
                key={`${client.id}:${status.songYoutubeId}`}
                style={{
                  border: "1px solid #1a3a22",
                  background: "rgba(4, 12, 8, 0.75)",
                  padding: "6px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    color:
                      status.phase === "error" || status.phase === "timeout"
                        ? "#ffb0b0"
                        : "#a8dfb5",
                    fontSize: "11px",
                  }}
                >
                  <span>
                    {client.id}
                    {client.id === sessionParticipantId ? " (you)" : ""} - {status.phase}
                  </span>
                  <span>{clampPercent(status.percentage).toFixed(0)}%</span>
                </div>
                <div
                  style={{
                    marginTop: "3px",
                    height: "8px",
                    background: "#09120a",
                    border: "1px solid #1a3a22",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: `${clampPercent(status.percentage)}%`,
                      height: "100%",
                      background:
                        status.phase === "error" || status.phase === "timeout"
                          ? "#7a1f1f"
                          : "linear-gradient(90deg, #1f7a36 0%, #39bf5f 55%, #8eff9b 100%)",
                      transition: "width 120ms linear",
                    }}
                  />
                </div>
                {status.errorMessage ? (
                  <div style={{ color: "#ffb0b0", fontSize: "11px", marginTop: "3px" }}>
                    {status.errorMessage}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}
        {stemDownloadRows.length > 0 ? (
          <div style={{ marginTop: "8px", display: "grid", gap: "6px" }}>
            {stemDownloadRows.map((row) => {
              const percent = stemRowPercent(row);
              return (
                <div key={row.role} style={{ fontSize: "11px" }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      color: row.phase === "error" ? "#ffb0b0" : "#a8dfb5",
                    }}
                  >
                    <span>
                      {STEM_ROLE_LABELS[row.role]} - {row.phase}
                    </span>
                    <span>
                      {formatBytes(row.loadedBytes)}
                      {row.totalBytes !== undefined && row.totalBytes > 0
                        ? ` / ${formatBytes(row.totalBytes)}`
                        : ""}
                    </span>
                  </div>
                  <div
                    style={{
                      marginTop: "2px",
                      height: "8px",
                      background: "#09120a",
                      border: "1px solid #1a3a22",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        width: `${percent}%`,
                        height: "100%",
                        background:
                          row.phase === "error"
                            ? "#7a1f1f"
                            : "linear-gradient(90deg, #1f7a36 0%, #39bf5f 55%, #8eff9b 100%)",
                        transition: "width 80ms linear",
                      }}
                    />
                  </div>
                  {row.message ? <div style={{ color: "#ffb0b0" }}>{row.message}</div> : null}
                </div>
              );
            })}
          </div>
        ) : null}
      </section>

      <section
        style={{
          marginBottom: "12px",
          border: "1px solid #1f4b2a",
          background: "#071008",
          padding: "10px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <strong>Stem mixer</strong>
          <span style={{ color: "#9fd4ff", fontSize: "12px" }}>
            Sliders, mute/solo, live level bars
          </span>
        </div>
        {stemMixerRows.length === 0 ? (
          <div style={{ marginTop: "8px", color: "#95a995", fontSize: "13px" }}>
            Load backend stems to show mixer channels.
          </div>
        ) : (
          <div
            style={{
              marginTop: "10px",
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))",
              gap: "10px",
            }}
          >
            {stemMixerRows.map((row) => (
              <div
                key={row.role}
                style={{
                  border: "1px solid #173b1f",
                  background: "#08150a",
                  padding: "8px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  minHeight: "280px",
                }}
              >
                {/*
                  Match web-app meter rendering style:
                  - explicit 0..100 percent scalar
                  - fixed coordinate space fill (y=100-level, h=level)
                */}
                {(() => {
                  const levelPercent = meterPercentFromLevel01(row.level);
                  return (
                    <>
                      <div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "flex-end",
                            justifyContent: "center",
                            gap: "10px",
                            height: "172px",
                          }}
                        >
                          <div
                            style={{
                              position: "relative",
                              width: "12px",
                              height: "130px",
                              border: "1px solid #124120",
                              background: "#020502",
                              overflow: "hidden",
                            }}
                          >
                            <svg
                              viewBox="0 0 10 100"
                              preserveAspectRatio="none"
                              style={{ width: "100%", height: "100%" }}
                            >
                              <defs>
                                <linearGradient
                                  id={`lab-meter-gradient-${row.role}`}
                                  x1="0"
                                  x2="0"
                                  y1="100"
                                  y2="0"
                                  gradientUnits="userSpaceOnUse"
                                >
                                  <stop offset="0%" stopColor="#22c55e" />
                                  <stop offset="70%" stopColor="#22c55e" />
                                  <stop offset="70%" stopColor="#eab308" />
                                  <stop offset="90%" stopColor="#eab308" />
                                  <stop offset="90%" stopColor="#ef4444" />
                                  <stop offset="100%" stopColor="#ef4444" />
                                </linearGradient>
                              </defs>
                              <rect width="100%" height="100%" fill="rgba(0, 0, 0, 0.3)" />
                              <rect
                                x="0"
                                y={100 - levelPercent}
                                width="100%"
                                height={levelPercent}
                                fill={`url(#lab-meter-gradient-${row.role})`}
                              />
                            </svg>
                          </div>
                          <div
                            style={{
                              position: "relative",
                              width: "36px",
                              height: "136px",
                              marginBottom: "8px",
                            }}
                          >
                            <input
                              type="range"
                              min={0}
                              max={100}
                              value={Math.round(row.volume * 100)}
                              onChange={(event) =>
                                setStemVolume(
                                  row.role,
                                  (event.currentTarget.valueAsNumber || 0) / 100,
                                )
                              }
                              style={{
                                position: "absolute",
                                left: "50%",
                                top: "50%",
                                width: "116px",
                                height: "24px",
                                transform: "translate(-50%, -50%) rotate(-90deg)",
                                transformOrigin: "center",
                              }}
                              disabled={!playbackReady}
                            />
                          </div>
                        </div>
                      </div>
                      <div style={{ marginTop: "12px", textAlign: "center" }}>
                        <div style={{ fontWeight: 700, fontSize: "12px" }}>
                          {STEM_ROLE_LABELS[row.role]}
                        </div>
                        <div style={{ color: "#8fb99a", fontSize: "10px" }}>{row.role}</div>
                        <div style={{ color: "#b6d9c0", fontSize: "10px", marginTop: "3px" }}>
                          Vol {Math.round(row.volume * 100)}%
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: "6px", marginTop: "8px" }}>
                        <button
                          type="button"
                          onClick={() => toggleStemSolo(row.role)}
                          style={{
                            borderColor: row.solo ? "#3ac15f" : undefined,
                            background: row.solo ? "#163820" : undefined,
                            minWidth: "42px",
                          }}
                          disabled={!playbackReady}
                        >
                          Solo
                        </button>
                        <button
                          type="button"
                          onClick={() => toggleStemMuted(row.role)}
                          style={{
                            borderColor: row.muted ? "#ff6f6f" : undefined,
                            background: row.muted ? "#381616" : undefined,
                            minWidth: "42px",
                          }}
                          disabled={!playbackReady}
                        >
                          Mute
                        </button>
                      </div>
                      <div style={{ marginTop: "6px", fontSize: "10px", color: "#99d0a8" }}>
                        Level {levelPercent.toFixed(0)}%
                      </div>
                    </>
                  );
                })()}
              </div>
            ))}
          </div>
        )}
      </section>

      <section
        style={{
          marginBottom: "12px",
          border: "1px solid #1f4b2a",
          background: "#071008",
          padding: "10px",
          display: "grid",
          gap: "8px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <strong>Local mix presets</strong>
          <span style={{ color: "#9fd4ff", fontSize: "12px" }}>
            Synced to backend profile for this participant
          </span>
        </div>
        <div style={{ display: "grid", gap: "6px", gridTemplateColumns: "2fr 1fr 1fr" }}>
          <input
            value={newPresetName}
            onChange={(event) => setNewPresetName(event.currentTarget.value)}
            placeholder="Preset name (e.g., Drummer Focus)"
          />
          <button
            type="button"
            onClick={() => {
              const playback = playbackControllerRef.current;
              if (!playback) {
                appendLog("playback not ready");
                return;
              }
              const snapshot: Partial<Record<StemRole, StemMixSetting>> = {};
              for (const role of playback.getStemRoles()) {
                const current = playback.getStemSettings(role);
                snapshot[role] = {
                  volume: current?.volume ?? 1,
                  muted: current?.muted ?? false,
                  solo: current?.solo ?? false,
                };
              }
              const trimmedName = newPresetName.trim();
              if (!trimmedName) {
                appendLog("preset name required");
                return;
              }
              const nextPresets = freshGuestMode
                ? [
                    {
                      name: trimmedName,
                      stems: snapshot,
                    },
                    ...mixPresets.filter(
                      (entry) => entry.name.toLowerCase() !== trimmedName.toLowerCase(),
                    ),
                  ].slice(0, 20)
                : upsertMixPreset(mixPresets, {
                    name: trimmedName,
                    stems: snapshot,
                  });
              setMixPresets(nextPresets);
              setSelectedPresetName(trimmedName);
              void syncMemberProfileToBackend(participantProfile.nickname, nextPresets);
              appendLog(`saved mix preset "${trimmedName}"`);
            }}
            disabled={!playbackReady}
          >
            Save preset
          </button>
          <button
            type="button"
            onClick={() => {
              const nextPresets = freshGuestMode
                ? mixPresets.filter(
                    (entry) => entry.name.toLowerCase() !== selectedPresetName.trim().toLowerCase(),
                  )
                : deleteMixPreset(mixPresets, selectedPresetName);
              setMixPresets(nextPresets);
              void syncMemberProfileToBackend(participantProfile.nickname, nextPresets);
              appendLog(`deleted mix preset "${selectedPresetName}"`);
              setSelectedPresetName("");
            }}
            disabled={!selectedPresetName}
          >
            Delete preset
          </button>
        </div>
        <div style={{ display: "grid", gap: "6px", gridTemplateColumns: "2fr 1fr" }}>
          <select
            value={selectedPresetName}
            onChange={(event) => setSelectedPresetName(event.currentTarget.value)}
          >
            <option value="">Select preset</option>
            {mixPresets.map((preset) => (
              <option key={preset.name} value={preset.name}>
                {preset.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => {
              const playback = playbackControllerRef.current;
              if (!playback || !selectedPresetName) {
                return;
              }
              const preset = mixPresets.find((item) => item.name === selectedPresetName);
              if (!preset) {
                appendLog(`preset "${selectedPresetName}" not found`);
                return;
              }
              for (const [role, setting] of Object.entries(preset.stems)) {
                playback.setStemSettings(role as StemRole, setting);
              }
              refreshStemMixerRows(false);
              appendLog(`loaded mix preset "${selectedPresetName}"`);
            }}
            disabled={!selectedPresetName || !playbackReady}
          >
            Load preset
          </button>
        </div>
      </section>

      <p style={{ marginTop: 0, marginBottom: "10px", color: "#90ff90" }}>
        Connected participants: {connectedCount}/{clients.length} | Timing model: issue now, execute
        at shared timestamp ({leadMs}
        ms lead) | Transport: livekit | Authority: flat (every participant can issue transport) |
        Latency comp: {latencyCompEnabled ? "ON" : "OFF"} | Auth:{" "}
        {!cognitoAuth ? "unconfigured" : isAuthenticated ? "signed-in" : "signed-out"} | Clock
        offset (local-server): {formatSignedOffsetMs(localMinusServerClockOffsetMs)} (EWMA)
      </p>
      <p
        style={{ marginTop: 0, marginBottom: "10px", color: playbackError ? "#ff9a9a" : "#9fd4ff" }}
      >
        Local playback: {playbackReady ? "ready (6 stems)" : "backend stems not loaded"} | Local
        lane: {localPlaybackClientIdRef.current ?? "none"} | Output-time model: AudioContext output
        clock + execute wall-clock | Source: {backendSongLabel}
        {playbackError ? ` | Error: ${playbackError}` : ""}
      </p>
      <p style={{ marginTop: 0, marginBottom: "10px", color: "#9fd4ff" }}>
        Session song descriptor hash: {currentDescriptorHash}
      </p>
      {lastJoinSummary ? (
        <p style={{ marginTop: 0, marginBottom: "10px", color: "#9fd4ff" }}>
          Join summary: total {Math.round(lastJoinSummary.totalMs)}ms | connected{" "}
          {lastJoinSummary.connectedCount} | failed {lastJoinSummary.failedCount} | p95 join{" "}
          {Math.round(lastJoinSummary.p95Ms)}ms
        </p>
      ) : null}
      {lastCatchUpSummary ? (
        <p style={{ marginTop: 0, marginBottom: "10px", color: "#8fe1ff" }}>
          Catch-up completed: {lastCatchUpSummary}
        </p>
      ) : null}
      {lastConnectError ? (
        <p style={{ marginTop: 0, marginBottom: "10px", color: "#ffb0b0" }}>
          Last join error: {lastConnectError}
        </p>
      ) : null}
      <section
        style={{
          marginTop: 0,
          marginBottom: "10px",
          border: "1px solid #1f4b2a",
          background: "#08120a",
          padding: "8px 10px",
          color: "#bff2c4",
          fontSize: "12px",
        }}
      >
        <strong>Participant roster</strong>
        <div style={{ marginTop: "4px", color: "#9fd4ff" }}>
          Session role: {localSessionRole ?? "unknown"} | Mix policy: local-only (never broadcast)
        </div>
        {clients.length === 0 ? (
          <div style={{ marginTop: "6px", color: "#9aa99e" }}>No participants connected.</div>
        ) : (
          <div style={{ marginTop: "6px", display: "grid", gap: "4px" }}>
            {clients.map((client) => {
              const telemetry = participantTelemetry[client.id];
              return (
                <div key={client.id} style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  <span style={{ color: "#d6ffe0" }}>
                    {client.isLocal ? "[local]" : "[remote]"} {client.id}
                  </span>
                  <span style={{ color: client.connected ? "#8cff8c" : "#b0b0b0" }}>
                    {client.status}
                  </span>
                  <span style={{ color: "#9fd4ff" }}>{client.countryCode}</span>
                  <span style={{ color: "#9aa99e" }}>
                    lastSeen{" "}
                    {client.lastSeenAtEpochMs !== undefined
                      ? `${Math.max(0, Math.round((Date.now() - client.lastSeenAtEpochMs) / 1000))}s ago`
                      : "n/a"}
                  </span>
                  <span style={{ color: "#8fe1ff" }}>
                    recv/apply {telemetry?.receives ?? 0}/{telemetry?.applies ?? 0}
                  </span>
                  <span style={{ color: "#8fe1ff" }}>
                    dly/app {telemetry ? telemetry.lastDeliveryLatencyMs.toFixed(1) : "0.0"}ms/
                    {telemetry ? telemetry.lastApplyOffsetMs.toFixed(1) : "0.0"}ms
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </section>
      <section
        style={{
          marginTop: 0,
          marginBottom: "10px",
          border: "1px solid #1f4b2a",
          background: "#08120a",
          padding: "8px 10px",
          color: "#bff2c4",
          fontSize: "12px",
        }}
      >
        <strong>Sync SLO telemetry</strong>
        <div style={{ marginTop: "4px" }}>
          Delivery p95 {syncTelemetry.deliveryP95Ms.toFixed(2)}ms | p99{" "}
          {syncTelemetry.deliveryP99Ms.toFixed(2)}ms | samples {syncTelemetry.deliverySampleCount}
        </div>
        <div>
          Apply offset p95 {syncTelemetry.applyOffsetP95Ms.toFixed(2)}ms | p99{" "}
          {syncTelemetry.applyOffsetP99Ms.toFixed(2)}ms | max{" "}
          {syncTelemetry.applyOffsetMaxMs.toFixed(2)}ms | samples {syncTelemetry.applySampleCount}
        </div>
        <div style={{ color: skewSloPass ? "#8cff8c" : "#ffb0b0" }}>
          SLO gate (aggressive): p95&lt;=5ms, p99&lt;=10ms, max&lt;=30ms ={" "}
          {skewSloPass ? "PASS" : "FAIL"}
        </div>
      </section>
      <section
        style={{
          marginTop: 0,
          marginBottom: "10px",
          border: "1px solid #1f4b2a",
          background: "#06110a",
          padding: "8px 10px",
          color: "#bff2c4",
          fontSize: "12px",
        }}
      >
        <strong>Live skew plot</strong>
        <div style={{ color: "#9fd4ff", marginTop: "3px", marginBottom: "6px" }}>
          Each line is one participant skew (ms) vs current authority over last 20s.
        </div>
        <canvas
          ref={skewCanvasRef}
          style={{
            width: "100%",
            height: "240px",
            border: "1px solid #1f4b2a",
            background: "#000",
            display: "block",
          }}
        />
      </section>
      <section
        style={{
          marginTop: 0,
          marginBottom: "10px",
          border: "1px solid #1f4b2a",
          background: "#08120a",
          padding: "8px 10px",
          color: "#bff2c4",
          fontSize: "12px",
        }}
      >
        <strong>5-tab smoke checklist</strong>
        <div style={{ marginTop: "6px", display: "grid", gap: "4px" }}>
          {(
            [
              ["tabsJoined", "5 tabs joined the same room"],
              ["sameRoom", "Participant roster shows all tabs in one room"],
              ["sharedSong", "Changing song on one tab updates all tabs"],
              ["syncSeek", "Seek on one tab moves all tabs"],
              ["distinctMix", "Each tab keeps independent stem mix"],
            ] as const
          ).map(([key, label]) => (
            <label key={key} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <input
                type="checkbox"
                checked={smokeChecklist[key]}
                onChange={(event) => {
                  const { checked } = event.currentTarget;
                  setSmokeChecklist((prev) => ({ ...prev, [key]: checked }));
                }}
              />
              {label}
            </label>
          ))}
        </div>
      </section>
      {commandBanner ? (
        <section
          style={{
            marginTop: 0,
            marginBottom: "10px",
            border: commandBanner.tone === "warning" ? "1px solid #6a5a1f" : "1px solid #1f3f6a",
            background: commandBanner.tone === "warning" ? "#1f1a08" : "#0b1220",
            padding: "8px 10px",
            color: commandBanner.tone === "warning" ? "#ffe08a" : "#9fd4ff",
          }}
        >
          {commandBanner.message}
        </section>
      ) : null}
      {commandToasts.length > 0 ? (
        <section
          style={{
            marginTop: 0,
            marginBottom: "10px",
            border: "1px solid #1f3f6a",
            background: "#0a1018",
            padding: "8px 10px",
            color: "#9fd4ff",
            fontSize: "12px",
          }}
        >
          <strong>Command lifecycle</strong>
          <div style={{ marginTop: "6px", display: "grid", gap: "4px" }}>
            {commandToasts
              .slice()
              .reverse()
              .map((toast) => (
                <div
                  key={toast.id}
                  style={{ color: toast.tone === "warning" ? "#ffd98a" : "#9fd4ff" }}
                >
                  {toast.message}
                </div>
              ))}
          </div>
        </section>
      ) : null}
      {commandArbitrationRows.length > 0 ? (
        <section
          style={{
            marginTop: 0,
            marginBottom: "10px",
            border: "1px solid #4b3f1f",
            background: "#171209",
            padding: "8px 10px",
            color: "#ffd98a",
            fontSize: "12px",
          }}
        >
          <strong>LWW command ordering</strong>
          <div style={{ marginTop: "6px", display: "grid", gap: "4px" }}>
            {commandArbitrationRows
              .slice()
              .reverse()
              .map((row) => (
                <div
                  key={[String(row.seq), row.op, row.issuerId].join(":")}
                  style={{ color: row.verdict === "winner" ? "#a8dfb5" : "#d0b38a" }}
                >
                  {row.op} seq={row.seq} issuer={row.issuerId} exec=
                  {new Date(row.executeAtMs).toLocaleTimeString()} verdict={row.verdict}
                </div>
              ))}
          </div>
        </section>
      ) : null}
      {failedClients.length > 0 ? (
        <section
          style={{
            marginTop: 0,
            marginBottom: "10px",
            border: "1px solid #4b1f1f",
            background: "#170d0d",
            padding: "8px 10px",
            color: "#ffb6b6",
          }}
        >
          <strong>Join failures</strong>
          <div style={{ marginTop: "6px", fontSize: "12px", color: "#ffd7d7" }}>
            {failedClients.map((client) => (
              <div key={client.id}>
                {client.id} ({client.countryCode}): {client.errorMessage}
              </div>
            ))}
          </div>
        </section>
      ) : null}

      <section style={{ marginTop: "12px" }}>
        <h2 style={{ marginBottom: "8px" }}>Playback apply traces</h2>
        <pre
          style={{
            maxHeight: "220px",
            overflow: "auto",
            background: "#0d1014",
            color: "#a6f0ff",
            padding: "12px",
            border: "1px solid #1f334b",
          }}
        >
          {playbackTraceLines.length > 0
            ? playbackTraceLines.join("\n")
            : "No receive/apply traces yet. Issue a story command to capture output-time snapshots."}
        </pre>
      </section>

      <section style={{ marginTop: "12px" }}>
        <h2 style={{ marginBottom: "8px" }}>Event log</h2>
        <pre
          style={{
            maxHeight: "260px",
            overflow: "auto",
            background: "#101010",
            color: "#c4ffc4",
            padding: "12px",
            border: "1px solid #1f4b2a",
          }}
        >
          {logLines.join("\n")}
        </pre>
      </section>
    </main>
  );
}
